'use client';

import {
  Fragment, useCallback, useEffect, useMemo, useRef, useState,
  type KeyboardEvent, type RefObject,
} from 'react';
import { flushSync } from 'react-dom';
import {
  fetchLanguages,
  fetchModes,
  fetchPronunciation,
  fetchDictionary,
  translateStream,
  ENGINE_STALE_EVENT,
  type Language,
  type Mode,
  type Pronunciation,
  type DictionaryEntry,
} from '@/lib/api';
import { contradictsScript, detectScriptClient } from '@/lib/detect-client';
import { preferredTerms, preferredTermsDeep } from '@/lib/koreanTerms';
import {
  loadTabs, saveTabs, promote, TAB_COUNT,
  loadLastTarget, saveLastTarget, loadLastMode, saveLastMode,
  loadOverview, saveOverview, loadOverviewSeen, saveOverviewSeen,
} from '@/lib/languagePrefs';
import { bootstrapToken } from '@/lib/originToken';
import LanguageTabs from './LanguageTabs';
import LevelPicker from './LevelPicker';
import { fetchAiPricing, isOwnModel, loadLevel, quoteLevel, saveLevel, type AiPricing } from '@/lib/levels';
import FitTabs from './FitTabs';
import ModeColumn from './ModeColumn';
import CopyButton from './CopyButton';
import VoiceDialog from './VoiceDialog';
import ImageDialog from './ImageDialog';
import FeedbackDialog from './FeedbackDialog';
import { openGuestPrompt } from './GuestPrompt';
import NotebookDialog from './notebook/NotebookDialog';
import FolderSavePicker from './notebook/FolderSavePicker';
import { useAuth } from '@/lib/auth';
import {
  saveEntry, saveLastFolder,
  type NotebookEntry,
} from '@/lib/notebook';
import PageBar from './PageBar';
import MicButton from './MicButton';
import ListenButton from './ListenButton';
import ScriptKeyboard from './ScriptKeyboard';
import { typeJamo, jamoForKey } from '@/lib/hangulIme';
import { keyboardFor, layoutFor } from '@/lib/keyboards';
import { capFor, outputOf, physicalKey, typeDirect } from '@/lib/directIme';
import { flushRomaji, typeRomaji } from '@/lib/romajiKana';
import {
  candidatesFor, choosePinyin, loadPinyin, typePinyin, type Candidate,
} from '@/lib/pinyinIme';
import { speechLang, joinSpoken, spokenCommand } from '@/lib/liveSpeech';
import NoMistakes from './NoMistakes';
import TypingHold, { preloadCoin } from './TypingHold';
import {
  splitOutput, splitOverview, splitSourceOverview, splitNotes, splitVariants, splitGrammarFixes,
  isNoMistakes, isNoMistakesPartial,
  NOTES_TAG, OVERVIEW_TAG, SOURCE_OVERVIEW_TAG, VARIANT_ORDER,
} from '@/lib/outputSections';
import type { Variant } from '@/lib/outputSections';
import { modeHint, modeLabel, type InputLanguage } from '@/lib/modeText';
// The numbered picker names each mode the same way the icon column does —
// with the `{lang}` placeholder filled in — so "3 Korean Registers" and the
// column's own tooltip cannot disagree about what a button does.

/**
 * The register cards.
 *
 * Three now, not two. The pair used to be styled as an opposition — plain card
 * for Formal, accent card for Slang — and a third card in the middle has to
 * read as a STEP between them rather than a third opinion, or the set stops
 * looking like one sentence at three settings.
 *
 * So the tint climbs with the informality: no tint, a light accent wash, then
 * the full accent. The eye reads that as a scale, which is exactly what the
 * three cards are.
 */
/**
 * The one language with a keyboard of its own.
 *
 * Named once, up here, because three separate places ask about it — whether to
 * offer the switch, whether to adopt it when the detector recognises it, and
 * which layout to draw. A second engine (Japanese kana, say) turns this into a
 * map from language to layout; until then a constant is the honest shape.
 */
const KEYBOARD_LANG = 'ko';

const VARIANT_LABEL: Record<Variant['key'], string> = {
  formal: 'Formal',
  friendly: 'Friendly',
  casual: 'Slang',
};

import { hangulSuggestion, type HangulSuggestion } from '@/lib/hangul';
import { loadResults, saveResults } from '@/lib/resultCache';
import {
  canonical, reshape, reline, shapeOf, sourceUnits, assemble, ONLY_MARK, type Reshaped,
} from '@/lib/textShape';
import {
  lookup as lookupSentences, remember as rememberSentences,
} from '@/lib/sentenceMemory';
import { paginate, pageOfOffset, pageWindow } from '@/lib/paginate';

/**
 * Translate's tabs beyond Standard, by the key the wire, the notebook and the
 * remembered preference all use. Software-AI knows the team's field; Easy takes
 * the text down to A1 in its own language and translates that. Both are written
 * on the click, one slot each, so switching between them loses nothing.
 */
type ExtraTab = 'ai' | 'easy';
type TransTab = 'standard' | ExtraTab;
/** The box's text, kept for the life of the tab so a trip to another page does not lose it. */
const DRAFT_KEY = 'gt.draft.v1';

const EXTRA_TABS: readonly ExtraTab[] = ['ai', 'easy'];
const EXTRA_TAB_LABEL: Record<ExtraTab, string> = { ai: 'Software-AI', easy: 'Easy' };
const NO_EXTRA_TEXT: Record<ExtraTab, string | null> = { ai: null, easy: null };
const NO_EXTRA_BUSY: Record<ExtraTab, boolean> = { ai: false, easy: false };

/**
 * What ONE translation may carry. Still 5,000 — it is the server's own ceiling
 * and the size of a request — but it is now the size of a PAGE rather than the
 * size of what the box will accept. See MAX_DOC.
 */
const MAX_CHARS = 5000;

/**
 * What the box will hold in total: ten pages.
 *
 * The old behaviour was to stop at 5,000 and silently drop the rest of a paste,
 * which is the worst of the three available answers — worse than refusing, and
 * far worse than paging, because the text that went missing was never named.
 *
 * Ten pages rather than no limit at all, because every page is a model call
 * somebody eventually pays for, and because a translator's box is not a document
 * pipeline: past this size the honest advice is to send the file, not to paste
 * it. Nothing is truncated silently at this line either — the counter turns red
 * and says what did not fit.
 */
const DOC_PAGES = 10;
const MAX_DOC = MAX_CHARS * DOC_PAGES;

/**
 * Where the flag on a mode button must disagree with the language registry's
 * first country. See the note where this is read.
 */
const FLAG_FOR_LANG: Record<string, string> = { en: 'us' };

/**
 * When typing turns into a translation — now TWO answers, not one.
 *
 * The history matters, because this is the third shape this has had and the
 * second one is still visible in the code around it. A 450ms debounce meant a
 * sentence cost several model calls, one per natural pause between words, every
 * one of them a partial sentence superseded before the reader could use it. The
 * fix was a long silent wait, which saved the money and cost the thing people
 * actually wanted: a pane that answers while you write in it.
 *
 * So the pane now does both, and the two are different promises:
 *
 *   • the LIVE DRAFT, `livePreviewMs` after the last keystroke. This is the
 *     real-time answer. It is provisional by construction — the sentence it
 *     translates is usually not finished — and the pane says so by dimming it
 *     and leaving the hold layer up over it.
 *   • the FINAL, when somebody stops. Reached by the countdown below, or
 *     immediately by any deliberate act: Enter, Ctrl+Enter, a separator in
 *     pasted text, a changed language or mode.
 *
 * WHY THE DRAFT IS NOT SIMPLY THE OLD PER-KEYSTROKE TRANSLATION BACK. Because
 * of what happens between the two. The draft's answer is filed in the memo and
 * in the sentence store on the way past (see `remember` and `filePairs`), so the
 * final that follows it is, for unchanged text, a memo hit costing nothing — and
 * for text that grew by one sentence, one sentence's worth of model call rather
 * than the paragraph's. The draft is not an extra translation on top of the
 * final; it is usually the same one, bought earlier.
 *
 * BOTH NUMBERS ARE THE OPERATOR'S, not constants — they arrive from
 * `/api/modes` (console ▸ Settings ▸ Typing &amp; live results). They are the
 * one dial in the product that trades money against patience, and where that
 * should sit depends on the bill and on who is using it that week. The values
 * below are only what the box does before the server has answered, and on the
 * first paint nobody has typed anything yet.
 */
const DEFAULT_HOLD_S = 8;
/**
 * 0 — the live draft is OFF by default, and the eight seconds are the whole wait.
 *
 * It shipped at 600ms, which put a provisional answer on screen while somebody
 * was still mid-sentence. The brief is now that the output follows eight seconds
 * after typing STOPS and not before, so the draft has to be out of the way: with
 * this at 0 the scheduler skips it entirely and the countdown is the only thing
 * that produces an answer from typing.
 *
 * Kept as a number rather than deleted, because it is still the operator's dial
 * (console ▸ Settings ▸ Typing & live results) and turning it back on is a
 * setting rather than a deploy. This is only what the box does before the server
 * has answered.
 */
const DEFAULT_LIVE_MS = 0;

/**
 * The silent second at the front of the countdown.
 *
 * A counter that appeared during every pause between words would be a flicker
 * nobody could read, so the first second of the wait is spent without one. It
 * is INSIDE the configured total rather than added to it: eight seconds means
 * one silent and seven counted, so what an operator types in the console is
 * what somebody at the keyboard actually waits.
 *
 * Skipped entirely below two seconds, where there would be nothing left to count.
 */
const HOLD_LEAD_MS = 1000;

/**
 * A paste is a request, always (owner, 2026-10-14: "when a user pastes something
 * into the input, you should send the AI request for sure").
 *
 * For a while a paste was only answered at once when the clipboard carried a
 * separator — a full stop, a line break, a question mark — and anything else (a
 * word, a name, a short phrase with no full stop, which is most of what people
 * actually paste to look up) went into the ordinary typing wait. On screen that
 * read as "I pasted and nothing happened": the pane sat on the previous answer,
 * or on nothing, for the whole countdown. Saving a fragment's translation was
 * never worth that, so the separator test is gone and every paste goes now.
 */

/**
 * Deleting asks for nothing.
 *
 * It used to ask for a translation on a 300ms leash, on the reasoning that an
 * answer still containing the word you just removed is wrong on screen and that
 * a stale translation is the one thing this pane must never show. The cost of
 * that reading is what retired it: backspace is not a request for anything. A
 * word deleted mid-sentence is the middle of an edit, and every correction — a
 * typo, a re-worded clause, a sentence taken out and started again — bought a
 * model call for a text the person was still in the middle of changing.
 *
 * So a deletion now schedules NOTHING: no request, no live draft, no countdown.
 * What it leaves on screen is the previous answer, unchanged, until the reader
 * either types again (the ordinary typing path, with its full wait) or presses
 * Ctrl/⌘+Enter, which still translates immediately — see `translateNow`.
 */

/**
 * How long a click waits when it is overtaking a request that is still running.
 *
 * Changing the mode or the language is a deliberate act and normally answers at
 * once — that is the whole point of the split in the scheduler below, and it
 * stays. This is only about the burst: somebody trying the mode column out
 * clicks four buttons in half a second, and each of those used to start a
 * request and abort it a moment later. Measured on the live dev site: four
 * clicks 120ms apart cost three model calls, two of which nobody ever read. The
 * upload is most of the bill (~70% of spend is input tokens, and most of that is
 * the system prompt), so an aborted request is very nearly as expensive as one
 * that finishes.
 *
 * The condition is what keeps this from costing anything. A click only waits
 * when something is ALREADY streaming — i.e. when there is a request it would be
 * throwing away. A click made while the pane is idle, which is what a deliberate
 * switch looks like, still fires immediately.
 *
 * 350ms is above a normal double-click and far below the point where a control
 * stops feeling connected to the finger that pressed it.
 */
const SETTLE_MS = 350;

/**
 * Characters that can appear in one update without anyone having typed them.
 *
 * The safety net under `onPaste`. Pasting with Ctrl+V fires a paste event and
 * is answered immediately by the flag it sets — measured at 25-640 ms in every
 * shape of paste there is: into an empty box, appended to an answered one, over
 * a selection, mid-typing, and a 5,000-character document. But a paste event is
 * not the only way finished text arrives in the box. Dropped text fires no
 * paste event at all, and neither do some of the platform paths that people
 * still call pasting (a middle-click on X11, a few mobile keyboards). Waiting
 * five seconds for those is the exact failure the flag exists to prevent, and it
 * fails silently, in a way no test of Ctrl+V would ever show.
 *
 * So the rule is stated about the TEXT rather than about the event: a jump this
 * big did not come from a keystroke, whatever produced it. 24 is chosen to sit
 * above the largest thing a person can commit in one update — a Japanese or
 * Chinese IME finishing a phrase, an autocorrect swapping a word — and far
 * below any pasted sentence. Anything smaller than this that genuinely was a
 * paste still has its event.
 */
const JUMP_CHARS = 24;

/**
 * When the box holds a WORD rather than a text.
 *
 * The pronunciation card's whole reason for existing is this distinction: you
 * want to know how something is said when you are looking a word up, and you do
 * not when you are translating a paragraph. Two words rather than one because
 * "thank you", "bon appétit" and 안녕 하세요 are all one thing to look up, and
 * because a card that vanished on the space bar would look broken.
 *
 * The character ceiling is the guard against the other reading of "two words" —
 * a single unbroken 500-letter string, which is not a word either.
 */
const WORD_MAX_WORDS = 2;
const WORD_MAX_CHARS = 48;

/**
 * How long the box must sit still before a word is looked up.
 *
 * Far shorter than the translation wait, because this costs a fraction of one
 * and answers in about half a second — but not zero: at zero, typing "water"
 * asks about w, wa, wat and wate on the way past, four look-ups nobody wanted.
 * 350 ms is under the pause between one word and the next for a touch typist,
 * so the card appears while they are still looking at the word.
 */
const WORD_IDLE_MS = 350;

/**
 * The word in the box, if the box holds a word at all.
 *
 * The cheap half of "is somebody looking a word up" — the half that can be
 * answered here, for free, without asking anything. It is deliberately the same
 * set of tests the pronounce and dictionary ROUTES apply before they will spend
 * a model call (see routes/pronounce.ts), so the client stops a request the
 * server would only have refused.
 *
 * Shared by both word features, which is the point: they are one state, and two
 * copies of this drift.
 */
function wordForLookup(text: string): string | null {
  const word = text.trim();
  if (!word) return null;
  const words = word.split(/\s+/);
  if (words.length > WORD_MAX_WORDS || word.length > WORD_MAX_CHARS) return null;
  if (!/\p{L}/u.test(word)) return null;
  return word;
}

/**
 * The dictionary entry used to wait for the pronunciation to come back before
 * it would even ASK, which cost it the whole of that round trip — measured at
 * 0.4s to 2.3s — on top of its own. It no longer does: both start on the same
 * beat, off the same debounce, and race.
 *
 * Kept as a name rather than folded into WORD_IDLE_MS so the two can be tuned
 * apart later, but they must not drift far: firing them at different times is
 * what makes one card look broken while the other is still thinking.
 */
const ENTRY_IDLE_MS = WORD_IDLE_MS;

/** The depleting ring, shared by the pill and the bare icon beside Copy. */
function CountdownRing({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    // -rotate-90 starts the sweep at twelve o'clock; without it the ring begins
    // draining from the three o'clock mark and reads as a spinner rather than a
    // clock. `currentColor` on each circle keeps this working in both themes
    // without a second set of classes.
    <svg viewBox="0 0 16 16" className={`${className} -rotate-90`} aria-hidden>
      <circle cx="8" cy="8" r="6" fill="none" strokeWidth="2" stroke="currentColor" className="opacity-20" />
      <circle
        cx="8" cy="8" r="6" fill="none" strokeWidth="2" strokeLinecap="round" stroke="currentColor"
        className="gt-ring text-accent-600 dark:text-accent-400"
      />
    </svg>
  );
}

/**
 * The model is answering.
 *
 * Same circle, same radius, same stroke as the countdown ring on purpose: one is
 * the wait before the work and the other is the work, and reading them as two
 * states of one thing is easier than reading them as two unrelated indicators.
 */
function Spinner() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 animate-spin text-accent-600 dark:text-accent-400" aria-hidden>
      <circle cx="8" cy="8" r="6" fill="none" strokeWidth="2" stroke="currentColor" className="opacity-20" />
      <path d="M8 2a6 6 0 0 1 6 6" fill="none" strokeWidth="2" strokeLinecap="round" stroke="currentColor" />
    </svg>
  );
}

/**
 * The answer, with each of its segments pointing back at the line it came from.
 *
 * Hovering a sentence in the translation lights up the sentence it translates in
 * the source box. It is the one thing a two-pane translator can do that a
 * one-pane one cannot, and the thing people actually use a translator FOR: not
 * "what does this say" but "which part of this says that".
 *
 * The mapping is not guessed at — `reshape` in lib/textShape hands over `links`
 * only when the two texts agree on how many sentences (or failing that, how many
 * lines) they contain, and hands over nothing at all when they do not. So this
 * either draws a mapping that is right by construction, or falls back to plain
 * text and offers nothing. A highlight that lands on the wrong sentence would be
 * worse than no highlight, because it would be believed.
 *
 * The text BETWEEN the linked pieces — the space between two sentences, the
 * blank line between two paragraphs — is emitted as-is between the spans, so the
 * paragraph still reads and still selects as one continuous piece of text.
 */
function LinkedAnswer({
  laid, active, onEnter, onLeave, children,
}: {
  laid: Reshaped;
  active: number | null;
  onEnter: (i: number) => void;
  onLeave: () => void;
  children?: React.ReactNode;
}) {
  if (!laid.links) return <>{laid.text}{children}</>;

  const links = laid.links;
  return (
    <span onMouseLeave={onLeave}>
      {links.map((l, i) => (
        <Fragment key={i}>
          {/* Everything since the previous piece ended — the space between two
              sentences, or the blank line between two paragraphs — emitted
              outside the span so it is never part of the highlight. */}
          {laid.text.slice(i === 0 ? 0 : links[i - 1].out.end, l.out.start)}
          <span
            onMouseEnter={() => onEnter(i)}
            className={
              // `cursor-pointer`, because this text does something when you
              // point at it and nothing about a paragraph says so on its own.
              // The I-beam a browser gives text by default is a promise that
              // the only thing here is text to select.
              'gt-seg cursor-pointer rounded-[3px] transition-colors ' +
              (active === i ? 'bg-accent-200/70 dark:bg-accent-700/45' : '')
            }
          >
            {laid.text.slice(l.out.start, l.out.end)}
          </span>
        </Fragment>
      ))}
      {laid.text.slice(links[links.length - 1].out.end)}
      {children}
    </span>
  );
}

/**
 * The one-sentence summary, as a card.
 *
 * Drawn twice on the page, from two different sentences: over the translation in
 * the language it is written in, and over the BOX in the language you typed in.
 * One component rather than two copies of the markup, because the two must be
 * recognisably the same object — the second card only reads as "the same summary
 * again, in your language" if it looks identical to the first.
 *
 * A tinted card rather than another paragraph: it is a different KIND of
 * sentence from the text around it — one is the content, this is a description
 * of it — and in the same ink at the same size the two read as one run-on text,
 * with the summary mistaken for the first line of what follows.
 */
function OverviewCard({ text, className = '' }: { text: string; className?: string }) {
  return (
    <div
      className={
        'gt-overview flex gap-2.5 rounded-xl border border-accent-500/25 bg-accent-200/55 ' +
        'px-3.5 py-3 dark:border-accent-500/25 dark:bg-accent-700/30 ' + className
      }
    >
      <svg
        viewBox="0 0 24 24"
        className="mt-[3px] h-4 w-4 shrink-0 text-accent-600 dark:text-accent-400"
        fill="currentColor"
        aria-hidden
      >
        {/* The same bolt as the switch that turned this on, so the card visibly
            belongs to that button. */}
        <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12z" />
      </svg>
      <p className="text-[15px] leading-relaxed text-ink-700 dark:text-ink-dim">{text}</p>
    </div>
  );
}

/**
 * The dictionary entry, as a card under the answer.
 *
 * Three sections in the order every paper dictionary uses, because the order is
 * the argument: the gloss is what you came for, the note is what stops you
 * misusing what you just got, and the sentence is what turns a word you can
 * recognise into one you can write. Reversed — examples first, meaning last —
 * the same three facts read as trivia.
 *
 * ── Why it looks like this ───────────────────────────────────────────────────
 *
 * The senses carry the card. They are set at the answer's own size and in the
 * answer's own ink, and everything else on the card is smaller and greyer, so
 * the entry has one thing in it that is unmistakably the point. A card where the
 * three sections were the same weight would be a wall to read, and this sits
 * under a translation somebody has already got the answer from — it has to be
 * skimmable in the half-second before they decide whether to read it at all.
 *
 * The rule down the left of the examples is doing the work a blank line cannot:
 * an example sentence is in the SOURCE language, sitting under an explanation in
 * the target one, and without a mark that says "different kind of text" the two
 * scripts run together into one grey block.
 */
function DictionaryCard({
  word,
  entry,
  className = '',
}: {
  word: string;
  entry: DictionaryEntry;
  className?: string;
}) {
  return (
    <div
      className={
        'gt-notes rounded-xl border border-ink-200 bg-white/70 p-4 ' +
        'dark:border-ink-700/70 dark:bg-ink-900/60 ' + className
      }
    >
      {/* The headword titles the card, and titles it in the source language's
          own letters. It is already in the box a few centimetres away, which is
          exactly why it belongs here too: the box is where you are typing and
          this is a reference entry, and a dictionary entry with no headword on
          it reads as a list of meanings of nothing.

          No copy button on this card, or on any card under the answer: the
          page has ONE copy for the output, and it takes the answer's text only
          (owner, 2026-10-14). */}
      <div className="mb-2.5 flex items-start justify-between gap-3">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-sm font-semibold text-ink-700 dark:text-ink-200">
            {word}
          </span>
          {/* Named once, quietly. Three stacked sections are self-evident to
              anyone who has opened a dictionary and shapeless to anyone who has
              not — and this card appears without being asked for, so it owes
              the reader an account of what it is. */}
          <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-ink-400">
            dictionary
          </span>
        </span>
      </div>

      {/* ── Meaning, first ──────────────────────────────────────────────────
          Numbered even when there is only one, because the number is what says
          "this word has senses, and here they are" — and a single sense that
          looked like a bare line would make the two-sense entry beside it look
          like a different feature. */}
      <ol className="flex flex-col gap-1.5">
        {entry.senses.map((s, i) => (
          <li key={i} className="flex gap-2 text-[15px] leading-relaxed">
            <span className="mt-[1px] shrink-0 text-xs font-semibold tabular-nums text-ink-400">
              {i + 1}
            </span>
            <span className="min-w-0 text-ink-900 dark:text-ink-50">
              {/* The part of speech is a label on the gloss, not part of it —
                  so it is set apart rather than run in, the way it is in print.
                  It is the one thing here allowed to be missing: plenty of
                  headwords have no useful one, and an empty bracket is worse
                  than no bracket. */}
              {s.pos && (
                <span className="mr-1.5 text-[13px] italic text-ink-400">{s.pos}</span>
              )}
              {s.gloss}
            </span>
          </li>
        ))}
      </ol>

      {/* ── Explanation, second ─────────────────────────────────────────────
          Dropped entirely when the model had nothing to warn about, rather than
          drawn empty or filled with something to say. A card that always has
          three sections teaches the reader that the third one is furniture. */}
      {entry.explanation && (
        <p className="mt-3 text-[13px] leading-relaxed text-ink-500 dark:text-ink-400">
          {entry.explanation}
        </p>
      )}

      {/* ── Examples, third ─────────────────────────────────────────────────
          The sentence is in the source language and gets the darker ink; its
          translation sits under it, smaller, because it is the crib rather than
          the thing being shown. Someone who can already read the first line
          never has to look at the second. */}
      {entry.examples.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2 border-l-2 border-ink-200 pl-3 dark:border-ink-700">
          {entry.examples.map((e, i) => (
            <li key={i}>
              <p className="text-[14px] leading-snug text-ink-800 dark:text-ink-100">{e.text}</p>
              <p className="text-[13px] leading-snug text-ink-400">{e.translation}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Keep a pane's content in view while the pane beside it scrolls past.
 *
 * The two panes share one grid row, so the taller one sets the height of both:
 * paste a chat log into Mistakes and the answer runs 1500px while the source
 * text is 300px, stranding it — and the character counter under it — off the
 * top of the screen the moment you start reading the translation. Pinning the
 * short side keeps the two halves of the same thought together.
 *
 * `position: sticky` is only kind to content that fits on screen: a block taller
 * than the viewport pins its own top and puts its bottom permanently out of
 * reach. So it is applied only while the block genuinely fits AND the pane has
 * meaningful slack to travel through — when the two sides are about the same
 * height there is nothing to pin and the class would be noise.
 *
 * Toggling the class cannot change either measurement (a sticky box stays in
 * flow at its natural size), so this cannot oscillate.
 */
function usePinned(
  block: RefObject<HTMLDivElement | null>,
  pane: RefObject<HTMLDivElement | null>,
) {
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    const b = block.current;
    const p = pane.current;
    if (!b || !p) return;

    const measure = () => {
      const bh = b.getBoundingClientRect().height;
      const ph = p.getBoundingClientRect().height;
      setPinned(
        window.matchMedia('(min-width: 768px)').matches &&
          bh + 32 <= window.innerHeight &&
          ph - bh > 80,
      );
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(b);
    ro.observe(p);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [block, pane]);

  return pinned;
}

/**
 * Does this keystroke finish the word being composed — and if so, can we take
 * the key over, or does it have its own job to do?
 *
 * Every Korean IME commits on more than the space bar, because a word can end in
 * a great many ways: a comma, a full stop, a digit, a new line, moving to the
 * next field. Only letters CONTINUE a word, so the rule is the inverse of the
 * obvious one — everything that is not a letter ends it.
 *
 * `owned` is the important half.
 *
 *   owned:true  — the key's whole effect is to insert one character, which we
 *                 can reproduce exactly by appending it after the conversion. We
 *                 must preventDefault and do it ourselves: leaving the browser
 *                 to insert it would race our own setText through the textarea's
 *                 onChange, and the browser's value — built from the text as it
 *                 was BEFORE the conversion — would land last and throw the
 *                 conversion away.
 *
 *   owned:false — the key does something we neither can nor should reproduce.
 *                 Tab moves focus, and pressing Tab has to keep moving focus.
 *                 So: convert, then stand aside and let it happen.
 */
function endsWord(
  e: KeyboardEvent<HTMLTextAreaElement>,
): { trailing: string; owned: boolean } | null {
  // A shortcut is not typing. Ctrl+A, ⌘+C and friends must pass straight
  // through — and Ctrl/⌘+Enter is this app's own "translate now".
  if (e.metaKey || e.ctrlKey || e.altKey) return null;

  // With a selection live, the key is about to REPLACE the selected text, which
  // is not the edit this was written for. Leave it entirely alone.
  const el = e.currentTarget;
  if (el.selectionStart !== el.selectionEnd) return null;

  if (e.key === 'Tab') return { trailing: '', owned: false };
  if (e.key === 'Enter') return { trailing: '\n', owned: true };
  /*
   * A BACKSLASH IS NOT A WORD ENDING, and it is the one exception to the rule
   * above.
   *
   * It is a single printable non-letter, so the test below used to take it and
   * commit the conversion: typing `\` put the Korean result on screen and
   * replaced the letters that were typed, which is a whole visible change to
   * somebody's text asked for by a key nobody presses to end anything. Unlike
   * the space bar, a comma or a full stop, a backslash does not close a word in
   * any language — it turns up inside paths, code and escapes, in the middle of
   * a run of characters.
   *
   * Returning null leaves the key to the browser: it types a backslash, and the
   * letters in front of it are left exactly as they were typed. Measured:
   * `dkssud` + `\` now leaves `dkssud\` in the box, where it used to leave
   * `안녕\`.
   *
   * The offer itself does then disappear, and that is not this rule — the card
   * describes the word AT THE CARET, and a backslash ends that word the same way
   * any other non-letter does. What changed is only that the text is no longer
   * rewritten on the way.
   */
  if (e.key === '\\') return null;
  // One printable character that is not a letter: the space bar, the punctuation
  // that closes a sentence, a digit. Named keys (Escape, Backspace, ArrowLeft)
  // are all longer than one character, so this excludes them by construction.
  if (e.key.length === 1 && !/[A-Za-z]/.test(e.key)) return { trailing: e.key, owned: true };
  return null;
}

export default function Translator() {
  /*
   * The Notebook is every signed-in person's OWN private space — a place to keep
   * the translations they choose to keep, in folders they make. Guests get no
   * rail button, no Ctrl+7, no Ctrl+Alt+Z and no dialog (a personal space has to
   * belong to an account); the /api/notebook routes refuse them too, so this only
   * decides what is drawn.
   */
  const { user: authUser, loaded: authLoaded, guest: guestCredits, marq: wallet } = useAuth();
  const notebookAllowed = !!authUser;

  /*
   * The translation level (engine: Low · Medium · High · Advanced · Expert).
   *
   * The numbers come from GET /api/engine; until they arrive, or while the
   * engine's level pricing is off, there is no picker and no level is sent — the
   * translator is exactly what it was. Guests always run at the guest level, and
   * send none: the server forces it. The choice is this browser's, in
   * localStorage (lib/levels.ts).
   *
   * Re-read when the tab comes back into view (an operator may have changed the
   * levels in the meantime) and when a request is refused for naming a level
   * the server no longer knows (lib/api.ts retries that request once without).
   */
  const [aiPricing, setAiPricing] = useState<AiPricing | null>(null);
  /** The first /api/engine answer is in (or failed): the level is known. */
  const [pricingSettled, setPricingSettled] = useState(false);
  const [chosenLevel, setChosenLevel] = useState('');
  const refetchPricing = useCallback(() => {
    return fetchAiPricing()
      .then(setAiPricing)
      // A failed re-read keeps the levels already showing rather than
      // switching the picker off under somebody using it.
      .catch(() => {})
      .finally(() => setPricingSettled(true));
  }, []);
  useEffect(() => {
    setChosenLevel(loadLevel() ?? '');
    void refetchPricing();
  }, [refetchPricing]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') void refetchPricing(); };
    const onStale = () => { void refetchPricing(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(ENGINE_STALE_EVENT, onStale);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(ENGINE_STALE_EVENT, onStale);
    };
  }, [refetchPricing]);
  // The levels depend on the account (the Claude level, backend lib/claudeApi.ts): re-read on a sign-in or sign-out
  // here. The first read already went with the session's cookie.
  const authId = authUser?.id ?? null;
  const pricedFor = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!authLoaded) return;
    if (pricedFor.current !== undefined && pricedFor.current !== authId) void refetchPricing();
    pricedFor.current = authId;
  }, [authLoaded, authId, refetchPricing]);

  /*
   * NOTHING IS ASKED until the sign-in and the engine have both answered.
   *
   * Before that, the page cannot know whose level applies: a signed-in person
   * looks like a guest for the first few hundred milliseconds, so a restored
   * text used to be translated at the guest level and then AGAIN at the saved
   * level the moment the account arrived — two charges for one page load.
   * A safety net lets the page go after a few seconds even if one of the two
   * never answers, so a hung request cannot leave the translator dead.
   */
  const [bootWaitOver, setBootWaitOver] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setBootWaitOver(true), 5000);
    return () => clearTimeout(t);
  }, []);
  const pageReady = (authLoaded && pricingSettled) || bootWaitOver;

  /** The level this page is at — what the picker shows, and what keys the caches. */
  const level = !aiPricing?.live
    ? null
    : !authUser
      ? aiPricing.guestLevel
      // A saved level that is gone, or `off` (Claude without its key), falls back to the default.
      : aiPricing.levels.some((l) => l.key === chosenLevel && !l.off) ? chosenLevel : aiPricing.defaultLevel;
  /**
   * The level that goes ON THE WIRE, passed explicitly at every call below.
   * None for a guest (the server forces the guest level) and none before the
   * page is ready (nothing is sent then anyway).
   */
  const sendLevel = authUser && pageReady ? level : null;
  /** The level runs on our own model, TrueMarch Model (the picker's last level): the output says so. */
  const ownLevel = !!level && !!aiPricing?.levels.find((l) => l.key === level)?.own;
  /** The level is Claude (backend lib/claudeApi.ts): the picker's first. */
  const claudeLevel = !!level && !!aiPricing?.levels.find((l) => l.key === level)?.claude;
  /**
   * Past the first level, answers are filed under the level as well — two
   * levels are two different answers. The first level keeps the plain keys, so
   * nothing already held is lost.
   */
  const levelTag = level && aiPricing && level !== aiPricing.levels[0]?.key ? `|${level}` : '';
  /**
   * ABOVE THE FIRST LEVEL, NOTHING IS SENT WHILE TYPING (owner's product rule).
   *
   * Every request at a level pays at least that level's minimum, and the first
   * level's machinery — a live draft as you type, a countdown that translates
   * when you pause, sentence-at-a-time partial requests, the open AI tab written
   * alongside — would make one "≈ 25 per translation" into several charges. So
   * at every higher level the page asks ONCE, when the person presses Translate
   * (the button, or Ctrl/⌘+Enter), for the whole page. Answers already held are
   * still put back for free. At the first level everything is exactly as before.
   *
   * Except our own model (the TrueMarch level): it bills nobody, so it works like the first level — live as you
   * type, no Translate card (owner 2026-10-20: "why are you showing the Translate button like this? … follow
   * others"). Its caches stay its own (`levelTag`).
   *
   * And Claude, the same way: the coin while you type, the translation when you stop, Ctrl+Enter at once (owner
   * 2026-10-30: "there was a good workflow to translate it automatically with Ctrl Enter by showing the loading …
   * avatar on the output but right now it's ruined, on the claude translation mode").
   */
  const manual = levelTag !== '' && !ownLevel && !claudeLevel;
  const levelInfo = level && aiPricing ? aiPricing.levels.find((l) => l.key === level) ?? null : null;
  /** How prices are said: free credits for a guest, nothing with KHARMA off. */
  const priceMode: 'kharma' | 'free' | 'none' = !authUser
    ? 'free'
    : wallet && wallet.enabled !== false ? 'kharma' : 'none';
  const pickLevel = useCallback((key: string) => {
    setChosenLevel(key);
    saveLevel(key);
    // No sentence memory to forget: it is keyed by level (see `levelTag`).
  }, []);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [modes, setModes] = useState<Mode[]>([]);

  // Which languages get a visible tab. Seeded from the browser's own language
  // preferences, then most-recently-used. See lib/languagePrefs.
  const [sourceTabs, setSourceTabs] = useState<string[]>([]);
  const [targetTabs, setTargetTabs] = useState<string[]>([]);

  /**
   * ── The document, and the page of it that is on screen ────────────────────
   *
   * `doc` is everything the visitor put in the box, however long. `text` is the
   * page of it currently being translated, and it is what the textarea, the
   * translator, the memo, the layout engine and every other consumer below see —
   * which is why it is still called `text` and is still a plain string: one
   * request carries at most `MAX_CHARS`, so the app under this line goes on
   * working exactly as it did when 5,000 characters was the whole of what the
   * box could hold.
   *
   * The pages are derived, never stored. Boundaries are indices into `doc` (see
   * lib/paginate), so `pages.map(p => p.text).join('')` is `doc` exactly, and
   * editing page *n* is a splice between two offsets rather than a merge of
   * strings that might not fit back together.
   *
   * Deriving them also means there is no such thing as a stale page list: delete
   * a paragraph on page 1 and pages 2..n reflow on the same keystroke, the way
   * they would in any editor.
   */
  /**
   * What one page may hold, and therefore where the text is cut.
   *
   * The server's own per-request ceiling, read from `/api/modes` on load, with
   * `MAX_CHARS` as the value to work with until it answers. It is a SETTING —
   * an operator can change it in the console — and a page size that disagreed
   * with it would produce the one failure this whole feature exists to prevent:
   * a box that says it has split the text to fit, sending pieces the server then
   * refuses as too long.
   */
  const [pageSize, setPageSize] = useState(MAX_CHARS);
  /** The same, readable from callbacks that must not be rebuilt when it lands. */
  const pageSizeRef = useRef(MAX_CHARS);
  pageSizeRef.current = pageSize;
  /** Ten pages of whatever a page currently is. See MAX_DOC. */
  const docLimit = pageSize * DOC_PAGES;

  const [doc, setDoc] = useState('');
  const [page, setPage] = useState(0);

  /**
   * The text in the box survives leaving the page.
   *
   * Games used to open as a dialog OVER the translator, so whatever had been
   * typed was still there when the dialog closed. Since 2026-10-13 Games is its
   * own page (/games), and a page change unmounts this component: without this
   * a five-minute break would cost the paragraph somebody was halfway through.
   *
   * `sessionStorage`, like the answer cache beside it (lib/resultCache.ts) and
   * for the same reason — it is this tab's scratch space, gone when the tab
   * closes, and it already holds the same text as the key of every cached
   * answer. So this stores nothing the browser was not already holding, and the
   * restored text finds its translation in that cache instead of paying for it
   * again.
   *
   * Restored in an effect, not as the initial state: the server renders an
   * empty box, and a first client render that disagreed would be a hydration
   * mismatch on the one element the page is for. `draftReady` keeps the save
   * below from writing that first empty value over the draft before it is read.
   */
  const draftReady = useRef(false);
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) setDoc((cur) => cur || saved);
    } catch { /* storage off: the box simply starts empty */ }
    draftReady.current = true;
  }, []);
  useEffect(() => {
    if (!draftReady.current) return;
    const t = setTimeout(() => {
      try {
        if (doc) sessionStorage.setItem(DRAFT_KEY, doc);
        else sessionStorage.removeItem(DRAFT_KEY);
      } catch { /* quota or private mode — the text is still on screen */ }
    }, 250);
    return () => clearTimeout(t);
  }, [doc]);
  const pages = useMemo(() => paginate(doc, pageSize), [doc, pageSize]);
  /** Clamped: deleting text can leave the current page number past the end. */
  const pageIndex = Math.min(page, pages.length - 1);
  const text = pages[pageIndex].text;

  /** Read by callbacks that must not close over a stale page number. */
  const pageRef = useRef(0);
  pageRef.current = pageIndex;

  /**
   * Replace the text of the page on screen, leaving the rest of the document
   * alone.
   *
   * Every existing caller of `setText` — the dictation splice, the Korean
   * conversion, its undo, Swap — means "this page now reads like this", so they
   * all keep working unchanged. The one thing this deliberately does NOT do is
   * follow the caret onto another page: those callers are editing in place, and
   * a page that changed underneath them would move the text they just wrote out
   * from under the cursor. Typing goes through `editPage` below, which does.
   */
  const setText = useCallback((next: string) => {
    setDoc((prev) => {
      const size = pageSizeRef.current;
      const ps = paginate(prev, size);
      const p = ps[Math.min(pageRef.current, ps.length - 1)];
      return (prev.slice(0, p.start) + next + prev.slice(p.end)).slice(0, size * DOC_PAGES);
    });
  }, []);

  /** Empty the whole document, not merely the page being looked at. */
  const clearAll = useCallback(() => {
    setDoc('');
    setPage(0);
  }, []);
  /**
   * Where the caret is, so the conversion can scope itself to the word being
   * typed rather than to the whole box. Tracked in state because it changes what
   * is rendered — see `candidate` below.
   */
  const [caret, setCaret] = useState(0);
  /**
   * A conversion the user has turned down, held as the exact keystrokes it was
   * offered for. Typing anything else changes the segment and the offer comes
   * back on its own, so this never needs clearing by hand.
   */
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [from, setFrom] = useState('auto');
  const [to, setTo] = useState('en');
  const [mode, setMode] = useState('translate');

  /**
   * Switch view mode — the one place it happens.
   *
   * This was written inline on `ModeColumn`'s `onSelect` prop, which was fine
   * while the column was the only way to change mode. It is not any more: the
   * numbered buttons below choose a mode too, and two call sites doing
   * "setMode, and also remember it" is how one of them comes to forget the
   * second half. Same two statements, in one place, called by both.
   */
  const selectMode = useCallback((id: string) => {
    setMode(id);
    saveLastMode(id);
  }, []);

  /**
   * Is the numbered mode picker on screen?
   *
   * Opened by Ctrl-clicking the input box (see the source pane's `onClick`),
   * which is a deliberate second way to reach the modes for somebody whose
   * hands are on the keyboard and whose eyes are on the text they just pasted —
   * the icon column is a mouse trip to the middle of the screen, and its icons
   * have to be recognised before they can be picked.
   *
   * Off by default and off after a pick: it is a way to change mode, not a
   * panel, and a row of numbers left sitting over the box would be one more
   * thing in front of the text.
   */
  const [modeNumbers, setModeNumbers] = useState(false);
  /**
   * The Overview switch. Restored from localStorage in an effect rather than as
   * the initial state, because reading storage during the first render makes the
   * server-rendered HTML and the client's first paint disagree.
   *
   * Seeded with the DEFAULT rather than with `true`, so the two agree for the
   * visitor who has never touched the switch — which is most of them, and the
   * default is off (see loadOverview). Seeding it on would draw the button lit
   * for a frame and then unlight it on hydration, on every first load.
   */
  const [overview, setOverview] = useState(false);
  /** Has this visitor ever pressed it? Drives the sparkle, nothing else. */
  const [overviewSeen, setOverviewSeen] = useState(true);
  /** A translation landed recently — the window in which the sparkle runs. */
  const [justTranslated, setJustTranslated] = useState(false);

  const [output, setOutput] = useState('');
  /**
   * The segment of the answer the pointer is on, so the source pane can mark the
   * line it was translated from. Null whenever the pointer is anywhere else,
   * which is most of the time and is why this costs nothing to keep in state.
   */
  const [linked, setLinked] = useState<number | null>(null);
  const [detected, setDetected] = useState<{ code: string; name: string } | null>(null);
  /**
   * Names for the AI Prompt tabs, from the server. Empty until the mode list
   * lands, and empty forever on an older backend — the pane titles a tab from
   * its own id when it has nothing better, so neither case is a broken tab.
   */
  const [promptStyles, setPromptStyles] = useState<{ styleId: string; label: string }[]>([]);
  /**
   * Which tab is open, remembered by style id rather than by position.
   *
   * By id because the set is operator-editable: a style added, disabled or
   * reordered in the console would otherwise move the reader to a different tab
   * than the one they chose. `null` means "nothing chosen on this browser yet",
   * which resolves to `DEFAULT_STYLE_TAB` below.
   */
  const [styleTab, setStyleTab] = useState<string | null>(null);

  /**
   * …and remembered across visits, not just across clicks in one page view.
   *
   * Somebody who has decided that the Claude Session tab is the one they take
   * away is deciding it about the product, not about this answer: they come
   * back, type the next rough line, and want the same tab open. Keeping that in
   * React state alone threw it away on every refresh.
   *
   * Read in an effect rather than in `useState`, like every other preference in
   * this file — this component is server-rendered, and a first render that
   * reads localStorage produces markup the server could not have produced,
   * which React resolves by discarding the whole tree.
   *
   * The stored id is used as-is and never validated against the style list. It
   * does not need to be: `activeStyle` falls back whenever the id is not one of
   * the tabs in front of the reader, which covers a style disabled in the
   * console and a key left over from an older version alike.
   */
  const STYLE_TAB_PREF = 'gt.prompt-style-tab.v1';
  /**
   * The tab a reader who has never chosen one lands on.
   *
   * An id rather than "the first tab": the first tab is Grammar Fix, which is
   * the cheapest thing to read but not what most people open this mode for.
   * Claude Session is the one that goes straight into a coding session, and it
   * is what the owner asked to open by default. If that style is ever disabled
   * in the console the first tab takes over again — see `activeStyle`.
   */
  const DEFAULT_STYLE_TAB = 'session';
  useEffect(() => {
    let pref: string | null = null;
    try { pref = localStorage.getItem(STYLE_TAB_PREF); } catch { /* private mode */ }
    if (pref) setStyleTab(pref);
  }, []);

  /** Open a tab, and remember it for the next visit. */
  const chooseStyleTab = useCallback((styleId: string) => {
    setStyleTab(styleId);
    try { localStorage.setItem(STYLE_TAB_PREF, styleId); } catch { /* private mode */ }
  }, []);

  /*
   * ── AI Prompt, one tab at a time ──────────────────────────────────────────
   *
   * This mode used to answer every enabled style in ONE call: five finished
   * prompts, arriving together, of which the reader opened one. The other four
   * were written, streamed, paid for and thrown away — and because the tabs
   * could only be drawn as each `[[STYLE:…]]` tag landed, the reader also sat
   * through all five being written before the row was complete.
   *
   * So the tabs are now drawn from the STYLE LIST (which the page already has,
   * from /api/modes) instead of from the answer, and one style is written per
   * request: the open tab, when the question settles (see the scheduler), and
   * any other tab on the click that opens it. A tab that has been written is
   * kept until the question changes, so going back to it is instant and free.
   */
  const [styleTexts, setStyleTexts] = useState<Record<string, string>>({});
  /** The tab currently being written, or null. Only ever one at a time. */
  const [styleLoading, setStyleLoading] = useState<string | null>(null);
  const [styleError, setStyleError] = useState<string | null>(null);
  /**
   * What the scheduler needs to know about the open tab, without depending on
   * it.
   *
   * A ref rather than three values in the effect's dependency list, and that is
   * not a style choice: `styleTexts` changes on every streamed chunk, so a
   * scheduler that listed it would tear its own countdown down and build it
   * again a few times a second while the answer it is waiting for arrives.
   *
   * Filled in further down, once `activeStyleId` exists. Effects run after the
   * render that writes it, so it is never read empty.
   */
  const autoStyle = useRef<{ id: string | null; pending: boolean; write: (id: string) => void }>({
    id: null,
    pending: false,
    write: () => {},
  });
  const styleRun = useRef<AbortController | null>(null);
  /** The same three facts for Registers. See `autoStyle` for why it is a ref. */
  const autoRegister = useRef<{
    id: Variant['key'];
    pending: boolean;
    write: (key: Variant['key']) => void;
  }>({
    id: 'formal',
    pending: false,
    write: () => {},
  });
  /**
   * The same two facts for Translate's AI tab. See `autoStyle` for why it is
   * a ref: it is written on every render and read only from inside the
   * scheduler's timers, so what fires is always the freshest closure rather
   * than whatever was current when the timer was armed.
   *
   * No `id` — there is only one tab here that can need writing.
   */
  const autoAi = useRef<{ pending: boolean; write: () => void }>({
    pending: false,
    write: () => {},
  });
  /**
   * Which question the texts above are answers to.
   *
   * The cache has to be thrown away when the request changes — a new rough
   * prompt, a different target language — and comparing a stored key is what
   * makes that happen on the same render the change does, rather than one
   * effect later with a stale tab on screen in between.
   */
  // The level too: an answer bought at one level is not the answer at another.
  const styleKey = `${canonical(text)}\u0000${from}\u0000${to}${levelTag}`;
  const styleKeyRef = useRef(styleKey);
  if (styleKeyRef.current !== styleKey) {
    styleKeyRef.current = styleKey;
    // Mid-flight answers belong to the previous question.
    styleRun.current?.abort();
    styleRun.current = null;
    if (Object.keys(styleTexts).length) setStyleTexts({});
    if (styleLoading) setStyleLoading(null);
    if (styleError) setStyleError(null);
  }

  /**
   * Write one tab.
   *
   * Deliberately NOT routed through `run()`. That path carries the incremental
   * sentence machinery — the plan, the splice, the held sentences — which
   * exists for translating a paragraph a sentence at a time and means nothing
   * for a mode that rewrites one rough prompt as one finished one. It also owns
   * `output`, and these five answers cannot share one string.
   *
   * Already-written tabs return immediately: the text is held until the
   * question changes, so re-opening a tab is free — and that guard is also what
   * stops the scheduler and a click from both paying for the same tab.
   */
  const loadStyle = useCallback((styleId: string) => {
    if (!text.trim()) return;
    if (styleTexts[styleId] !== undefined) return;

    // One tab at a time. Clicking through the row quickly should leave the tab
    // that is actually open being written, not four abandoned streams racing.
    styleRun.current?.abort();
    const controller = new AbortController();
    styleRun.current = controller;

    setStyleLoading(styleId);
    setStyleError(null);

    let raw = '';
    void translateStream(
      {
        text: canonical(text), from, to, mode: 'ai-prompt',
        // No summary sentence: the answer here IS a prompt to be copied whole,
        // and a description of it under the tab would be copied with it.
        overview: false,
        style: styleId,
        level: sendLevel,
        signal: controller.signal,
      },
      {
        onDelta: (chunk) => {
          raw += chunk;
          // The tag the server still writes in front of the version. Stripped
          // here rather than left to splitStyles(): with one style per request
          // there is nothing to split, and the tag is not part of the prompt
          // anybody means to copy.
          setStyleTexts((prev) => ({ ...prev, [styleId]: raw.replace(/^\s*\[\[STYLE:[^\]]+\]\]\s*/, '') }));
        },
        onDone: () => {
          if (controller.signal.aborted) return;
          setStyleLoading((cur) => (cur === styleId ? null : cur));
        },
        onError: (message) => {
          if (controller.signal.aborted) return;
          setStyleError(message);
          setStyleLoading((cur) => (cur === styleId ? null : cur));
          // Cleared rather than left half-written: a tab holding two sentences
          // of a prompt that failed reads as a finished short answer.
          setStyleTexts((prev) => {
            const next = { ...prev };
            delete next[styleId];
            return next;
          });
        },
        onLimit: (message) => {
          if (controller.signal.aborted) return;
          setStyleError(message);
          setStyleLoading((cur) => (cur === styleId ? null : cur));
        },
      },
    );
  }, [text, from, to, styleTexts, sendLevel]);


  /** Open a tab AND write it if it has never been opened for this question. */
  const openStyleTab = useCallback((styleId: string) => {
    chooseStyleTab(styleId);
    loadStyle(styleId);
  }, [chooseStyleTab, loadStyle]);

  /*
   * ── Registers, one tab at a time ──────────────────────────────────────────
   *
   * The same machinery as the four blocks above, for the mode that had the same
   * problem in a sharper form.
   *
   * Registers answered formal, friendly AND slang in a single call. Unlike the
   * AI Prompt tabs — five rewrites of one rough line — each of these is a full
   * rewrite of whatever was pasted in, and this is the mode people paste whole
   * messages into. So two thirds of every answer was written, streamed and paid
   * for by somebody who opened one tab; and because a version could not be
   * drawn until its tag had landed, a reader who wanted the slang sat through
   * the formal and friendly ones being written first.
   *
   * Now the open tab is written when the question settles, another tab is
   * written by the click that opens it, and a tab that has been written is kept
   * until the question changes — so going back to it is instant and free.
   */
  const [registerTexts, setRegisterTexts] = useState<Partial<Record<Variant['key'], string>>>({});
  /** The register currently being written, or null. Only ever one at a time. */
  const [registerLoading, setRegisterLoading] = useState<Variant['key'] | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const registerRun = useRef<AbortController | null>(null);
  /**
   * Each written register, translated into the OUTPUT language.
   *
   * Registers answers in the language the text was written in, which is the
   * point of it — and leaves somebody who set an output language with a
   * message they chose but cannot hand to the person who reads that language.
   * So under the answer there is a card with the open version translated into
   * the language the `to` picker says (owner, 2026-10-13).
   *
   * Keyed by register like `registerTexts`, written once per tab and kept until
   * the question changes, for the same reason: going back to a tab is instant
   * and is not paid for twice.
   */
  const [registerTranslations, setRegisterTranslations] = useState<Partial<Record<Variant['key'], string>>>({});
  const [registerTranslating, setRegisterTranslating] = useState<Variant['key'] | null>(null);
  /** Tabs whose translation failed: not retried by the effect, so a refused request cannot loop. */
  const [registerTranslationFailed, setRegisterTranslationFailed] = useState<Partial<Record<Variant['key'], true>>>({});
  /**
   * Above the first level the card is written only when asked for (one press,
   * one request — see `manual`): the register whose card was asked for.
   */
  const [registerTranslationAsked, setRegisterTranslationAsked] = useState<Variant['key'] | null>(null);
  const registerTranslationRun = useRef<AbortController | null>(null);
  /**
   * Which question the three texts above are answers to.
   *
   * Same key and same comparison as `styleKey`: thrown away on the render the
   * question changes on, rather than an effect later with a stale version still
   * on screen under a tab that now means something else.
   */
  const registerKey = `${canonical(text)}\u0000${from}\u0000${to}${levelTag}`;
  const registerKeyRef = useRef(registerKey);
  if (registerKeyRef.current !== registerKey) {
    registerKeyRef.current = registerKey;
    registerRun.current?.abort();
    registerRun.current = null;
    if (Object.keys(registerTexts).length) setRegisterTexts({});
    if (registerLoading) setRegisterLoading(null);
    if (registerError) setRegisterError(null);
    registerTranslationRun.current?.abort();
    registerTranslationRun.current = null;
    if (Object.keys(registerTranslations).length) setRegisterTranslations({});
    if (registerTranslating) setRegisterTranslating(null);
    if (Object.keys(registerTranslationFailed).length) setRegisterTranslationFailed({});
    if (registerTranslationAsked) setRegisterTranslationAsked(null);
  }

  /**
   * Write one register.
   *
   * Deliberately NOT routed through `run()`, for the reasons `loadStyle` gives:
   * that path carries the incremental sentence machinery, which exists for
   * translating a paragraph a sentence at a time and means nothing for a
   * rewrite of the whole message — and it owns `output`, which three answers
   * cannot share.
   *
   * Already-written registers return immediately. That guard is also what stops
   * the scheduler and a click from both paying for the same tab.
   */
  const loadRegister = useCallback((key: Variant['key']) => {
    if (!text.trim()) return;
    if (registerTexts[key] !== undefined) return;

    // One at a time. Clicking through the row quickly should leave the tab that
    // is actually open being written, not three abandoned streams racing.
    registerRun.current?.abort();
    const controller = new AbortController();
    registerRun.current = controller;

    setRegisterLoading(key);
    setRegisterError(null);

    let raw = '';
    void translateStream(
      {
        text: canonical(text), from, to, mode: 'native',
        // No summary sentence. It is one rewrite of a message the reader wrote
        // themselves, and a description of it under the tab would be copied
        // along with the version they came for.
        overview: false,
        register: key,
        level: sendLevel,
        signal: controller.signal,
      },
      {
        onDelta: (chunk) => {
          raw += chunk;
          // The tag the server still writes in front of the version. Stripped
          // here rather than left to splitVariants(): with one register per
          // request there is nothing to split, and the tag is not part of the
          // message anybody means to copy.
          setRegisterTexts((prev) => ({
            ...prev,
            [key]: raw.replace(/^\s*\[\[(FORMAL|FRIENDLY|CASUAL)\]\]\s*/, ''),
          }));
        },
        onDone: () => {
          if (controller.signal.aborted) return;
          setRegisterLoading((cur) => (cur === key ? null : cur));
        },
        onError: (message) => {
          if (controller.signal.aborted) return;
          setRegisterError(message);
          setRegisterLoading((cur) => (cur === key ? null : cur));
          // Cleared rather than left half-written: a tab holding two sentences
          // of a message that failed reads as a finished short answer.
          setRegisterTexts((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        },
        onLimit: (message) => {
          if (controller.signal.aborted) return;
          setRegisterError(message);
          setRegisterLoading((cur) => (cur === key ? null : cur));
        },
      },
    );
  }, [text, from, to, registerTexts, sendLevel]);

  /**
   * ── Translate's AI tab ──────────────────────────────────────────────────
   *
   * The Translate pane answers twice: the faithful rendering it has always
   * written, and an EASY one that says the same thing in wording a reader gets
   * right on the first pass. See `EASY_RULES` in the backend's lib/modes.ts for
   * what the second prompt actually asks for.
   *
   * Why it is a tab and not a mode: they are two answers to ONE question, and
   * the reader chooses between them with the source still in front of them.
   * A mode-rail entry would have made it a different question, taken the first
   * answer off the screen to show the second, and needed switching on in the
   * operator's mode list before anybody could see it at all.
   *
   * Written ON THE CLICK, never by the scheduler — the one place this departs
   * from Registers and AI Prompt, deliberately. Those modes have no answer at
   * all until a tab is written, so the open one has to be filled for them. Here
   * the faithful answer is ALREADY being written by the ordinary path on every
   * keystroke that settles, so auto-writing the open AI tab would mean two
   * full model calls for every translation instead of one, forever, for a
   * second reading most of them will not be opened to.
   *
   * A written tab is kept until the question changes, so going back to it is
   * instant and free — the same contract the other two tabs make.
   */
  // One slot per extra tab (Software-AI and Easy), so switching between them
  // keeps what each has already written.
  const [aiTexts, setAiTexts] = useState<Record<ExtraTab, string | null>>(NO_EXTRA_TEXT);
  const [aiBusyMap, setAiBusyMap] = useState<Record<ExtraTab, boolean>>(NO_EXTRA_BUSY);
  const [aiErrors, setAiErrors] = useState<Record<ExtraTab, string | null>>(NO_EXTRA_TEXT);
  const aiRun = useRef<Record<ExtraTab, AbortController | null>>({ ai: null, easy: null });

  /**
   * Which question the extra tabs' texts are answers to.
   *
   * Same key and same during-render comparison as `registerKey`: thrown away on
   * the render the question changes on, rather than in an effect a beat later
   * with a stale AI answer still on screen beside a new source text.
   */
  const aiKey = `${canonical(text)}\u0000${from}\u0000${to}${levelTag}`;
  const aiKeyRef = useRef(aiKey);
  if (aiKeyRef.current !== aiKey) {
    aiKeyRef.current = aiKey;
    for (const k of EXTRA_TABS) {
      aiRun.current[k]?.abort();
      aiRun.current[k] = null;
    }
    if (EXTRA_TABS.some((k) => aiTexts[k] !== null)) setAiTexts(NO_EXTRA_TEXT);
    if (EXTRA_TABS.some((k) => aiBusyMap[k])) setAiBusyMap(NO_EXTRA_BUSY);
    if (EXTRA_TABS.some((k) => aiErrors[k] !== null)) setAiErrors(NO_EXTRA_TEXT);
  }

  /**
   * Write the AI answer for whatever is in the box.
   *
   * Not routed through `run()`, for the reason `loadRegister` gives: that path
   * owns `output`, which the faithful tab is using, and carries the incremental
   * sentence machinery — which would hand this prompt a paragraph one sentence
   * at a time and take away the whole-message context that resolving an implied
   * subject depends on.
   */
  const loadAi = useCallback((tab: ExtraTab) => {
    if (!text.trim()) return;
    if (aiTexts[tab] !== null || aiBusyMap[tab]) return;

    aiRun.current[tab]?.abort();
    const controller = new AbortController();
    aiRun.current[tab] = controller;

    const setBusyFor = (v: boolean) => setAiBusyMap((m) => ({ ...m, [tab]: v }));
    const setErrorFor = (v: string | null) => setAiErrors((m) => ({ ...m, [tab]: v }));
    const setTextFor = (v: string | null) => setAiTexts((m) => ({ ...m, [tab]: v }));

    setBusyFor(true);
    setErrorFor(null);

    /*
     * Whatever happens, this tab stops saying it is being written.
     *
     * ⚠️ NOT redundant with `onDone` and `onError` below, and the gap it closes
     * is the second reason this tab could sit on "Writing the AI version…"
     * forever. `translateStream` has paths that return having called NO
     * handler at all — the sign-in recovery branch (`recoverOrRedirect`) is the
     * live one, and it is reachable from the expired-origin-token 403s that are
     * ordinary traffic here. The faithful answer survives those because it has
     * its own retry around the same call; a tab whose only way out was a
     * handler did not.
     *
     * Attached to the promise rather than added as a third handler so it cannot
     * be missed by a path added later, and given to BOTH outcomes so a rejection
     * clears the tab instead of becoming an unhandled one.
     */
    const finished = () => {
      if (controller.signal.aborted) return;
      setBusyFor(false);
    };

    let raw = '';
    void translateStream(
      {
        text: canonical(text), from, to, mode: 'translate',
        // No summary sentence. The faithful tab beside it already carries the
        // Overview when it was asked for, and a second copy of it under the
        // other tab would be copied along with the translation.
        overview: false,
        ...(tab === 'ai' ? { ai: true } : { easy: true }),
        level: sendLevel,
        signal: controller.signal,
      },
      {
        onDelta: (chunk) => {
          // A superseded stream can still deliver a chunk that was already in
          // flight when it was aborted. Writing it would put the previous
          // question's answer under a tab that now belongs to a new one — and,
          // worse, make the tab look written, so the right answer is never
          // asked for.
          if (controller.signal.aborted) return;
          raw += chunk;
          setTextFor(raw);
        },
        onDone: () => {
          if (controller.signal.aborted) return;
          setBusyFor(false);
        },
        onError: (message) => {
          if (controller.signal.aborted) return;
          setErrorFor(message);
          setBusyFor(false);
          // Cleared rather than left half-written: two sentences of a failed
          // answer read as a finished short one.
          setTextFor(null);
        },
        onLimit: (message) => {
          if (controller.signal.aborted) return;
          setErrorFor(message);
          setBusyFor(false);
        },
      },
    ).then(finished, finished);
  }, [text, from, to, aiTexts, aiBusyMap, sendLevel]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limited, setLimited] = useState<string | null>(null);
  /** `kharma`: what the last answer cost the account, settled (see `onDone`). */
  /** `own`: TrueMarch Model wrote it (the answer's model; unset on a cache hit, where the level says so). */
  const [stat, setStat] = useState<{ ms: number; cached: boolean; same?: boolean; kharma?: number; own?: boolean } | null>(null);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Panes and the content blocks inside them — measured by usePinned.
  const srcPane = useRef<HTMLDivElement>(null);
  const srcBlock = useRef<HTMLDivElement>(null);
  const outPane = useRef<HTMLDivElement>(null);
  const outBlock = useRef<HTMLDivElement>(null);

  /**
   * In-memory results for this page view.
   *
   * The server already avoids a second AI call — a repeat is a ~3ms cache hit —
   * but the browser still pays a round trip to find that out. Someone comparing
   * modes on one piece of text clicks back and forth constantly, and each of
   * those should be instant rather than "wait, flash, appear".
   *
   * It has since become load-bearing for cost as well as for speed: a hit here
   * is the fastest of the paths that skip the model entirely, and the one that
   * catches the whitespace edits nobody thinks of as edits.
   *
   * Keyed on everything that changes the answer. Bounded, with the oldest entry
   * evicted first, so a long session cannot grow it without limit.
   */
  const memo = useRef<Map<string, string>>(new Map());
  /**
   * The memo key of the answer the pane is showing — set wherever an answer
   * for a question is put on screen. Above the first level it is how the page
   * knows the answer in front of the reader is for an EARLIER text, and that
   * the Translate button is the way to the current one.
   */
  const [shownKey, setShownKey] = useState<string | null>(null);
  /**
   * Set when the reader picks "Detect language" themselves, which switches off
   * the automatic adoption of Korean. Declared up here with the other refs
   * because `applyFrom` writes it and the effect that reads it is defined
   * several hundred lines further down.
   */
  const declinedPromotion = useRef(false);
  /** Which model produced what is in the memo — see the wipe in `remember`. */
  const memoModel = useRef<string | null>(null);
  /** Bumped whenever the memo gains an answer; see `remember` and `donePages`. */
  const [memoVersion, setMemoVersion] = useState(0);

  // Rehydrate the memo from the tab's own storage before the first keystroke.
  useEffect(() => {
    const { model, entries } = loadResults();
    memoModel.current = model;
    memo.current = new Map(entries);
  }, []);

  // Stored preferences, read after mount so the first client paint matches the
  // HTML the server sent. `overviewSeen` starts as `true` for the same reason:
  // the honest default before storage has been read is "do not animate", since
  // a sparkle that appears and then vanishes on hydration is worse than one that
  // arrives a frame late.
  useEffect(() => {
    setOverview(loadOverview());
    setOverviewSeen(loadOverviewSeen());
  }, []);

  // Cancels the in-flight request. A fast typist would otherwise have an older,
  // slower translation land on top of a newer one.
  const inflight = useRef<AbortController | null>(null);

  /** The silent half of the wait, then the second-by-second half. */
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  /**
   * The live draft's own timer, deliberately separate from `idleTimer`.
   *
   * They are two waits with two lengths running over the same keystroke, and
   * one ref could only hold one of them — the draft would cancel the countdown
   * it is supposed to run ahead of. `cancelSchedule` clears both.
   */
  const liveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * How long the pane waits, and how long the draft waits — both the operator's,
   * both arriving with the mode list. See the constants at the top of the file.
   */
  const [holdSeconds, setHoldSeconds] = useState(DEFAULT_HOLD_S);
  const [liveMs, setLiveMs] = useState(DEFAULT_LIVE_MS);

  /**
   * This one scheduler pass must not fetch a live draft.
   *
   * Set by a paste that carried no separator — text somebody is about to keep
   * typing. Without it, "pasted text waits for Enter" would be defeated by the
   * draft answering it 600ms later anyway, and the rule would be one nobody
   * could observe. Cleared on the next pass, so typing after the paste gets its
   * drafts back immediately.
   */
  const noDraft = useRef(false);

  /**
   * A TYPING wait is running — either half of it.
   *
   * Deliberately narrower than "a timer is running", and the difference is the
   * whole of what this flag is for. It drives the hold layer in the output pane
   * (see TypingHold) and the 0.5 dim under it, and both of those are statements
   * about the reader rather than about the scheduler: "you are mid-sentence, so
   * the answer above is the last one and not this one".
   *
   * The other two waits are excluded because neither is that. A deletion waits
   * 300ms, which is below the point at which anything on screen informs anybody
   * and well inside the range where a character appearing and vanishing is just
   * a flash. A settling click waits 350ms with a request already streaming, so
   * the pane is working — telling somebody to stop typing while it does would
   * be false. `countdown` alone could not carry this: it is null for the whole
   * silent half, which is the part where the pane looks most broken.
   */
  const [waiting, setWaiting] = useState(false);

  /** What the box held last time the scheduler looked, to tell typing from a
   *  changed setting: one should wait, the other is a request for an answer. */
  const prevText = useRef('');
  /** Set by the actions that mean "now" — a paste, or Enter. */
  const forceNext = useRef(false);

  /**
   * Typing, pasting, cutting — an edit made with the caret, which is the one
   * kind that may move the visitor onto another page.
   *
   * The caret is tracked through the edit as an offset into the DOCUMENT rather
   * than into the page, and the page shown afterwards is whichever one that
   * offset ended up on. That is what makes typing past the end of page 3 carry
   * on into page 4 instead of stopping dead at a boundary, and what keeps a
   * deletion that empties a page from stranding the visitor on a page that no
   * longer exists.
   *
   * Both paginations here are over the whole document and both are cheap — a
   * scan of at most `max` characters per page — so this stays a keystroke-rate
   * operation even on the ten-page maximum.
   */
  const editPage = useCallback(
    (value: string, at: number) => {
      const here = pages[Math.min(pageRef.current, pages.length - 1)];
      const next = (doc.slice(0, here.start) + value + doc.slice(here.end)).slice(0, docLimit);
      const caretInDoc = Math.min(here.start + at, next.length);
      const nextPages = paginate(next, pageSize);

      /**
       * Where to land, and the one place the caret is the wrong thing to follow.
       *
       * Typing that spills over a page boundary has to follow the caret, or the
       * next character would be typed onto a page nobody can see. But a PASTE of
       * a whole document also leaves the caret at the very end of it, and
       * following that drops the reader on the last page of something they have
       * not read a word of — measured on the first build of this: paste 13,000
       * characters, land on page 3 of 3, with Next already greyed out.
       *
       * So a change that brings in more than a page of text at once is treated
       * as what it is — arriving text rather than typed text — and opens where
       * the new material STARTS.
       */
      let where = caretInDoc;
      let caretOnPage = -1;
      if (next.length - doc.length > pageSize) {
        let k = 0;
        while (k < here.text.length && k < value.length && here.text[k] === value[k]) k++;
        where = here.start + k;
        caretOnPage = 0;
      }
      const landsOn = pageOfOffset(nextPages, where);

      setDoc(next);
      setPage(landsOn);
      setCaret(caretOnPage >= 0 ? caretOnPage : caretInDoc - nextPages[landsOn].start);
      // Say so, once, when a paste was bigger than the box will hold. Silence is
      // what the old truncation did.
      setClipped(
        here.start + value.length + (doc.length - here.end) > docLimit
          ? `That is longer than ${docLimit.toLocaleString()} characters — the end was not kept.`
          : null,
      );
    },
    [doc, pages, pageSize, docLimit],
  );

  /**
   * ── SELECT ALL MEANS ALL OF IT ────────────────────────────────────────────
   *
   * The box holds ONE PAGE. Everything else about the document — the other
   * pages, the character budget, the page turner — is built around that, and it
   * is the right design: an editor holding 50,000 characters is what the paging
   * exists to avoid.
   *
   * But it leaks through Ctrl/⌘+A, because the browser's select-all selects the
   * textarea, and the textarea is one page. So select-all and delete emptied
   * page 1 and left pages 2..10 sitting there — while the ✕ beside the box,
   * which says the same thing in a different gesture, emptied the whole thing.
   * Two controls that mean "clear this" disagreeing about what "this" is.
   *
   * Nobody reading their own ten-page document thinks of it as ten documents.
   * Ctrl+A means the lot, so that is what it now does: the keystroke is noted
   * here, and the next edit, copy or cut is applied to the DOCUMENT instead of
   * to the page.
   *
   * A ref and not state, because the handlers that read it run on the same tick
   * the key was pressed on and must not see a render behind.
   */
  const selectedAll = useRef(false);
  /** The same fact, for the badge that tells the reader it happened. */
  const [showSelectedAll, setShowSelectedAll] = useState(false);

  /**
   * Was the page's whole text selected at the instant the edit began?
   *
   * `beforeinput` is the only moment this can be asked. By the time `onChange`
   * runs the selection has already been replaced by whatever was typed, and
   * `onSelect` — see the note on it — does not fire in step with the selection
   * it reports. The native event is used rather than React's `onBeforeInput`,
   * which is a synthetic that covers insertions and not deletions; Delete over
   * a selection is the case this whole change exists for.
   */
  const wholeAtEdit = useRef(false);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const onBeforeInput = () => {
      wholeAtEdit.current =
        el.selectionStart === 0 && el.selectionEnd === el.value.length && el.value.length > 0;
    };
    el.addEventListener('beforeinput', onBeforeInput);
    return () => el.removeEventListener('beforeinput', onBeforeInput);
  }, []);

  /** Forget a whole-document selection. Called by anything that moves the caret. */
  const dropSelectAll = useCallback(() => {
    if (!selectedAll.current) return;
    selectedAll.current = false;
    setShowSelectedAll(false);
  }, []);

  /**
   * Replace the WHOLE document, as the edit that lands on a select-all.
   *
   * Deliberately not `editPage`: that one splices into the current page's slice
   * of `doc`, which is exactly the behaviour being corrected here. Typing a
   * character over a ten-page selection leaves a one-character document, a
   * paste replaces everything, and Delete empties it — all of which are what
   * the same keystrokes do in any editor.
   */
  const replaceWholeDoc = useCallback((value: string, at: number) => {
    const next = value.slice(0, docLimit);
    const first = paginate(next, pageSizeRef.current)[0];
    setDoc(next);
    setPage(0);
    setCaret(Math.min(at, first ? first.text.length : next.length));
    // None of this belonged to the text that was just replaced.
    undo.current = null;
    setDismissed(null);
    setClipped(
      value.length > docLimit
        ? `That is longer than ${docLimit.toLocaleString()} characters — the end was not kept.`
        : null,
    );
  }, [docLimit]);

  /**
   * Turn to another page.
   *
   * A page turn is a REQUEST for that page's translation, not a draft in
   * progress — the same reasoning as Enter, paste and Swap — so it skips the
   * typing wait entirely. A page that has been read before costs nothing at all:
   * its answer is still in the memo, and `serveFromMemo` puts it back instantly.
   */
  const goToPage = useCallback(
    (n: number) => {
      const next = Math.max(0, Math.min(n, pages.length - 1));
      if (next === pageRef.current) return;
      forceNext.current = true;
      // None of the per-page editing state belongs to the page being opened.
      setCaret(0);
      setDismissed(null);
      undo.current = null;
      // Turning the page is choosing a place to work, which is the opposite of
      // having the whole document selected.
      dropSelectAll();
      setPage(next);
    },
    [pages.length, dropSelectAll],
  );

  /** The voice-file dialog, opened from the button under the box. */
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);

  /**
   * A paste that did not fit the document limit, in the words the counter shows.
   * Cleared by the next edit that fits.
   */
  const [clipped, setClipped] = useState<string | null>(null);

  /** The feedback dialog, opened from the link under the whole card. */
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  /*
   * ── THE NOTEBOOK ──────────────────────────────────────────────────────────
   *
   * A window over the translator rather than a route, because what somebody
   * comes here for is the sentence they are already typing — see the file note
   * in components/notebook/NotebookDialog.tsx. `notebookEntry` is set only when
   * it should open ON something, which is what the save toast's "Open" does.
   */
  const [notebookOpen, setNotebookOpen] = useState(false);
  const [notebookEntry, setNotebookEntry] = useState<string | null>(null);
  /**
   * Ctrl+7 / the rail button hold the translation here and ask WHICH folder to
   * keep it in — a personal notebook is somewhere you file things on purpose, so
   * the save is a choice, never silent. Null while the picker is closed.
   */
  const [savePickerOpen, setSavePickerOpen] = useState(false);
  /**
   * What Ctrl+7 does, reached through a ref.
   *
   * The handler for it lives with the other mode-number keys, hundreds of lines
   * above the notebook's own code — and a `const` cannot be named in a
   * dependency array before it is initialised, which is a ReferenceError at
   * render rather than a warning. The ref is written on every render and read
   * only from inside the handler, exactly as `autoStyle` and `autoRegister`
   * below are, so the key always runs the current function without the effect
   * having to depend on it.
   */
  const notebookAction = useRef<() => void>(() => {});
  const notebookAllowedRef = useRef(notebookAllowed);
  notebookAllowedRef.current = notebookAllowed;
  /**
   * What Ctrl+Alt+Z has just done, said out loud for a moment.
   *
   * A chord that saves silently is a chord nobody trusts: the second time they
   * press it they press it twice, because there was no way to tell whether the
   * first one worked. The toast names the FOLDER — that is the one fact the
   * chord decides on the user's behalf — and carries the way into it.
   */
  const [saved, setSaved] = useState<{ id: string; folder: string; duplicate: boolean } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingEntry, setSavingEntry] = useState(false);

  /* ── Dictation ────────────────────────────────────────────────────────────
   *
   * The mic button owns the microphone; this owns where its words land.
   *
   * Speech arrives in two kinds and they must not be treated alike. A PARTIAL
   * is a guess that will be replaced — usually two or three times, as the
   * recogniser hears the rest of the word — and a FINAL is a run that will
   * never be revised. Appending both would write "so", "sorry", "so I said"
   * into the box one after another and leave all three there.
   *
   * So dictation keeps an anchor: the text as it stood after the last committed
   * run (`base`) and the offset in it where speech is going (`at`). What is on
   * screen at any moment is `base` with the current partial spliced in at `at`,
   * which makes replacing a partial the same operation as writing the first
   * one, and makes committing one a matter of moving the anchor past it.
   *
   * `at` is where the CARET was when the button was pressed, not the end of the
   * text, so dictating into the middle of a paragraph works the way typing
   * there does. It is re-read on every manual edit (see the textarea's
   * `onChange`), because a user who types while the microphone is open has just
   * moved the place their next words belong.
   */
  const dictation = useRef<{ base: string; at: number } | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  /**
   * The language the microphone turned out to be hearing.
   *
   * Not chosen by anyone — discovered from the audio itself, by the one route
   * that can do that (see MicButton's `onLanguage`). It exists so the discovery
   * only has to happen once: the first phrase identifies the language, and
   * everything after it is recognised by the browser's own free engine, which
   * had to be told.
   *
   * Session-scoped and deliberately not remembered. A stored answer would be
   * asserted over tomorrow's speech without ever being checked against it,
   * which is the same wrong-language failure it exists to fix — and re-hearing
   * it costs one phrase.
   */
  const [voiceLang, setVoiceLang] = useState<string | null>(null);
  /** Where to put the caret once React has painted a committed run. */
  const caretAfterPaint = useRef<number | null>(null);
  /** Set by the actions that remove text, which refresh on a short leash. */
  const deleting = useRef(false);
  /**
   * The question currently on the wire.
   *
   * Without it, editing back to text that is already being fetched — type a
   * character, delete it — would start a second identical request while the
   * first is still streaming, and pay for both.
   */
  const inflightKey = useRef<string | null>(null);

  /** The last conversion made on the space bar, for one backspace to take back. */
  const undo = useRef<{ after: string; before: string; caret: number; source: string } | null>(null);

  /**
   * Keyed on the words, not on the layout.
   *
   * `canonical` throws away everything that is whitespace and nothing that is
   * not: blank lines, indents, trailing spaces, `\r\n`, and the difference
   * between NFC and NFD Korean. What is left is one segment per line, which is
   * the actual question.
   *
   * This is where most of the saving in this file now comes from. Pressing Enter
   * to open a paragraph, tidying the indent on a pasted list, or letting the
   * space bar close a word all used to be a full re-translation of the whole
   * box: none of them changed a single word, but every one of them changed the
   * string being hashed. They now land on the answer already in hand — and a hit
   * here never even schedules a request, so they cost nothing at all rather than
   * costing a round trip to be told nothing changed.
   *
   * The layout is not lost by keying it out: it is re-applied to the answer on
   * the way to the screen, so the pane still lays out exactly like the box. See
   * `shape` and `reshape` below.
   *
   * The same string is what goes on the wire (see `run`), so the server's cache
   * and this one agree about what a repeat is — including across two visitors
   * who typed the same sentences with different spacing.
   */
  const memoKey = useCallback(
    (t: string, f: string, dst: string, m: string, bare = false) =>
      // A mode that follows the input language ignores the target picker
      // entirely, so including it would file the same answer under as many keys
      // as the user happens to have targets — and re-fetch a translation we are
      // already holding the moment they touch a control that changes nothing.
      //
      // `bare` files it with NO level: the key the first level uses, so it can
      // never be served as an answer bought at a higher one (see `loadEntry`).
      `${f}|${modes.find((x) => x.id === m)?.followsInputLanguage ? 'src' : dst}|${m}${bare ? '' : levelTag}|${canonical(t)}`,
    [modes, levelTag],
  );

  const remember = useCallback((key: string, value: string, model?: string) => {
    const m = memo.current;
    // An empty answer is not an answer. Filed, it would be served back for this
    // text from now on — instantly, and blank — and no paste, Enter or reload
    // in this tab would ever ask the server again.
    if (!value.trim()) { m.delete(key); return; }
    // The admin can change the translation model from the console, and results
    // from the old one would otherwise be replayed from storage for the rest of
    // the session. The first answer from a new model is the signal to start over.
    //
    // `model` is optional because not every entry comes back from one: `swap`
    // files a pair the user is already looking at (see `seedSwapPair`). Such an
    // entry belongs to whichever model produced the answer it was derived from,
    // which is the one already recorded here — so it must not reset this, and
    // must not look like a model change either.
    if (model) {
      if (memoModel.current && memoModel.current !== model) m.clear();
      memoModel.current = model;
    }
    m.delete(key);          // re-insert so recency is the map's own order
    m.set(key, value);
    if (m.size > 60) m.delete(m.keys().next().value as string);
    saveResults(memoModel.current, m);
    // The memo is a ref, so nothing re-renders when it gains an entry — and the
    // page bar's "3 of 7 translated" is a reading of exactly that. One counter
    // per finished answer is enough to make it honest without making the memo
    // itself into state.
    setMemoVersion((v) => v + 1);
    }, []);

  /**
   * Teach the memo the pair that swapping puts on screen — in both directions.
   *
   * Swapping used to cost a model call EVERY time, and worse, a different one
   * each time. Pressing it moves the answer into the input box, which changes
   * the text, which is a question the memo has never seen — so the model was
   * asked to translate `T(x)` back, and returned `x'`: the same meaning, not the
   * same words. Press swap again and the app translated `x'` forward to `T'(x')`,
   * and again, and again. Measured on the live site: six presses, six model
   * calls, and the sentence visibly drifting on every one of them.
   *
   * What fixes it is noticing that the app is not missing this answer at all.
   * The moment before the swap, the screen holds `x` in one pane and `T(x)` in
   * the other, as a matched pair. Swapping only exchanges which side they are
   * drawn on. Filing both directions of that pair is therefore not inventing a
   * translation — it is writing down the one already on screen, which is exactly
   * what the button claims to do. Swapping becomes free, instant, and its own
   * inverse: back and forth for as long as you like, and you always land on the
   * words you actually typed rather than on a paraphrase of a paraphrase.
   *
   * Two entries, because the forward one is not already there in the usual case:
   * the original was fetched with the source picker on "Detect language", so it
   * is filed under `auto`, and after the swap the picker holds the resolved code.
   *
   * Client-side only, deliberately. The server's cache is shared by every
   * visitor and holds answers a model actually produced; this pair is a
   * rearrangement of one, and it has no business being handed to somebody else
   * as a fresh translation.
   */
  const seedSwapPair = useCallback(
    (src: string, dst: string, source: string, plain: string, raw: string) => {
      if (!source.trim() || !raw.trim()) return;
      // Reverse — the answer, put back, is the text it came from. Keyed on the
      // PLAIN answer, because that is what is about to be in the input box, and
      // valued with the user's own words, which carry no tags of their own.
      remember(memoKey(plain, dst, src, mode), source);
      // Forward — the same question the user just asked, filed under the
      // resolved source language rather than under `auto`.
      //
      // The RAW answer here, tags and all. Everything else in the memo stores
      // what the stream produced, and the pane's splitters run on the way out;
      // storing the stripped text would quietly cost this pair its Overview card
      // and its notes the next time it was served.
      remember(memoKey(source, src, dst, mode), raw);
    },
    [remember, memoKey, mode],
  );

  const activeMode = useMemo(() => modes.find((m) => m.id === mode), [modes, mode]);
  const lockedTarget = activeMode?.forcesTarget ?? null;
  /**
   * This mode answers in the language the text is written in.
   *
   * Drives three things at once: the language bar drops its whole target half,
   * the mode buttons name that language in their labels and tooltips, and the
   * memo key above stops pretending the target picker matters.
   */
  const followsInput = !!activeMode?.followsInputLanguage;

  /**
   * Nothing to translate: the two languages are the same one.
   *
   * The answer to "put this English into English" is the English you already
   * typed, so this is settled here — no wait, no request, no model, no second
   * of waiting to be handed back the input.
   *
   * Only for the mode that says it is a no-op. Humanize, Registers,
   * Mistakes and the CEFR modes all answer in the language they were given and
   * are doing real work;
   * short-circuiting on `from === to` alone would break every one of them. The
   * backend enforces the same rule for anything that is not this browser.
   *
   * `from` of `auto` never qualifies — a guess about the source language is not
   * a good enough reason to hand someone their own text back.
   */
  const identity =
    !!activeMode?.identityWhenSameLanguage && from !== 'auto' && from === to;

  // Mint the origin token on mount so the first translation does not pay for
  // the handshake on top of its own latency.
  useEffect(() => { bootstrapToken(); }, []);

  /**
   * Land with the cursor already in the input — typing is the only thing anyone
   * comes here to do.
   *
   * Skipped on touch devices: focusing on load throws up the on-screen keyboard
   * and scrolls the page before the visitor has decided to type, hiding the very
   * controls they might have wanted first.
   *
   * The test is deliberately "opt OUT on coarse pointers" rather than "opt IN on
   * fine ones". Requiring a positive `(hover: hover) and (pointer: fine)` match
   * silently loses the focus on any browser that reports those features oddly —
   * headless Chrome answers `none` to both — so the default has to be to focus,
   * with only genuine touch input suppressing it.
   */
  useEffect(() => {
    if (!window.matchMedia('(pointer: coarse)').matches) {
      inputRef.current?.focus();
    }
  }, []);

  // Input sizing is pure CSS — see `.grow-wrap` in globals.css. The box always
  // fits its own text, so it never scrolls inside itself. No measuring, no
  // effect, nothing to fire at the right moment.

  useEffect(() => {
    // The coin into the cache now, while the pane is empty and nothing needs
    // it. By the time somebody has typed enough to see it, it is local.
    preloadCoin();

    Promise.all([fetchLanguages(), fetchModes()])
      .then(([langs, m]) => {
        setLanguages(langs);
        setModes(m.modes);
        // Names for the AI Prompt tabs. Operator-editable, so they travel with
        // the mode list rather than being written into the frontend.
        if (m.promptStyles?.length) setPromptStyles(m.promptStyles);
        // The page size is the server's, whenever it says. See `pageSize`.
        if (m.maxInputChars && m.maxInputChars > 0) setPageSize(m.maxInputChars);

        // How the box behaves, also the server's. Both guarded on `typeof`
        // rather than truthiness: `livePreviewMs: 0` is the live draft's OFF
        // switch, and a `||` here would read the operator turning it off as the
        // field being absent and turn it straight back on.
        if (typeof m.typingCountdownSeconds === 'number' && m.typingCountdownSeconds > 0) {
          setHoldSeconds(m.typingCountdownSeconds);
        }
        if (typeof m.livePreviewMs === 'number' && m.livePreviewMs >= 0) {
          setLiveMs(m.livePreviewMs);
        }

        // Seed the tab bars only once the supported set is known — the browser
        // may ask for languages we do not have, and those must be dropped
        // rather than shown as dead tabs.
        const supported = langs.map((l) => l.code);
        const src = loadTabs('source', supported);
        const tgt = loadTabs('target', supported);
        setSourceTabs(src);
        setTargetTabs(tgt);

        // Restore what this browser chose last time. Validated against the sets
        // we just fetched, so a language we dropped or a mode the admin switched
        // off cannot strand the app on a selection that no longer exists.
        const lastTarget = loadLastTarget(supported);
        setTo(lastTarget ?? tgt[0] ?? 'en');

        const lastMode = loadLastMode(m.modes.map((x) => x.id));
        if (lastMode) setMode(lastMode);
        else setMode(m.default);
      })
      .catch(() => setError('Could not load the language list. Reload the page.'));
  }, []);

  /**
   * Choosing a language.
   *
   * A language only gets promoted onto the visible tabs when it came from the
   * DROPDOWN — i.e. it was not on the bar and has to make room. Clicking one of
   * the three tabs must leave the order alone: reshuffling the row under the
   * cursor moves the other tabs out from under the pointer, so the next click
   * lands on something the user did not aim at.
   */
  /**
   * Put a language into the source picker and its tab bar, and nothing else.
   *
   * The half of `pickFrom` that only moves state. Swap uses these directly: it
   * has already worked out both sides and must not have them adjusted under it.
   */
  const applyFrom = useCallback(
    (code: string, fromMenu = false) => {
      setFrom(code);
      if (code === 'auto') {
        // Asked for detection explicitly. Remembered so the Korean promotion
        // above does not immediately take it back — see `declinedPromotion`.
        declinedPromotion.current = true;
        return;
      }
      setSourceTabs((prev) => {
        if (!fromMenu && prev.slice(0, TAB_COUNT).includes(code)) return prev;
        const next = promote(prev, code, languages.map((l) => l.code));
        saveTabs('source', next);
        return next;
      });
    },
    [languages],
  );

  /** The same for the target. */
  const applyTo = useCallback(
    (code: string, fromMenu = false) => {
      setTo(code);
      saveLastTarget(code);
      setTargetTabs((prev) => {
        if (!fromMenu && prev.slice(0, TAB_COUNT).includes(code)) return prev;
        const next = promote(prev, code, languages.map((l) => l.code));
        saveTabs('target', next);
        return next;
      });
    },
    [languages],
  );

  /**
   * Choosing a language for one side, when that language is already on the
   * other, FLIPS the pair — it never leaves both halves reading the same.
   *
   * This is the bug that reached production. Korean → English, then a click on
   * the Korean tab in the target half, and the app sat on Korean → Korean: a
   * pair the translate mode answers by handing back the input unchanged (see
   * `identity`, and `isIdentity` on the server, which agrees). The panes then
   * showed the same English text twice, with an overview card above each in a
   * different language, and nothing anybody clicked made it translate — because
   * from the app's point of view it already had. It reads as the cache serving
   * a stale answer. It is not the cache; it is a question whose true answer is
   * the question.
   *
   * The state was reachable from the pickers only, never from Swap — Swap has
   * refused a same-language pair from the beginning ("Both sides are already
   * the same language"). Which is the argument for this: a control that refuses
   * to strand you is not a special case, it is the rule the other two controls
   * were missing.
   *
   * Flipping rather than refusing, because "Korean" clicked in the target half
   * of a Korean → English pair is not a mistake — it is somebody asking for the
   * other direction, said with the only two controls in front of them. Google
   * Translate reads it the same way. The TEXT deliberately stays where it is:
   * only Swap moves the words between the panes, and a language click that also
   * emptied the box someone had typed in would be a far worse surprise than the
   * one this fixes.
   */
  const pickFrom = useCallback(
    (code: string, fromMenu = false) => {
      // Locked and follows-input modes own the target; there is nothing to flip.
      if (code !== 'auto' && code === to && !lockedTarget && !followsInput) {
        /*
         * Where the target goes. The old source is the honest answer — it is
         * the language the pair was already about — and on "Detect language"
         * that means whatever detection resolved to. Failing both (an empty box
         * on a fresh visit), the first target tab that is not the language just
         * chosen, so the pair is at least valid.
         */
        const back = from !== 'auto' ? from : detected?.code ?? null;
        const nextTo =
          back && back !== code
            ? back
            : targetTabs.find((t) => t !== code) ?? (code === 'en' ? 'ko' : 'en');
        applyTo(nextTo);
      }
      applyFrom(code, fromMenu);
    },
    [applyFrom, applyTo, to, from, detected, targetTabs, lockedTarget, followsInput],
  );

  const pickTo = useCallback(
    (code: string, fromMenu = false) => {
      // The source moves to where the target was — a straight flip, since the
      // target is always a real language rather than `auto`.
      if (code === from && !followsInput) applyFrom(to);
      applyTo(code, fromMenu);
    },
    [applyFrom, applyTo, from, to, followsInput],
  );

  // A mode may pin its own target (A1/A2/B1 always answer in English). Follow it
  // so the picker never disagrees with what the server will actually do.
  useEffect(() => {
    if (lockedTarget && to !== lockedTarget) setTo(lockedTarget);
  }, [lockedTarget, to]);

  /**
   * The language the user is writing in, as far as the app can currently tell.
   *
   * Named explicitly rather than picked out of `from`/`detected` at each use
   * site, because four separate pieces of chrome depend on it — the mode labels,
   * the mode tooltips, the flag on the Registers button and the line where
   * the target tabs used to be — and they must never disagree with each other.
   *
   * Null is a real answer: an empty box, or Latin text the detector has not
   * settled yet. Every consumer has a sensible thing to show for it, and all of
   * them prefer that to a language that the next keystroke could disprove.
   */
  const inputLang: InputLanguage | null = useMemo(() => {
    const code = from !== 'auto' ? from : detected?.code ?? null;
    if (!code) return null;
    const entry = languages.find((l) => l.code === code);
    return {
      code,
      name: entry?.name ?? detected?.name ?? code,
      // The language's first country, except where this app has an opinion.
      // English lists `gb` first — right for the picker, where three flags say
      // "this language, in these places" — but the Registers prompt asks
      // for AMERICAN slang, and a Union Jack over "kinda urgent lol" promises
      // the wrong one of the two.
      country: FLAG_FOR_LANG[code] ?? entry?.countries?.[0] ?? null,
    };
  }, [from, detected, languages]);

  /* ── How is that said? ──────────────────────────────────────────────────── */

  /**
   * The phonetics of the word in the box, when it is a word.
   *
   * Held with the text it describes rather than on its own, because the box
   * moves faster than the network: without the word attached, a look-up that
   * lands after two more keystrokes would draw the pronunciation of something
   * the user has already finished deleting.
   */
  const [phonetic, setPhonetic] = useState<{ word: string; said: Pronunciation } | null>(null);
  /**
   * Words asked about in this session, including the ones with no answer.
   *
   * Backspacing over a word and typing it again is the single commonest thing
   * that happens in this box, and without a local memo each pass would ask the
   * server again. `null` is stored deliberately: a word nobody can transcribe
   * must be remembered as such, or it is re-asked on every visit to it.
   */
  const phonetics = useRef(new Map<string, Pronunciation | null>());
  /**
   * The word the pronunciation route said was not a word.
   *
   * Held as state rather than read off `phonetics` because it has to make the
   * dictionary entry re-render when it arrives, and a ref cannot.
   */
  const [noSound, setNoSound] = useState<string | null>(null);

  useEffect(() => {
    // Not a word: a sentence, an empty box, a number, a URL. The card is for
    // looking something up, and none of these is that.
    const word = wordForLookup(text);
    if (!word) {
      setPhonetic(null);
      setNoSound(null);
      return;
    }

    const lang = inputLang?.code ?? 'auto';
    const key = `${lang}:${word}`;
    if (phonetics.current.has(key)) {
      const held = phonetics.current.get(key) ?? null;
      setPhonetic(held ? { word, said: held } : null);
      setNoSound(held ? null : word);
      return;
    }

    // Nothing is cleared here on purpose. Someone typing the second half of a
    // word still has the first half's card under the box, and blanking it on
    // every keystroke would make it flicker rather than change.
    const abort = new AbortController();
    const timer = setTimeout(() => {
      fetchPronunciation(word, lang, abort.signal)
        .then((said) => {
          phonetics.current.set(key, said);
          setPhonetic(said ? { word, said } : null);
          // A NO is recorded, not just a missing YES. It is the one thing the
          // pronunciation knows that the local tests above cannot: this is a
          // keyboard mash or a layout accident rather than a word. The entry
          // reads it to retract itself — see `shownEntry`.
          setNoSound(said ? null : word);
        })
        // Silence is the whole error policy for this card — see the route. An
        // abort is us superseding it, and anything else is a card that does not
        // appear, which is exactly what the visitor had a moment ago.
        .catch(() => {});
    }, WORD_IDLE_MS);

    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [text, inputLang]);

  /**
   * The language the microphone should expect, and its name for the tooltip.
   *
   * Recognition is not detection. An engine is told what to listen for and
   * matches against that language's sounds, so the wrong tag does not degrade
   * into a low-confidence guess — set to English, it hears Korean as confident
   * English nonsense. Every one of the 234 languages carries a country in the
   * registry (bar two, which `speechLang` names itself), so what goes out is a
   * real regional tag rather than a bare code.
   *
   * The interesting case is "Detect language" with an empty box, where there is
   * nothing to detect FROM — dictation is the thing that would produce the text
   * detection needs. Falling back to the browser's own default is what happens
   * with no tag at all, and it is wrong for exactly the people this app is for:
   * a Korean speaker on an English-locale laptop. So it falls back to the first
   * of their own source-language tabs instead, which is seeded from
   * `navigator.languages` and then follows what they actually pick — a guess
   * made from their behaviour rather than from their operating system.
   *
   * Once a language IS detected the tag changes, and MicButton restarts the
   * engine on it mid-dictation.
   */
  const dictationLang = useMemo(() => {
    // The picker first — an explicit choice, or a language already detected from
    // text in the box — then whatever the voice itself turned out to be.
    //
    // ⚠️ There is deliberately NO third guess here. It used to fall back to the
    // user's most-used source language, which on a fresh visit is really the
    // browser's locale, and an empty string looked like the worse answer. It is
    // the better one: empty means "nobody knows", and that is what sends the
    // first phrase to the route that can find out instead of to a recogniser
    // that would confidently mishear it. Guessing here would put the old bug
    // back.
    const code = inputLang?.code ?? voiceLang ?? '';
    if (!code) return { code: '', tag: '', name: '' };
    const entry = languages.find((l) => l.code === code);
    // `inputLang.country` already applies this app's flag overrides; a heard
    // language has no such opinion and takes the registry's first country.
    const countries = inputLang?.code === code && inputLang.country
      ? [inputLang.country]
      : entry?.countries ?? [];
    return {
      code,
      tag: speechLang(code, countries),
      name: entry?.name ?? inputLang?.name ?? code,
    };
  }, [inputLang, voiceLang, languages]);

  /**
   * The two tags the Listen buttons read in: the language of the box, and the
   * language of the answer.
   *
   * Deliberately NOT `dictationLang`, which is about the microphone and carries
   * a fallback for "nobody knows yet" that belongs to recognition alone — a
   * recogniser given the wrong language hears nonsense, while a voice given the
   * wrong one merely reads with an accent, and an empty tag simply means no
   * button. See lib/speak.
   */
  const speakTag = useCallback(
    (code: string | null | undefined) => {
      if (!code || code === 'auto') return '';
      const entry = languages.find((l) => l.code === code);
      // The app's own flag opinion applies here too: `en` is read by an American
      // voice, which is the one the Registers prompt is written for.
      const country =
        inputLang?.code === code && inputLang.country
          ? inputLang.country
          : FLAG_FOR_LANG[code] ?? entry?.countries?.[0];
      return speechLang(code, country ? [country] : []);
    },
    [languages, inputLang],
  );

  const sourceSpeechTag = useMemo(() => speakTag(inputLang?.code), [speakTag, inputLang]);
  /** A mode that answers in the input's language is read in that language. */
  const targetSpeechTag = useMemo(
    () => speakTag(followsInput ? inputLang?.code : to),
    [speakTag, followsInput, inputLang, to],
  );

  /** Paint a detected label from the script alone, before any request goes out. */
  useEffect(() => {
    if (from !== 'auto') {
      setDetected(null);
      return;
    }
    const guess = detectScriptClient(text);
    if (guess) setDetected(guess);
    else if (!text.trim()) setDetected(null);
    // Neither: the script says nothing about Latin text. Keep whatever the
    // server last resolved — unless the characters now disprove it, which is
    // what `contradictsScript` is for. Without this, replacing Chinese with
    // Spanish leaves the Chinese flag on the Registers button and the word
    // "Chinese" in four mode labels until the next answer arrives.
    else setDetected((prev) => (contradictsScript(text, prev?.code) ? null : prev));
  }, [text, from]);

  /**
   * Korean, adopted as the input language the moment it is recognised.
   *
   * "Detected" is a label; naming the language is a decision, and only the
   * decision brings the keyboard (see `keyboardOffered`). Leaving it at
   * "Detect language" left the reader with Korean on screen, a detector that
   * agreed it was Korean, and no way to type another word of it — which is the
   * gap the owner asked to close.
   *
   * It is the paths that put Korean in the box WITHOUT a Korean keyboard that
   * make this worth doing: a paste, a photograph read by OCR, dictation, and
   * above all the wrong-IME card — type `wndrnr`, accept `중국`, and from that
   * moment the keys are Korean.
   *
   * Korean only, deliberately. Every other language on the bar is typed with
   * the keyboard the reader already has, so promoting them would change what
   * the picker says and hand back nothing for it.
   */
  useEffect(() => {
    if (from !== 'auto' || detected?.code !== KEYBOARD_LANG) return;
    // Somebody who has asked for "Detect language" by hand is answered, not
    // overruled — otherwise the option could never be selected while Korean was
    // in the box, because this would undo the click every time.
    if (declinedPromotion.current) return;

    // Carry the answer across the key change. The memo is keyed on the source
    // as the PICKER states it, so 'auto' and 'ko' file the same translation
    // twice — without this the promotion costs a needless round trip and a
    // "Translating…" flicker over an answer that is already on screen.
    const held = memo.current.get(memoKey(text, 'auto', to, mode));
    if (held !== undefined) memo.current.set(memoKey(text, KEYBOARD_LANG, to, mode), held);

    /*
     * `pickFrom`, not `applyFrom` — so this behaves exactly as though the
     * reader had clicked Korean in the picker themselves.
     *
     * The difference is what happens when the TARGET is already Korean. This
     * used to stand down entirely in that case, on the grounds that flipping
     * someone's target is a bigger change than selecting their source. That
     * was wrong in practice: a reader whose target is Korean is precisely the
     * reader who types Korean into the box, and for them the feature simply
     * never appeared. `pickFrom` resolves the collision the way every manual
     * pick in this app resolves it — the target moves to the first other tab —
     * which is at least a rule people can learn, rather than one control that
     * silently does nothing under a condition they cannot see.
     */
    pickFrom(KEYBOARD_LANG);
  }, [from, detected, to, text, mode, memoKey, pickFrom]);

  /**
   * Work out how much of this text we are already holding.
   *
   * Returns null for a mode that cannot be answered a sentence at a time, and
   * for text with nothing worth splitting. Otherwise: the source units, what we
   * have for each, and the indices still missing.
   */
  const planIncremental = useCallback(
    (value: string, srcLang: string, dstLang: string, modeId: string) => {
      const mode = modes.find((m) => m.id === modeId);
      if (!mode?.incremental) return null;

      const shape = shapeOf(value);
      const units = sourceUnits(shape);
      // One unit is the whole text; there is nothing to save and the marked-line
      // prompt would only add a rule for the model to trip over.
      if (units.length < 2) return null;

      // Keyed by level as well (the first level keeps the plain key): a sentence
      // bought at one level is never spliced into another level's answer.
      const held = lookupSentences(srcLang, dstLang, modeId + levelTag, units.map((u) => u.text));
      const missing = held.map((h, i) => (h === null ? i : -1)).filter((i) => i >= 0);
      if (!missing.length) return { shape, units, held, missing };

      // Nothing held at all — the first translation of a new document. Ask for
      // it whole: a "partial" request for every line is the same generation with
      // an extra rule in front of it, and it would file the answer under a
      // different server cache key than the plain one everyone else shares.
      if (missing.length === units.length) return null;

      return { shape, units, held, missing };
    },
    [modes, levelTag],
  );

  /**
   * File the sentence pairs a finished answer contains.
   *
   * This is where the memory is FED, and it has to happen on the whole-text
   * path as much as on the partial one — the first translation of a document is
   * always a whole one, so a version of this that only filed partial answers
   * would have an empty memory forever and never take the cheap path at all.
   *
   * The pairs come from `reshape`'s links, which means they are the same
   * matching the two panes already draw for the reader: sentence for sentence,
   * and only when the answer genuinely corresponded to the source. Nothing is
   * inferred here that is not already on screen.
   */
  const filePairs = useCallback(
    (value: string, answer: string, srcLang: string, dstLang: string, modeId: string, model?: string) => {
      if (!modes.find((m) => m.id === modeId)?.incremental) return;
      const shape = shapeOf(value);
      // The same peeling the pane does, so what is measured is the answer and
      // not the notes or the summaries hanging off the end of it.
      const body = splitNotes(splitOverview(splitSourceOverview(answer).body).body).body;
      const laid = reshape(splitOutput(body).main, shape);
      if (!laid.links) return;
      /*
       * ⚠️ ONLY THE ONE-FOR-ONE LINKS, which is what `exact` means.
       *
       * The pane will happily mark a piece of the answer that covers two
       * source sentences, or one that the aligner could only place
       * approximately — a coarse mark is a true statement about where a
       * reader is. The memory cannot take either: it is keyed on ONE source
       * sentence, and filing a pair whose answer half is really the
       * translation of that sentence AND THE NEXT ONE would hand back half a
       * paragraph the next time that sentence appeared on its own.
       */
      const exact = laid.links.filter((l) => l.exact);
      if (!exact.length) return;
      rememberSentences(
        srcLang, dstLang, modeId + levelTag,
        exact.map((l) => ({
          src: value.slice(l.src.start, l.src.end),
          out: laid.text.slice(l.out.start, l.out.end),
        })),
        model,
      );
    },
    [modes, levelTag],
  );

  const run = useCallback(
    (
      value: string, srcLang: string, dstLang: string, modeId: string,
      /**
       * Ask for the whole text, whatever is already held.
       *
       * Set by the one caller that has learned something: a partial answer came
       * back with the wrong number of lines, so this mode or this text is not
       * behaving, and the correct answer is worth more than the saving.
       */
      whole = false,
    ) => {
      inflight.current?.abort();
      inflightKey.current = null;

      if (!value.trim()) {
        setOutput('');
        setBusy(false);
        setError(null);
        setStat(null);
        return;
      }

      /*
       * AI Prompt answers ONE TAB at a time.
       *
       * So the one thing this path must not do for that mode is send the
       * request. Everything it would have asked for — five finished prompts in
       * a single call, four of them never read — is now `loadStyle()`'s job,
       * one style per call: the open tab when the scheduler decides the
       * question has settled, any other on the click that opens it.
       *
       * The state is still cleared, because every other trigger into `run`
       * (the typing hold, Ctrl+Enter, a language change) means the question has
       * changed, and the pane must not keep showing an answer to the last one.
       * The tabs themselves stay on screen: they come from the style list, not
       * from the answer.
       */
      if (modeId === 'ai-prompt') {
        setOutput('');
        setBusy(false);
        setError(null);
        setLimited(null);
        setStat(null);
        return;
      }

      /**
       * How much of this is already in hand, sentence by sentence.
       *
       * Computed before anything is sent, because it decides all three of what
       * goes on the wire, what comes back, and what is on screen while it does.
       */
      // `let`, because one thing below can still take it away: a context the
      // server would refuse. See the note where that happens.
      let plan = whole ? null : planIncremental(value, srcLang, dstLang, modeId);
      // Above the first level there are no partial requests: a plan survives
      // only when it needs nothing from the server (every sentence in hand).
      if (manual && plan && plan.missing.length) plan = null;

      // Every sentence already translated: there is nothing to ask. This is the
      // case that fires when a paragraph is re-ordered, when a sentence is
      // deleted, or when someone types back to text they had a moment ago.
      if (plan && !plan.missing.length) {
        const whole = assemble(plan.shape, plan.units, plan.held);
        setOutput(whole);
        setBusy(false);
        setError(null);
        setLimited(null);
        setStat({ ms: 0, cached: true });
        remember(memoKey(value, srcLang, dstLang, modeId), whole);
        setShownKey(memoKey(value, srcLang, dstLang, modeId));
        return;
      }

      const controller = new AbortController();
      inflight.current = controller;
      const key = memoKey(value, srcLang, dstLang, modeId);
      inflightKey.current = key;
      /**
       * The words, without the layout — the same string the key is built from.
       *
       * Sending this rather than the raw box is what makes the saving real on
       * the SERVER as well as here: its cache trims the ends and nothing else,
       * so a blank line in the middle used to miss there too, and the request
       * that got past this component still paid a model call. Now both sides
       * hash the same question.
       *
       * It also stops the model being asked to reproduce a layout it keeps
       * getting wrong. The blank lines come back on at render time from the
       * source itself, which cannot be wrong about them — see `reshape`.
       */
      /**
       * A partial request is two pieces, and which text goes in which is the
       * whole reason it works.
       *
       *   `asked`   — the lines that still need translating, and nothing else.
       *               This lands where the mode's own "Translate this:" points,
       *               so the model does its ordinary job on a short input.
       *   `context` — the passage they came from, with a mark on those lines,
       *               handed over separately as reference.
       *
       * The obvious arrangement — send everything, ask for the marked lines —
       * was tried first and does not work: it asks a translation model to look
       * at a sentence and not translate it. See `contextPreamble` in the
       * backend's lib/modes.ts for what was measured.
       */
      // Bound to a const so the closures below narrow: `plan` itself is a `let`
      // from here on, and may yet be dropped.
      const proposed = plan;
      const marked = proposed
        ? proposed.shape.segments
            .map((_seg, line) =>
              proposed.units
                .map((u, i) => ({ u, i }))
                .filter(({ u }) => u.line === line)
                .map(({ u, i }) => (proposed.missing.includes(i) ? `${ONLY_MARK} ${u.text}` : u.text))
                .join('\n'))
            .join('\n')
        : '';

      /**
       * The context is LONGER than the text it describes, and the server checks
       * it against the same ceiling.
       *
       * Every marked line carries a `► ` and every sentence is re-joined onto a
       * line of its own, so a page of 4,879 characters became a context of
       * 5,025 — and the server, correctly, refused it: `context is too long
       * (5025 chars)`. On screen that was a red error on a page that looked well
       * inside the limit the counter had just shown, and no amount of clicking
       * cleared it, because every retry rebuilt the same over-long context.
       *
       * Pagination is what made it reachable. Before it, text near the ceiling
       * was refused by the box; now a full page is the ordinary case, and the
       * pages most likely to have held sentences behind them — the ones being
       * re-read — are exactly the pages nearest the limit.
       *
       * The fallback is the whole-text request, which is always accepted because
       * the page itself is what the ceiling is measured against. It costs the
       * saving on this one request and nothing else: the sentences stay in
       * memory, and the next edit that shortens the page takes the cheap path
       * again.
       */
      if (plan && marked.length > pageSizeRef.current) plan = null;

      const asked = plan
        ? plan.missing.map((i) => plan.units[i].text).join('\n')
        : canonical(value);
      const context = plan ? marked : '';
      /**
       * What the server said the source actually was.
       *
       * Captured so the finished answer can be filed under the RESOLVED language
       * as well as under `auto`. Two entries for one answer, and the second one
       * is what makes picking that language by hand — the commonest thing anyone
       * does after reading "English - Detected" — free instead of a second
       * request for a translation already on screen.
       *
       * It is also the honest key. `auto` is not a language; it is a question,
       * and the answer under it belongs to whatever the question resolved to.
       */
      let resolved: string | null = null;
      setBusy(true);
      setError(null);
      setLimited(null);
      setStat(null);

      // Cleared on the first chunk rather than here, so the previous translation
      // stays on screen while the new one is on its way instead of the pane
      // flashing empty on every keystroke.
      //
      // One exception: a finished `NO_MISTAKES`. Leaving that up means the
      // answer to the PREVIOUS question — a thumbs-up saying the English is
      // fine — sits there for the whole round trip and is then replaced by the
      // Humanize rewrite or the actual list of mistakes. That reads as the
      // product changing its mind. A "Translating…" pane says the true thing.
      setOutput((prev) => (isNoMistakes(splitOverview(prev).body) ? '' : prev));

      let first = true;
      /**
       * The raw stream, kept for a partial answer.
       *
       * A partial answer is not the thing that goes on screen — it is a handful
       * of lines that have to be spliced into the sentences already held — so
       * the accumulated text lives here rather than in `output`, and `output` is
       * set to the assembled whole on every chunk.
       */
      let raw = '';

      /**
       * Splice what has arrived into what we hold, and put the tagged sections
       * back on the end.
       *
       * The result is byte-identical in SHAPE to what a whole-text answer looks
       * like — body, then `[[NOTES]]`, then `[[OVERVIEW]]`, then
       * `[[OVERVIEW_SRC]]` — so everything downstream (the splitters, `reshape`,
       * Copy, Swap, the sentence link) works on an assembled answer without
       * knowing one was assembled.
       */
      const spliced = (): { text: string; lines: string[] } => {
        const a = splitSourceOverview(raw);
        const b = splitOverview(a.body);
        const c = splitNotes(b.body);
        const lines = c.body.split('\n').map((l) => l.trim()).filter((l) => l !== '');

        const parts = [...plan!.held];
        plan!.missing.forEach((idx, k) => {
          if (lines[k] !== undefined) parts[idx] = lines[k];
        });

        let out = assemble(plan!.shape, plan!.units, parts);
        if (c.notes) out += `\n\n${NOTES_TAG}\n${c.notes}`;
        if (b.overview) out += `\n\n${OVERVIEW_TAG} ${b.overview}`;
        if (a.sourceOverview) out += `\n${SOURCE_OVERVIEW_TAG} ${a.sourceOverview}`;
        return { text: out, lines };
      };

      // `overview: true`, always, whatever the switch says.
      //
      // The switch is a SHOW/HIDE, not a setting that changes the question. When
      // it decided the request, flipping it threw away a perfectly good answer
      // and paid for another one — a visible second or two of "Translating…"
      // for a sentence that was already written, on a control that looks like it
      // reveals something already there. So the summary now rides along with
      // every answer and the button only decides whether it is drawn.
      //
      // It costs one short sentence of output per translation. The alternative
      // costs a whole round trip every time somebody presses the button.
      void translateStream(
        {
          text: asked, from: srcLang, to: dstLang, mode: modeId, overview: true,
          partial: !!plan, context, level: sendLevel, signal: controller.signal,
        },
        {
          onMeta: (m) => {
            if (m.detected && m.fromName) { resolved = m.from; setDetected({ code: m.from, name: m.fromName }); }
          },
          onDetected: (d) => { resolved = d.from; setDetected({ code: d.from, name: d.fromName }); },
          onDelta: (chunk) => {
            if (plan) {
              // The sentences already held are on screen from the first frame
              // and never leave it. Only the missing one grows, in its own
              // place, which is the whole visible point of this path: a pane
              // that stays still while you read it.
              raw += chunk;
              setOutput(spliced().text);
              return;
            }
            if (first) {
              setOutput('');
              first = false;
            }
            setOutput((prev) => prev + chunk);
          },
          onDone: (d) => {
            let answer = d.text;

            if (plan) {
              raw = d.text;
              const { text, lines } = spliced();

              // The one check that makes the whole scheme safe to run.
              //
              // The model was asked for exactly as many lines as were marked.
              // If it wrote a different number, then whatever it wrote is not a
              // set of replacements for the missing sentences, and splicing it
              // would put a translation under the wrong sentence — silently,
              // and looking perfectly plausible. So a mismatch throws the
              // partial answer away and asks for the whole text instead.
              if (lines.length !== plan.missing.length) {
                if (inflightKey.current === key) inflightKey.current = null;
                run(value, srcLang, dstLang, modeId, true);
                return;
              }

              answer = text;
              // File each new sentence against its source, so the NEXT keystroke
              // finds it here instead of on the wire.
              rememberSentences(
                srcLang, dstLang, modeId + levelTag,
                plan.missing.map((idx, k) => ({ src: plan.units[idx].text, out: lines[k] })),
                d.model,
              );
            } else {
              // A whole answer, taken apart into the sentences it is made of.
              // This is what makes the NEXT keystroke cheap.
              filePairs(value, answer, srcLang, dstLang, modeId, d.model);
            }

            setOutput(answer);
            // The price only under the engine's level pricing (`level` comes with it);
            // with it off the row reads exactly as it always did.
            setStat({ ms: d.ms, cached: d.cached, kharma: d.level ? d.kharma : undefined, own: isOwnModel(d.model) });
            setBusy(false);
            setJustTranslated(true);
            setShownKey(key);
            if (inflightKey.current === key) inflightKey.current = null;
            remember(key, answer, d.model);
            // The same answer, also filed under the language it turned out to
            // be in. Only when the picker said `auto` — otherwise this IS that
            // key and writing it twice would just evict something else.
            if (srcLang === 'auto' && resolved && resolved !== 'auto') {
              remember(memoKey(value, resolved, dstLang, modeId), answer);
            }
          },
          onError: (message, status) => {
            /**
             * A 400 on a PARTIAL request is a statement about the shape of the
             * request, not about the text — the server refused the marked-line
             * form, and the whole-text form of the same question is always one
             * it accepts. So it is asked again rather than shown as a failure.
             *
             * This is the belt to the braces of the length check above. That
             * check compares the context against this client's copy of the
             * server's ceiling, and the two can disagree: a tab left open while
             * an operator lowers the limit in the console holds the old number,
             * and every partial request it sends from then on is refused. The
             * user of that tab has done nothing wrong and has nothing to fix —
             * on the pages of a long document it read as "the first page
             * translates and the rest show a red error", with clicking again
             * rebuilding the identical over-long context every time.
             *
             * Bounded by `whole`: the retry carries no context at all, so it
             * cannot come back here as a partial and ask a third time.
             */
            if (plan && !whole && status === 400) {
              if (inflightKey.current === key) inflightKey.current = null;
              run(value, srcLang, dstLang, modeId, true);
              return;
            }
            setError(message);
            setBusy(false);
            if (inflightKey.current === key) inflightKey.current = null;
          },
          onLimit: (message) => {
            // Not an error the user can fix by retrying — say what happened and
            // when it clears, and leave the previous translation on screen.
            setLimited(message);
            setBusy(false);
            if (inflightKey.current === key) inflightKey.current = null;
          },
        },
      );
    },
    [remember, memoKey, planIncremental, filePairs, manual, levelTag, sendLevel],
  );

  /**
   * "You left your keyboard in English."
   *
   * `wndrnr` is not a word — it is `중국` typed with the IME still in English.
   * See lib/hangul.ts for the conversion, for the segment scoping that lets this
   * fire once per word rather than once per box, and for the three gates that
   * stop it going off on real English.
   *
   * Gated on the language the app believes the box is in — the picker when it
   * names one, and otherwise whatever the tab above the box is currently
   * claiming to have detected. Not on the picker alone, which is what it used to
   * be: that only knew about somebody who had said "this is English" by hand,
   * and had nothing to say about the far commoner case where the app worked it
   * out itself, wrote "English - Detected" on the tab, and then offered to turn
   * a word of that English into Korean anyway.
   *
   * The rule lives in lib/hangul as `engineApplies` rather than here, because
   * the next keyboard engine added for another language needs the same one and
   * two copies of it would eventually disagree.
   */
  const believedLang = from !== 'auto' ? from : detected?.code ?? null;
  const korean = useMemo(
    () => hangulSuggestion(text, caret, believedLang),
    [text, caret, believedLang],
  );

  /* ── The Korean keyboard ──────────────────────────────────────────────────*/

  /**
   * Offered only when the reader has SAID the input is Korean.
   *
   * `from` is the picker's own value, so this is a manual choice and never a
   * detection: while the source is "Detect language" it holds `'auto'` no
   * matter what the detector believes. That is the whole distinction the owner
   * asked for. Someone who has named the language is telling us what they are
   * about to type, and a keyboard is a reasonable thing to hand them; someone
   * who has not is still being read, and remapping their keys underneath that
   * would be a guess with a very high price.
   *
   * Naming the language also ends detection for that text on its own — the
   * request carries `from` and the detector is never asked — so nothing extra
   * is needed here to switch it off.
   */
  /**
   * The keyboard on offer, or null — see lib/keyboards.ts.
   *
   * This used to be `from === 'ko'`. Nothing about the problem was Korean: a
   * visitor who names Greek, Thai, Hindi or Russian is in exactly the same
   * position, having told us what they are about to write on a machine that
   * cannot write it. What varies between them is only what a keypress MEANS,
   * and that is the `engine` on the spec.
   */
  const keyboard = useMemo(() => keyboardFor(from), [from]);
  const keyboardOffered = !!keyboard;
  const keyboardLayout = useMemo(() => (keyboard ? layoutFor(keyboard) : null), [keyboard]);
  /** The language's English name, for the chip and the panel's heading. */
  const keyboardName = useMemo(
    () => languages.find((l) => l.code === from)?.name ?? from,
    [languages, from],
  );
  const [imeOn, setImeOn] = useState(false);

  /**
   * On by default, off by memory.
   *
   * Someone who picks Korean means to type Korean, and making them find a
   * second switch before a single letter works would waste the choice they
   * already made. But it is a real change to what their keys do, so turning it
   * off is remembered: the reader with a Korean IME of their own says no once,
   * not on every visit.
   *
   * Safe alongside a real IME either way — an OS IME sends composed Hangul and
   * never the Latin keystrokes this reads, so the two cannot both fire.
   */
  /*
   * ONE PREFERENCE PER LANGUAGE.
   *
   * Turning the Greek keyboard off says nothing about whether somebody wants
   * the Russian one, and a single shared switch would have made every new
   * language inherit a decision taken about a different alphabet. The Korean
   * key is read as a fallback for `ko` so that nobody who has already turned
   * that one off finds it back on.
   */
  const keyboardPref = `gt.keyboard.${from}.v1`;
  useEffect(() => {
    if (!keyboardOffered) { setImeOn(false); return; }
    let pref: string | null = null;
    try {
      pref = localStorage.getItem(keyboardPref);
      if (pref === null && from === 'ko') pref = localStorage.getItem('gt.korean-keyboard.v2');
      /*
       * v1 is discarded rather than migrated, and that is the whole point of
       * the version bump.
       *
       * The panel's corner button used to be a ✕ that switched the IME off,
       * and it wrote '0' here. But nobody pressing it meant "I do not want a
       * Korean keyboard" — they meant "put this layout away". The value
       * recorded an intention its owner never had, and honouring it left those
       * browsers with the keyboard permanently off and no clue why: the
       * language would flip to Korean on the first Hangul character and
       * nothing else would happen.
       *
       * So the old key is not read, and it is removed on sight so it cannot
       * confuse anything later. Everyone who pressed that button gets the
       * keyboard back; anyone who genuinely wants it off says so again through
       * the chip, which is now the only control that means that.
       */
      localStorage.removeItem('gt.korean-keyboard.v1');
    } catch { /* private mode */ }
    setImeOn(pref !== '0');
  }, [keyboardOffered, keyboardPref, from]);

  /**
   * Whether the LAYOUT is drawn — which is not the same question as whether the
   * keyboard is on, and conflating the two was a real bug.
   *
   * The panel's corner button used to switch the whole IME off. Putting the
   * diagram away therefore handed the reader a Latin keyboard without saying
   * so, and the next word they typed came out as `dkssud`. It is a minimise
   * now: `imeOn` above decides what the keys DO, this decides only whether the
   * picture is on screen, and the two no longer touch.
   *
   * Remembered separately for the same reason. Somebody who knows the layout
   * wants it folded away on every visit while still typing Korean, and that
   * preference must not be filed as "I do not want the keyboard".
   */
  /*
   * The panel's own open/closed state is NOT per language, and that asymmetry
   * is deliberate. "I know this layout, fold it away" is a habit about the
   * diagram rather than about Greek: somebody who has put one away has told us
   * how they like this panel, not which alphabets they can touch-type.
   */
  const PANEL_PREF = 'gt.keyboard.panel.v1';
  const [panelOpen, setPanelOpen] = useState(true);
  useEffect(() => {
    if (!keyboardOffered) return;
    let pref: string | null = null;
    try {
      pref = localStorage.getItem(PANEL_PREF);
      if (pref === null) pref = localStorage.getItem('gt.korean-keyboard.panel.v1');
    } catch { /* private mode */ }
    setPanelOpen(pref !== '0');
  }, [keyboardOffered]);

  const setPanel = useCallback((open: boolean) => {
    setPanelOpen(open);
    try { localStorage.setItem(PANEL_PREF, open ? '1' : '0'); } catch { /* private mode */ }
  }, []);

  /**
   * The key just pressed, so the panel can light it up.
   *
   * A counter rather than the key alone: pressing `r` twice is two presses and
   * has to flash twice, and a state that only held `'r'` would not change on
   * the second one. The panel keys off the number, so a repeat is still an
   * event.
   */
  const [pressedKey, setPressedKey] = useState<{ key: string; n: number } | null>(null);
  const pressSeq = useRef(0);
  const flashKey = useCallback((key: string) => {
    pressSeq.current += 1;
    setPressedKey({ key, n: pressSeq.current });
  }, []);

  const setKeyboard = useCallback((on: boolean) => {
    setImeOn(on);
    try { localStorage.setItem(keyboardPref, on ? '1' : '0'); } catch { /* private mode */ }
  }, [keyboardPref]);

  /**
   * Apply one IME edit at the caret: drop `back` characters, then type `insert`.
   *
   * Goes through `editPage` and then puts the caret back by hand, exactly as
   * `commit` does above and for the same reason — the textarea is controlled,
   * so React repaints it with the caret at the end unless something moves it.
   * Typing that way would send the reader to the end of the box on every
   * keystroke.
   */
  const applyImeEdit = useCallback((back: number, insert: string) => {
    const el = inputRef.current;
    /*
      The box itself, not `text`: the state can trail the DOM by a keystroke
      when keys come faster than renders, and an edit computed against the
      older string dropped or misplaced syllables.
    */
    const cur = el ? el.value : text;
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? start;
    const head = cur.slice(0, Math.max(0, start - back)) + insert;
    const next = head + cur.slice(end);
    const at = head.length;
    // Same re-anchor the onChange handler does: typing while the microphone is
    // open moves where the next spoken words belong.
    if (dictation.current) dictation.current = { base: next, at };
    /*
      Painted NOW, and the caret put back in the same tick. It used to wait for
      the next animation frame, and a key pressed inside that frame (ordinary
      Korean typing speed) read the caret React had parked at the END of the
      repainted box — so the syllable landed at the end and the cursor jumped
      there. Owner, 2026-10-21: "why is it moving the cursor to the back".
    */
    flushSync(() => editPage(next, at));
    if (el) {
      el.focus();
      const pos = Math.min(at, el.value.length);
      el.setSelectionRange(pos, pos);
    }
  }, [text, editPage]);

  /**
   * One jamo, landing on whatever is in front of the caret.
   *
   * With a selection open there is nothing to compose ONTO — the highlighted
   * text is about to be replaced — so the jamo is inserted whole rather than
   * merged into the character before it.
   */
  const typeHangul = useCallback((jamo: string) => {
    const el = inputRef.current;
    const cur = el ? el.value : text;
    const start = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? start;
    const edit = start === end ? typeJamo(cur.slice(0, start), jamo) : { back: 0, insert: jamo };
    applyImeEdit(edit.back, edit.insert);
  }, [text, applyImeEdit]);

  /**
   * The panel's own space and backspace, for a reader using it with a mouse.
   *
   * Backspace deletes a CHARACTER, not a jamo. Taking `한` apart into `하` is
   * what a desktop IME does to the syllable it is still composing, but this
   * keyboard has no composing buffer — it commits every keystroke — so it
   * cannot tell a syllable typed a second ago from one that was pasted an hour
   * ago. Deleting whole characters is the behaviour that is right either way.
   */
  const spaceHangul = useCallback(() => applyImeEdit(0, ' '), [applyImeEdit]);
  const backspaceHangul = useCallback(() => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    if (start === end && start === 0) return;
    // No `deleting` flag, for the same reason the Backspace KEY no longer sets
    // one (see the textarea's onKeyDown): this is the panel's backspace, it is
    // pressed mid-sentence, and it must not drop the reader out of the hold.
    applyImeEdit(start === end ? 1 : 0, '');
  }, [text, applyImeEdit]);

  /* ── The other three engines ─────────────────────────────────────────────
   *
   * `typeHangul` above is one of four, and the three below are the ones that
   * needed state of their own. What they have in common is that the thing
   * being composed LIVES IN THE TEXTAREA rather than in a floating pre-edit
   * box: the imePending romaji or pinyin is real text the whole time, so a reader
   * who navigates away, switches mode or closes the panel is left with what
   * they typed instead of with nothing.
   */

  /** A dead key's mark, waiting for the letter it belongs on. `direct` only. */
  const [armedDead, setArmedDead] = useState<string | null>(null);

  /**
   * Latin that has not become anything yet — `romaji` and `pinyin`.
   *
   * It is also what is on screen, character for character, which is what makes
   * every edit below a `back: imePending.length` replacement.
   */
  const [imePending, setImePending] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [dictError, setDictError] = useState<string | null>(null);
  /** Bumped when the pinyin dictionary lands, to re-run the lookup below. */
  const [dictAt, setDictAt] = useState(0);

  /*
   * A LANGUAGE OR A SWITCH CHANGING ABANDONS THE COMPOSITION.
   *
   * Not "finishes" it. The imePending text is already in the box as Latin, so
   * there is nothing to lose by forgetting it — and something real to lose by
   * guessing: converting a half-typed `zhongg` into a character nobody chose,
   * at the moment somebody switched language, would be the IME making the one
   * decision it must never make on its own.
   */
  useEffect(() => { setImePending(''); setArmedDead(null); }, [from, imeOn]);

  /**
   * Fetch the pinyin dictionary, but only for somebody who is going to use it.
   *
   * 420 KB, and worth being careful about when to spend it: not on page load,
   * not when Chinese is merely the TARGET, and not for a reader who has turned
   * the keyboard off. Naming Chinese as your input language and leaving the
   * keyboard on is as close to a declaration of intent as this can get.
   */
  useEffect(() => {
    if (keyboard?.engine !== 'pinyin' || !imeOn) return;
    let alive = true;
    setDictError(null);
    loadPinyin()
      .then(() => { if (alive) setDictAt((n) => n + 1); })
      .catch(() => {
        if (alive) setDictError('The Chinese dictionary could not be loaded. Try again in a moment.');
      });
    return () => { alive = false; };
  }, [keyboard, imeOn]);

  useEffect(() => {
    if (keyboard?.engine !== 'pinyin' || !imePending) { setCandidates([]); return; }
    setCandidates(candidatesFor(imePending));
    // `dictAt` is in here on purpose and is not used in the body: it is the
    // signal that the dictionary arrived, and without it the candidates for
    // whatever was typed WHILE it was downloading stay empty for ever.
  }, [keyboard, imePending, dictAt]);

  /** Take a candidate, leaving any pinyin it did not spell still imePending. */
  const pickCandidate = useCallback((c: Candidate) => {
    const r = choosePinyin(imePending, c);
    applyImeEdit(r.edit.back, r.edit.insert);
    setImePending(r.pending);
  }, [imePending, applyImeEdit]);

  /**
   * One character off a cap, whichever panel it came from.
   *
   * The romaji chart is the interesting case: a TAP is a choice of kana, so it
   * goes in whole rather than being replayed through the spelling rules. Sent
   * back through them, a tapped な would turn into ん the moment the next key
   * was a consonant — a rule about typing `na`, not about pressing な.
   */
  const typePanelChar = useCallback((char: string, dead: boolean) => {
    if (!keyboard) return;
    if (keyboard.engine === 'hangul') { typeHangul(char); return; }
    if (keyboard.engine === 'direct') {
      const press = typeDirect(char, dead, armedDead);
      setArmedDead(press.dead);
      if (press.edit) applyImeEdit(press.edit.back, press.edit.insert);
      return;
    }
    // romaji and pinyin: whatever is imePending is finished off first, because
    // the tap is a deliberate act and must not be merged into it.
    const done = keyboard.engine === 'romaji' ? flushRomaji(imePending) : imePending;
    applyImeEdit(imePending.length, done + char);
    setImePending('');
  }, [keyboard, armedDead, imePending, typeHangul, applyImeEdit]);

  /**
   * Every keystroke the keyboard wants, before anything else can claim it.
   *
   * Returns true when it took the key. The modifier check is what keeps every
   * shortcut alive: Ctrl+A, ⌘+C and Ctrl+Enter must not be eaten. `isComposing`
   * is the other half — a reader with a real OS IME is mid-syllable in THEIR
   * engine, and stepping on that would corrupt a composition they can see.
   */
  const imeKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!keyboard || !imeOn) return false;
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    if ((e.nativeEvent as unknown as { isComposing?: boolean }).isComposing) return false;

    /*
     * Space and backspace are usually NOT intercepted — they already do the
     * right thing — but they are on the panel, so they light up with
     * everything else. A keyboard where two keys stayed dark would look broken
     * rather than deliberate.
     */
    const lightOnly = () => {
      if (e.key === ' ') flashKey('space');
      else if (e.key === 'Backspace') flashKey('backspace');
      return false;
    };

    if (keyboard.engine === 'hangul') {
      const jamo = jamoForKey(e.key);
      if (!jamo) return lightOnly();
      e.preventDefault();
      typeHangul(jamo);
      flashKey(e.key.toLowerCase());
      return true;
    }

    if (keyboard.engine === 'direct') {
      const key = physicalKey(e.key);
      const cap = key && keyboardLayout ? capFor(keyboardLayout, key) : null;
      const out = cap ? outputOf(cap, e.shiftKey) : null;
      if (!out) return lightOnly();
      e.preventDefault();
      const press = typeDirect(out.char, out.dead, armedDead);
      setArmedDead(press.dead);
      if (press.edit) applyImeEdit(press.edit.back, press.edit.insert);
      flashKey(key as string);
      return true;
    }

    const letter = e.key.length === 1 && /[a-z]/i.test(e.key);

    if (keyboard.engine === 'romaji') {
      if (letter) {
        e.preventDefault();
        const r = typeRomaji(imePending, e.key);
        applyImeEdit(r.edit.back, r.edit.insert);
        setImePending(r.pending);
        flashKey(e.key.toLowerCase());
        return true;
      }
      if (!imePending) return lightOnly();
      if (e.key === 'Backspace') {
        e.preventDefault();
        applyImeEdit(1, '');
        setImePending(imePending.slice(0, -1));
        return true;
      }
      if (e.key === 'Escape') { e.preventDefault(); setImePending(''); return true; }
      // A lone `n` is ん — the one case where giving up on the buffer has a
      // right answer. Everything else stays the Latin it already is, and the
      // key that ended it carries on to do its own job.
      const done = flushRomaji(imePending);
      if (done !== imePending) applyImeEdit(imePending.length, done);
      setImePending('');
      return lightOnly();
    }

    if (keyboard.engine === 'pinyin') {
      if (letter) {
        e.preventDefault();
        const r = typePinyin(imePending, e.key);
        applyImeEdit(r.edit.back, r.edit.insert);
        setImePending(r.pending);
        flashKey(e.key.toLowerCase());
        return true;
      }
      if (!imePending) return lightOnly();
      if (/^[1-9]$/.test(e.key) && candidates[Number(e.key) - 1]) {
        e.preventDefault();
        pickCandidate(candidates[Number(e.key) - 1]);
        return true;
      }
      if (e.key === ' ' && candidates[0]) {
        e.preventDefault();
        pickCandidate(candidates[0]);
        flashKey('space');
        return true;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        applyImeEdit(1, '');
        setImePending(imePending.slice(0, -1));
        return true;
      }
      if (e.key === 'Escape') {
        // Give up on the conversion and keep the Latin. Deliberately NOT "take
        // the first candidate": a reader who pressed Escape said no, and
        // answering that by choosing for them is the one thing an IME must
        // never do.
        e.preventDefault();
        setImePending('');
        return true;
      }
      setImePending('');
      return lightOnly();
    }

    return false;
  }, [
    keyboard, imeOn, keyboardLayout, armedDead, imePending, candidates,
    typeHangul, applyImeEdit, pickCandidate, flashKey,
  ]);

  /**
   * The panel's space bar and backspace.
   *
   * Space picks the first candidate when there is one, exactly as the real
   * space bar does — a panel whose space bar did something different from the
   * key it is a picture of would be teaching the wrong lesson.
   */
  const panelSpace = useCallback(() => {
    if (keyboard?.engine === 'pinyin' && candidates[0]) { pickCandidate(candidates[0]); return; }
    if (keyboard?.engine === 'romaji' && imePending) {
      applyImeEdit(imePending.length, flushRomaji(imePending) + ' ');
      setImePending('');
      return;
    }
    spaceHangul();
  }, [keyboard, candidates, imePending, pickCandidate, applyImeEdit, spaceHangul]);

  /**
   * A key that types the same on every keyboard (digits, punctuation, Enter),
   * tapped on the panel. It goes straight in as text, never through the
   * language's engine: Hangul would try to compose a "1" into the syllable
   * before it. Pending romaji or pinyin is finished first, as a space does.
   */
  const panelText = useCallback((t: string) => {
    if (armedDead) setArmedDead(null);
    if (imePending) {
      const done = keyboard?.engine === 'romaji' ? flushRomaji(imePending) : imePending;
      applyImeEdit(imePending.length, done + t);
      setImePending('');
      return;
    }
    applyImeEdit(0, t);
  }, [keyboard, armedDead, imePending, applyImeEdit]);

  const panelBackspace = useCallback(() => {
    if (imePending) { applyImeEdit(1, ''); setImePending(imePending.slice(0, -1)); return; }
    backspaceHangul();
  }, [imePending, applyImeEdit, backspaceHangul]);

  /**
   * The caret moved by itself, so whatever was imePending is no longer in front
   * of it.
   *
   * Every edit in here is expressed as "delete the imePending Latin behind the
   * caret and put this instead". Click into the middle of the paragraph with
   * `zhongg` still imePending and that deletes six characters of somebody's
   * sentence. Forgetting the composition is the cheap, correct answer: the
   * Latin stays where it was typed.
   */
  const dropPending = useCallback(() => {
    if (imePending) setImePending('');
    if (armedDead) setArmedDead(null);
  }, [imePending, armedDead]);

  /**
   * The offer as far as the interface is concerned — nothing turned down.
   *
   * Silent while the keyboard is on, and that is the point of the pairing. The
   * card exists to catch Korean typed with an English IME; with this keyboard
   * running there is no such thing, because the keys already produce Hangul.
   * Leaving it on would mean offering to convert `안녕` into itself.
   */
  const candidate = korean && korean.source !== dismissed && !imeOn ? korean : null;

  /**
   * The word the user is CHECKING, as opposed to the text they are translating.
   *
   * The pronunciation card and the dictionary entry are one state seen from
   * both panes — somebody has typed a word and is looking it up — and this is
   * that state. Either both panes are right about it or the page is telling the
   * user two different things about what they are doing.
   *
   * What changed here is WHEN it can be known. It used to be defined as "the
   * pronunciation has come back", which made it a fact that took a round trip
   * to establish — and the entry, waiting on it, paid that round trip before it
   * could even start asking. Measured, that was 0.4s to 2.3s of doing nothing.
   *
   * It is now the local test plus `!candidate`, both of which are free, so both
   * features start together on the same keystroke. The pronunciation is still
   * what settles the one question the local test cannot answer — mash or word —
   * but it settles it by RETRACTION (see `noSound`) rather than by being waited
   * for. Nothing is drawn for a word that turns out not to be one; it is simply
   * not drawn a second late for every word that is.
   *
   * `!candidate` stays part of it: while the keyboard-layout offer is up the box
   * does not hold the word the user meant yet, and neither card should be
   * spending a request on a string they are about to replace.
   *
   * `!imePending` is the same rule for the Japanese and Chinese keyboards,
   * which keep their half-typed Latin in this box. Without it, spelling
   * `zhong` on the way to 中国 bought the phonetics of `zhong` — a card
   * about a syllable nobody has finished, over text that is about to be
   * replaced.
   */
  const checkedWord = candidate || imePending ? null : wordForLookup(text);

  /* ── And what does it mean? ─────────────────────────────────────────────── */

  /**
   * The dictionary entry for the word in the box, when it is a word.
   *
   * Held with the word AND the language it was glossed into, for the reason
   * `phonetic` is held with its word: both of those can change while the answer
   * is in flight, and an entry drawn under the wrong headword — or in the wrong
   * language — is not a smaller version of being right. It is unnoticeably
   * wrong, which is the failure mode a reference card can least afford.
   */
  const [entry, setEntry] = useState<{ word: string; to: string; found: DictionaryEntry } | null>(null);
  /**
   * Entries asked for in this session, including the ones with no answer.
   *
   * Same memo as `phonetics`, same reason, and it matters more here because the
   * call is several times the size: backspacing over a word and typing it again
   * is the commonest thing that happens in this box, and it must not cost a
   * second look-up. `null` is stored deliberately — a word with no entry has to
   * be remembered as having none, or it is re-asked every time it is revisited.
   */
  const entries = useRef(new Map<string, DictionaryEntry | null>());

  /**
   * Which language the entry is written in.
   *
   * Normally the target, because that is the language the reader chose to
   * receive answers in. The exception is a mode that answers in the language
   * the user wrote in (A1, Mistakes, Registers): those hide the target pickers
   * entirely, so `to` is whatever was last set and is not what the page is
   * about. Glossing into the input language there gives a monolingual entry —
   * an English word explained in English — which is the right book to hand
   * somebody who is being taught inside one language.
   */
  const entryTo = followsInput ? inputLang?.code ?? null : to;

  useEffect(() => {
    // `checkedWord` is the whole trigger — see its note. Not a word-count of
    // its own: this card and the pronunciation card are two halves of one
    // state, and a second gate would let them disagree.
    const word = checkedWord;
    if (!word || !entryTo) {
      setEntry(null);
      return;
    }

    const lang = inputLang?.code ?? 'auto';
    const key = `${lang}>${entryTo}:${word}`;
    if (entries.current.has(key)) {
      const held = entries.current.get(key) ?? null;
      setEntry(held ? { word, to: entryTo, found: held } : null);
      return;
    }

    // Nothing is cleared here, for the reason the pronunciation effect gives:
    // somebody typing the second half of a word still has the first half's
    // entry under the answer, and blanking it on every keystroke would make the
    // pane flicker rather than change.
    const abort = new AbortController();
    const timer = setTimeout(() => {
      fetchDictionary(word, lang, entryTo, abort.signal)
        .then((found) => {
          entries.current.set(key, found);
          setEntry(found ? { word, to: entryTo, found } : null);
        })
        // Silence is the whole error policy here — see the route. An abort is us
        // superseding it; anything else is a card that does not appear, which is
        // exactly the page the visitor had a moment ago.
        .catch(() => {});
    }, ENTRY_IDLE_MS);

    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [checkedWord, inputLang, entryTo]);

  /**
   * The entry, only while it still describes what is on screen.
   *
   * Both halves of the guard earn their place. The word check is the one
   * `phonetic` makes — the answer lands a moment after it was asked for, and by
   * then the box may hold something else. The language check is this card's
   * own: switching the target tab re-asks for the entry, and until that lands
   * the previous one is still in state, glossed into the language the reader
   * has just navigated away from.
   */
  const shownEntry = useMemo(() => {
    if (!entry || entry.word !== checkedWord || entry.to !== entryTo) return null;
    // The retraction. `checkedWord` is now settled locally and instantly, which
    // is what made the entry fast — but the local tests cannot tell a word from
    // a keyboard mash, and the pronunciation route can. When it comes back and
    // says this is not a word, the entry goes with it.
    //
    // In practice this almost never fires late enough to be seen: the
    // pronunciation is the cheaper call and usually answers first, so the entry
    // is suppressed before it ever had an answer to draw. The case it exists
    // for is the slow one, and a card that appears and then leaves is still
    // better than a confident dictionary entry for `gksrmf`.
    if (noSound === checkedWord) return null;
    // The entry arrives as an object, so it takes the walking version — its
    // glosses, explanation and example translations are five separate fields
    // and any of them can name the country. Same rewrite, same place in the
    // pipeline as the answer's: the last step before it is drawn.
    return preferredTermsDeep(entry.found);
  }, [entry, checkedWord, entryTo, noSound]);

  /**
   * Replace the keystrokes with the Korean they spell.
   *
   * Only the active segment moves; anything already in the box is left exactly
   * as it is, which is what lets this run word after word in one sentence.
   */
  const commit = useCallback(
    (sug: HangulSuggestion, trailing: string, keepFocus = true) => {
      const head = text.slice(0, sug.start) + sug.text + trailing;
      const next = head + text.slice(sug.end);
      undo.current = { after: next, before: text, caret: sug.end, source: sug.source };
      setText(next);
      setCaret(head.length);
      setDismissed(null);
      // Back in the box with the caret after the word, ready to keep typing —
      // the suggestion is a correction, not a destination.
      //
      // Skipped when the key that triggered this is one that moves focus (Tab).
      // Putting the caret back would fight the very thing the key was pressed to
      // do, and the rAF makes it worse: focus would land on the next control and
      // then get yanked back a frame later.
      const el = inputRef.current;
      if (keepFocus && el) {
        el.focus();
        requestAnimationFrame(() => el.setSelectionRange(head.length, head.length));
      }
    },
    [text],
  );

  /**
   * Undo a conversion that happened on the space bar.
   *
   * This is the safety valve that makes committing on space acceptable at all.
   * Every Korean IME does the same thing — backspace immediately after a commit
   * gives you the letters back — and without it a wrong conversion means
   * retyping the word, which is a worse outcome than the feature not existing.
   *
   * The guard is that the box still holds exactly what the commit produced. Type
   * one more character and backspace goes back to meaning backspace.
   */
  const revert = useCallback(() => {
    const u = undo.current;
    if (!u || u.after !== text) return false;
    undo.current = null;
    setText(u.before);
    setCaret(u.caret);
    // Marked as turned down, or the card would reappear over the very letters
    // the user just insisted on keeping.
    setDismissed(u.source);
    const el = inputRef.current;
    if (el) {
      el.focus();
      requestAnimationFrame(() => el.setSelectionRange(u.caret, u.caret));
    }
    return true;
  }, [text]);

  /**
   * Put an answer we are already holding on screen.
   *
   * Returns false when there is none, so every caller reads as "serve it if we
   * have it, otherwise do the expensive thing". Both places that can decide to
   * spend a model call go through here first — the scheduler, and the Ctrl+Enter
   * that deliberately jumps the queue.
   */
  const serveFromMemo = useCallback((key: string) => {
    const cached = memo.current.get(key);
    if (cached === undefined) return false;
    // See `remember`: a blank entry (from an older build, or storage) is a miss.
    if (!cached.trim()) { memo.current.delete(key); return false; }
    inflight.current?.abort();
    inflightKey.current = null;
    setOutput(cached);
    setShownKey(key);
    setBusy(false);
    setError(null);
    setLimited(null);
    setStat({ ms: 0, cached: true });
    return true;
  }, []);

  /**
   * Stop whatever wait is running, silent half or counted half.
   *
   * This is also the effect's own cleanup, so it runs on every keystroke — the
   * next pass through the scheduler sets `waiting` straight back if it is still
   * a typing wait, both inside one commit, so the layer does not blink between
   * characters. Clearing it here rather than only on the branches that finish
   * is what guarantees the layer cannot outlive its wait: every early return in
   * the scheduler (memo hit, empty box, same language, request already on the
   * wire) takes the answer off this cleanup and none of them has to remember to.
   */
  const cancelSchedule = useCallback(() => {
    if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
    if (liveTimer.current) { clearTimeout(liveTimer.current); liveTimer.current = null; }
    if (tick.current) { clearInterval(tick.current); tick.current = null; }
    setCountdown(null);
    setWaiting(false);
  }, []);

  /**
   * When to spend a model call.
   *
   * Four of these paths cost nothing at all, and they are deliberately checked
   * before the one that does:
   *
   *   1. same language in and out — the answer is the input, settled here
   *   2. an empty box — nothing to ask
   *   3. an answer already held from this session, including one whose only
   *      difference is whitespace (see memoKey)
   *   4. the same question already on the wire
   *
   * Then the split that does the actual saving. A DELIBERATE act — Enter, a
   * paste, a changed language or mode — is someone asking for an answer, and
   * gets one immediately. TYPING is not a request; it is someone still deciding
   * what to ask, so it waits for them to finish.
   *
   * The order of 3 and the deliberate-act branch is the whole of what Enter
   * means, and both halves of it are wanted:
   *
   *   • Enter on a thought that has never been translated — `Where do these
   *     numbers come from?` typed and finished with a keystroke — is a request,
   *     and it is answered on the spot rather than after the five-second wait.
   *     That is the branch below.
   *   • Enter that only opens a paragraph in text already answered changes no
   *     word of the question (see memoKey), so it lands on branch 3 and costs
   *     nothing. The answer re-lays itself out around the new blank line at
   *     render time, so it still visibly responds to the keystroke.
   *
   * Which of the two it is falls out of whether the answer is already held. It
   * never has to be guessed at from the keystroke.
   */
  useEffect(() => {
    const previous = prevText.current;
    const changed = text !== previous;
    prevText.current = text;
    const forced = forceNext.current;
    forceNext.current = false;
    const removed = deleting.current;
    deleting.current = false;
    // Read and cleared on the same beat as the other one-shot flags, so it can
    // only ever suppress the single pass the paste produced.
    const suppressDraft = noDraft.current;
    noDraft.current = false;

    /*
     * Text that appeared rather than being typed — see JUMP_CHARS.
     *
     * Dictation is the one thing excluded, and it has to be: speech arrives in
     * phrases, so a partial can add far more than this at once, and every one
     * of them would buy a translation of a sentence the speaker has not
     * finished saying. It already has its own ending (`endDictation`), which
     * asks the moment they stop talking.
     */
    // Answered at once, exactly as a paste with its event is (see onPaste): a
    // drop or a keyboard paste that fired no event is still somebody pasting.
    const jumped =
      changed && !dictation.current && text.length - previous.length >= JUMP_CHARS;

    if (identity) {
      inflight.current?.abort();
      inflightKey.current = null;
      setOutput(text);
      setBusy(false);
      setError(null);
      setLimited(null);
      setStat(text.trim() ? { ms: 0, cached: false, same: true } : null);
      return;
    }

    if (!text.trim()) {
      inflight.current?.abort();
      inflightKey.current = null;
      setOutput('');
      setBusy(false);
      setError(null);
      setStat(null);
      return;
    }

    /*
     * A HALF-TYPED SYLLABLE IS NOT A QUESTION.
     *
     * The Japanese and Chinese keyboards keep their pending Latin in this box,
     * so `zhong` on its way to 中国 is real text as far as everything else
     * here is concerned. Without this the countdown starts on it, the model is
     * asked to translate `zhong`, and the pronunciation card offers the
     * phonetics of a syllable nobody has finished spelling — all of it paid
     * for, and all of it thrown away by the next keystroke.
     *
     * Holding rather than cancelling: `cancelSchedule` leaves whatever was
     * last answered on screen, which is right. The reader is mid-word.
     */
    if (imePending) {
      setWaiting(false);
      return cancelSchedule;
    }

    /*
     * Not before the sign-in and the engine have answered — see `pageReady`. The
     * pass that `pageReady` turning true produces sees an unchanged text, which is
     * the "deliberate act" branch below: a restored text is answered once, at
     * the right level, straight away.
     */
    if (!pageReady) return cancelSchedule;

    /*
     * ABOVE THE FIRST LEVEL: no live draft, no countdown, no partial request,
     * no tab written alongside — see `manual`. What is already held is still
     * put back for nothing (the memo, or every sentence already in hand); what
     * is not waits for Translate.
     */
    if (manual) {
      if (mode === 'native' || mode === 'ai-prompt') return cancelSchedule;
      const held = memoKey(text, from, to, mode);
      if (inflightKey.current === held) return cancelSchedule;
      if (serveFromMemo(held)) return cancelSchedule;
      const inHand = planIncremental(text, from, to, mode);
      if (inHand && !inHand.missing.length) run(text, from, to, mode);
      return cancelSchedule;
    }

    /*
     * AI Prompt: the HIGHLIGHTED tab writes itself. The other four still wait
     * for the click that opens them.
     *
     * This mode used to schedule nothing at all, on the reasoning that a tab is
     * written by the click that opens it. One tab is already open before any
     * click: the reader's remembered choice, restored from the last visit and
     * underlined the moment the mode is entered. So the pane they actually got
     * was a tab highlighted over an empty box telling them to "pick a version
     * above" — the version that was already picked, with nothing on screen to
     * say what else to press.
     *
     * So the trigger is now the same as every other mode's — the hold after the
     * last keystroke, Ctrl+Enter, a paste that carries a separator, or simply
     * arriving here with a prompt already in the box — while the request stays
     * ONE style rather than five: the open one. The four nobody is looking at
     * are exactly as unwritten, and as free, as they were.
     *
     * No live draft, unlike the modes below. `loadStyle` rewrites the whole
     * rough prompt in one call with no incremental machinery behind it, so a
     * draft is not a cheap preview of the final — it is a second full-price
     * answer to a sentence that is still being typed.
     */
    /*
     * Registers, which now buys ONE register at a time — see `loadRegister`.
     *
     * Handled exactly like AI Prompt below and for the same reasons, so the two
     * share this shape deliberately rather than by accident: the open tab is
     * written when the question settles, the other two are written by the click
     * that opens them, and a tab in hand is never paid for twice.
     *
     * No live draft, unlike the ordinary translate modes further down. Each of
     * these is a full rewrite in one call with no incremental machinery behind
     * it, so a draft is not a cheap preview of the final — it is a second
     * full-price answer to a message that is still being typed.
     */
    if (mode === 'native') {
      const auto = autoRegister.current;

      // Already written, or being written, for exactly this question.
      // `registerKey` empties both the moment the question changes, so this is
      // the check that keeps a settled pane settled.
      if (!auto.pending) {
        setWaiting(false);
        return cancelSchedule;
      }

      // Not typing: a mode switch, a language change, Ctrl+Enter, a pasted
      // paragraph. The question is finished, so there is nothing to wait for.
      if (!changed || forced || jumped) {
        setWaiting(false);
        auto.write(auto.id);
        return cancelSchedule;
      }

      // Backspace and Delete are typing and keep the hold — see the textarea's
      // onKeyDown. Only a cut lands here.
      if (removed) {
        setWaiting(false);
        return cancelSchedule;
      }

      setWaiting(true);
      const registerLead = holdSeconds >= 2 ? HOLD_LEAD_MS : 0;
      idleTimer.current = setTimeout(() => {
        idleTimer.current = null;
        let left = Math.max(1, holdSeconds - (registerLead ? 1 : 0));
        setCountdown(left);
        tick.current = setInterval(() => {
          left -= 1;
          if (left > 0) {
            setCountdown(left);
            return;
          }
          clearInterval(tick.current!);
          tick.current = null;
          setCountdown(null);
          setWaiting(false);
          // Read again rather than closed over: a click during the countdown
          // may have moved the reader to another tab, or written this one.
          const now = autoRegister.current;
          if (now.pending) now.write(now.id);
        }, 1000);
      }, registerLead);
      return cancelSchedule;
    }

    if (mode === 'ai-prompt') {
      const auto = autoStyle.current;

      /*
       * Nothing to write: the tab list has not arrived, or this tab is already
       * written — or being written — for exactly this question. `styleKey`
       * empties both of those the moment the question changes, so this is the
       * check that keeps a settled pane settled and not the one that makes it
       * stale.
       */
      if (!auto.id || !auto.pending) {
        setWaiting(false);
        return cancelSchedule;
      }

      /*
       * Not typing. A mode switch, a language change, Ctrl+Enter, a pasted
       * paragraph: the question is finished, so there is nothing to wait for.
       */
      if (!changed || forced || jumped) {
        setWaiting(false);
        auto.write(auto.id);
        return cancelSchedule;
      }

      // A cut is somebody taking something back, for the same reason it is
      // below: it starts no countdown, and the cleanup from the previous pass
      // has already called off the one that was running. Backspace and Delete
      // are NOT this — they are typing, and they keep the hold (see onKeyDown).
      if (removed) {
        setWaiting(false);
        return cancelSchedule;
      }

      // Typing. The operator's hold, counted down out loud, and this time over
      // a pane that does fill when it reaches zero.
      setWaiting(true);
      const promptLead = holdSeconds >= 2 ? HOLD_LEAD_MS : 0;
      idleTimer.current = setTimeout(() => {
        idleTimer.current = null;
        let left = Math.max(1, holdSeconds - (promptLead ? 1 : 0));
        setCountdown(left);
        tick.current = setInterval(() => {
          left -= 1;
          if (left > 0) {
            setCountdown(left);
            return;
          }
          clearInterval(tick.current!);
          tick.current = null;
          setCountdown(null);
          setWaiting(false);
          // Read again rather than closed over: a click during the countdown
          // may have moved the reader to another tab, or written this one.
          const now = autoStyle.current;
          if (now.id && now.pending) now.write(now.id);
        }, 1000);
      }, promptLead);
      return cancelSchedule;
    }

    /*
     * The question is settled: write the AI tab too, if it is the one open.
     *
     * Called at every point below where this branch decides the faithful answer
     * is being produced for a FINISHED question — and deliberately not from the
     * live draft, which is a provisional answer to a sentence still being typed.
     *
     * Reads the ref rather than closing over anything, so a tab that was
     * written, or closed, or opened between arming a timer and its firing is
     * seen as it is now. `loadAi` is itself guarded against writing a tab
     * twice, so calling this on a path that has already written costs nothing.
     */
    const settleAi = () => {
      const now = autoAi.current;
      if (now.pending) now.write();
    };

    const key = memoKey(text, from, to, mode);
    // A memo hit is a finished answer, and one of the three paths that used to
    // leave the AI tab empty — it returns without touching `waiting`.
    if (serveFromMemo(key)) { settleAi(); return; }

    // Already asking exactly this. Let it finish rather than paying twice — but
    // the question is still settled, so the second tab is still owed an answer.
    if (inflightKey.current === key) { settleAi(); return; }

    /**
     * Every sentence of this text is already translated, even though this exact
     * text has never been seen.
     *
     * The commonest way to arrive here is deleting a sentence, or moving one,
     * or typing back to something you had a moment ago. The old answer to all
     * of those was a five-second wait and a full re-translation of a paragraph
     * whose every sentence was already in hand. `run` assembles it without
     * asking anyone; the reason it is called from here rather than left to the
     * timers is that there is nothing to wait FOR.
     */
    const ready = planIncremental(text, from, to, mode);
    if (ready && !ready.missing.length) {
      run(text, from, to, mode);
      settleAi();
      return;
    }

    if (!changed || forced || jumped) {
      // Nothing on the wire: this is the ordinary case — a mode or a language
      // chosen while the pane is sitting still — and it answers immediately.
      if (!inflightKey.current) {
        run(text, from, to, mode);
        settleAi();
        return;
      }
      // Something IS streaming, so this click is about to throw a request away.
      // Wait a beat first: a burst of clicks lands inside the window, each one
      // cancelling the last timer, and only the mode the user actually settles
      // on is ever asked for.
      //
      // The running request is deliberately NOT aborted here — `run` does that
      // when it finally starts. Its prompt is already paid for, so letting it
      // run for another 350ms is nearly free, and it buys two things: if the
      // user clicks straight back to it, the early `inflightKey` check above
      // adopts the stream in progress instead of paying for it twice, and if it
      // finishes first its answer lands in the memo for nothing.
      idleTimer.current = setTimeout(() => {
        idleTimer.current = null;
        run(text, from, to, mode);
        settleAi();
      }, SETTLE_MS);
      return cancelSchedule;
    }

    /*
     * A deletion. Nothing is scheduled and nothing is sent.
     *
     * The timers below are how typing turns into a request, and a cut is not
     * typing — it is somebody taking something back with the mouse. Starting the
     * countdown here would translate a half-finished edit eight seconds later;
     * the previous 300ms leash did it almost at once, which was worse.
     *
     * Backspace and Delete used to arrive here too and no longer do: erasing a
     * character is mid-sentence typing, so it stays in the hold rather than
     * ending it. See the textarea's onKeyDown.
     *
     * The cleanup from the previous pass has already run by the time this does,
     * so a countdown that WAS running for the keystrokes before this one is
     * already cancelled — cutting calls off a pending translation as well as
     * not starting one.
     */
    if (removed) return cancelSchedule;

    // Typing. Two timers over one keystroke, answering two different questions.
    //
    // The flag goes up HERE, on the same beat they start, rather than off a
    // keystroke handler — so the layer is on screen for exactly the span the
    // pane is holding the FINAL back, and for no other. It stays up over the
    // live draft on purpose: the draft is provisional, and the dim plus the
    // countdown are how the pane says so.
    setWaiting(true);

    /*
     * 1. The live draft — the real-time answer.
     *
     * Skipped when the operator has set the debounce to 0, and skipped for the
     * one pass after a paste that carried no separator (see `noDraft`). It goes
     * through `run` like everything else, so it files its sentences and its memo
     * entry on the way past and the final that follows it is usually free.
     */
    if (liveMs > 0 && !suppressDraft) {
      liveTimer.current = setTimeout(() => {
        liveTimer.current = null;
        run(text, from, to, mode);
      }, liveMs);
    }

    /*
     * 2. The final, counted down out loud.
     *
     * The silent lead is inside the configured total rather than added to it —
     * see HOLD_LEAD_MS — so eight seconds in the console is eight seconds at the
     * keyboard.
     */
    const lead = holdSeconds >= 2 ? HOLD_LEAD_MS : 0;
    idleTimer.current = setTimeout(() => {
      idleTimer.current = null;
      let left = Math.max(1, holdSeconds - (lead ? 1 : 0));
      setCountdown(left);
      tick.current = setInterval(() => {
        left -= 1;
        if (left > 0) {
          setCountdown(left);
          return;
        }
        clearInterval(tick.current!);
        tick.current = null;
        setCountdown(null);
        // Down before the request goes, not after it comes back. The pane has
        // its own language for working — the spinner in the corner and the
        // streaming caret — and leaving the character up over the top of it
        // would say "still typing" at somebody who has stopped.
        setWaiting(false);

        /*
         * Three ways this costs nothing, and they are the whole reason a live
         * draft and a countdown can coexist without paying twice for one answer.
         *
         *   • the draft already answered exactly this text — a memo hit;
         *   • the draft is still on the wire asking exactly this — adopt it
         *     rather than aborting it and starting the same request again;
         *   • neither, so ask.
         */
        // Before the three-way below, because all three of its outcomes are
        // this same settled question — adopted, remembered or asked for.
        settleAi();

        const settled = memoKey(text, from, to, mode);
        if (inflightKey.current === settled) return;
        if (serveFromMemo(settled)) return;
        run(text, from, to, mode);
      }, 1000);
    }, lead);

    // Runs before the next pass and on unmount, so a keystroke always restarts
    // the wait from the beginning rather than stacking a second one behind it.
    return cancelSchedule;
    // `overview` is deliberately NOT in here. It changes nothing about the
    // question being asked — see the note in `run` — so listing it would make
    // pressing the button re-enter the scheduler and, in the worst case, fire a
    // second request for an answer already on screen.
  }, [text, from, to, mode, run, identity, imePending, cancelSchedule, memoKey, serveFromMemo, planIncremental,
      holdSeconds, liveMs, pageReady, manual]);

  /**
   * Ctrl/⌘+Enter — translate now — listened for on the WINDOW, not the box.
   *
   * It used to be a branch in the textarea's own keydown, which meant it only
   * existed while the caret was inside the textarea. Measured: with focus on
   * the page body, or on any button — after clicking a mode, a language tab,
   * Copy, or simply clicking the page — the shortcut fired NOTHING, and the
   * reader who pressed it then sat through the full five-second wait believing
   * they had asked for it. That is the whole of what "it doesn't translate
   * instantly" meant.
   *
   * From inside the box it is unchanged and already immediate: 1ms from the
   * keypress to the request leaving, against 5,709ms for the wait it skips.
   *
   * Two things it deliberately stays out of:
   * — another text field. The feedback dialog sends with Ctrl+Enter, and a
   *   shortcut that also translated the page behind it would be a bug in both.
   * — an open dialog. Nobody pressing keys over the voice or image dialog is
   *   asking about the text underneath it.
   *
   * Through the memo first, like every other path that can spend. "Now" is a
   * statement about the WAIT, not a request to buy an answer we already hold.
   */
  /**
   * Translate now — the act itself, with no opinion about what asked for it.
   *
   * Extracted from the key handler below because it is no longer only a key
   * handler: the hold layer in the output pane puts the same thing on screen as
   * a button, for the reader who is looking straight at the pane that has gone
   * quiet and is not going to guess at a chord. The two MUST stay one function.
   * A button that re-implemented this would be a second place to forget the
   * memo check, and the failure that produces — a click that buys an answer
   * already in hand — is invisible on screen and shows up only on the bill.
   *
   * Through the memo first, like every other path that can spend. "Now" is a
   * statement about the WAIT, not a request to buy an answer we already hold.
   */
  const translateNow = useCallback(() => {
    if (!text.trim()) return;
    // Before the account and the engine are known, the level is not: see `pageReady`.
    if (!pageReady) return;
    cancelSchedule();

    /*
     * AI Prompt has no `run()` to reach — so ask for the tab directly.
     *
     * `run()` deliberately does nothing in this mode: five finished prompts in
     * one call is exactly what the lazy tabs were built to stop, so the only
     * thing that spends here is `loadStyle`, one style at a time. Which left
     * "translate now" doing strictly LESS than nothing: `cancelSchedule()`
     * above tore down the countdown that was about to write the open tab, and
     * `run()` then returned without replacing it. The chord did not fail
     * quietly — it cancelled the answer, and the pane sat on "this version is
     * written when you stop typing" for as long as you were willing to wait.
     *
     * Read from the ref for the same reason the scheduler does: `styleTexts`
     * changes on every streamed chunk, and listing it here would rebuild this
     * callback — and with it the window key listener that holds it — several
     * times a second while an answer arrives.
     *
     * `loadStyle` keeps its own already-written guard, so this is the memo
     * check below by another name: a tab in hand costs nothing to re-open, and
     * pressing the chord twice does not buy the same prompt twice.
     */
    if (mode === 'ai-prompt') {
      const auto = autoStyle.current;
      if (auto.id) auto.write(auto.id);
      return;
    }

    // The same for Registers, and for the same reason: without it the chord
    // tore down the countdown that was about to write the open tab and then
    // returned without replacing it, so "translate now" did strictly less than
    // nothing. `loadRegister` keeps its own already-written guard, so pressing
    // the chord twice does not buy the same version twice.
    if (mode === 'native') {
      const auto = autoRegister.current;
      auto.write(auto.id);
      return;
    }

    /*
     * Above the first level, ONE request per press: the faithful answer when it
     * is not in hand, and only once it is, the open Software-AI / Easy tab. The
     * tabs are drawn under the faithful answer, so that one comes first.
     */
    if (manual) {
      const key = memoKey(text, from, to, mode);
      if (inflightKey.current === key) return;
      if (serveFromMemo(key)) {
        const extra = autoAi.current;
        if (extra.pending) extra.write();
        return;
      }
      run(text, from, to, mode);
      return;
    }

    if (!serveFromMemo(memoKey(text, from, to, mode))) run(text, from, to, mode);
    // …and Translate's open extra tab (Software-AI / Easy), exactly as the
    // scheduler's `settleAi` does. Without this, Ctrl+Enter after an edit wrote
    // Standard only — and `cancelSchedule` above had just killed the countdown
    // that would have written the tab — so it sat on "Click Software-AI to
    // write this version" however many times Ctrl+Enter was pressed.
    const extra = autoAi.current;
    if (extra.pending) extra.write();
  }, [text, from, to, mode, run, cancelSchedule, memoKey, serveFromMemo, pageReady, manual]);

  /**
   * The same act, asked for with the mouse.
   *
   * The only thing it adds is the focus, and it is not a flourish. Clicking any
   * button takes the caret out of the textarea, so without this the click that
   * says "I am finished, show me" would also be the click that made the next
   * keystroke go nowhere — the reader carries on typing into a box that no
   * longer has focus and loses the characters. Pressing the chord has the same
   * effect for free, because it never moved focus in the first place.
   */
  const translateNowFromPane = useCallback(() => {
    translateNow();
    inputRef.current?.focus();
  }, [translateNow]);

  /*
   * ── PRESSING CTRL SHOWS THE MODE NUMBERS ──────────────────────────────────
   *
   * The key itself, on its own. Not Ctrl-with-a-click and not Ctrl-with-a-
   * letter: you press Ctrl and the row appears.
   *
   * THEY STAY UP WHEN THE KEY IS RELEASED, and that is the part that decides
   * the whole design. A row that vanished with the key would be the macOS
   * Option-key pattern, where the modifier reveals shortcuts you then press on
   * the keyboard — but these are BUTTONS, meant to be clicked, and you cannot
   * click one while holding a key down and still have a hand for the mouse. So
   * Ctrl reveals them and they wait. Escape, the ✕, or picking one puts them
   * away.
   *
   * ── The chord guard ───────────────────────────────────────────────────────
   * Ctrl is the first half of everything this box already does — Ctrl+A,
   * Ctrl+C, Ctrl+V, Ctrl+Enter — so the row would flash up on every one of
   * them. It opens on the BARE key (`e.key === 'Control'`, nothing else down
   * yet) and closes again the moment a second key joins it, so a chord never
   * leaves it on screen. Copying a paragraph looks exactly as it always did.
   *
   * The two guards are the ones the Ctrl+Enter handler below already applies,
   * for the same reasons: nothing happens behind the identity wall, and nothing
   * happens while another of this page's dialogs owns the screen.
   */
  useEffect(() => {
    // `globalThis.KeyboardEvent` for the reason the Ctrl+Enter handler below
    // states: React's own KeyboardEvent type is in scope here and shadows the
    // DOM one.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { setModeNumbers(false); return; }

      // The bare modifier. `e.key === 'Control'` is the key itself arriving;
      // the two `!`s are what make it BARE — Ctrl+Shift and ⌘+Ctrl are the
      // starts of other chords, not a request for this.
      if ((e.key === 'Control' || e.key === 'Meta') && !e.altKey && !e.shiftKey) {
        if (document.querySelector('[data-gt-modal="mandatory"]')) return;
        if (voiceOpen || imageOpen || feedbackOpen || notebookOpen) return;
        setModeNumbers(true);
        return;
      }

      // Nothing below here means anything unless the badges are on screen.
      if (!modeNumbers) return;

      /*
       * The pick — Ctrl held, then the number.
       *
       * The badges live exactly as long as the key that summoned them (see the
       * keyup handler below), so this only ever runs mid-chord and a digit
       * typed on its own is never touched.
       *
       * ⚠️ Ctrl+1 … Ctrl+8 are the browser's own tab shortcuts and a page
       * cannot take them back, whatever it does with preventDefault. The mode
       * still changes here — this handler runs first — but Chrome, Edge and
       * Firefox will also move the reader to another tab, so the chord is
       * dependable only where those are not bound (a standalone window, an
       * installed PWA). Ctrl+9 and anything past the eighth mode are clear.
       */
      if (/^[1-9]$/.test(e.key)) {
        const i = Number(e.key) - 1;
        const target = modes[i];
        if (target) {
          // Only when it IS a mode: an unclaimed digit has to reach the box as
          // the character somebody typed.
          e.preventDefault();
          selectMode(target.id);
        } else if (i === modes.length && notebookAllowedRef.current) {
          /*
           * One past the last mode: the Notebook. Ctrl+7 today, and Ctrl+8 the
           * day a seventh mode ships — the number is read off the rail rather
           * than written down here, exactly as the badge on the button is.
           */
          /*
           * Ctrl+7 is this button's NUMBER, so it does what the button does —
           * saves what is on screen and opens the notebook onto it. A shortcut
           * drawn on a control and then behaving differently from it is a
           * shortcut nobody can trust. Ctrl+Alt+Z is the one that only opens.
           */
          e.preventDefault();
          notebookAction.current();
        }
        setModeNumbers(false);
        return;
      }

      /*
       * Anything else puts them away.
       *
       * The badges are a question — "which number is which?" — and every key
       * that is not an answer to it is the reader having moved on. Typing
       * carries on untouched, because nothing above swallowed the keystroke.
       * Shift and Alt are exempt: they are the START of what somebody types,
       * not a change of subject.
       */
      if (e.key !== 'Shift' && e.key !== 'Alt') setModeNumbers(false);
    };

    /*
     * Let go of Ctrl and they are gone.
     *
     * The badges are what the key is SAYING, so they last exactly as long as it
     * is held — the same relationship a modifier has with the cursor changing
     * shape over a link. Nothing to dismiss, nothing left on screen to wonder
     * about, and no state that can outlive the gesture that made it.
     *
     * It also makes a bare digit safe again: with nothing on screen after the
     * release, the handler above returns early and every number somebody types
     * goes into the box as itself.
     */
    const onKeyUp = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') setModeNumbers(false);
    };

    /*
     * Alt-Tab away mid-press and the keyup never arrives — the badges would be
     * waiting, held up by a key that is no longer down, when the window came
     * back. A modifier's effect must not survive the window losing focus.
     */
    const onBlur = () => setModeNumbers(false);

    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [voiceOpen, imageOpen, feedbackOpen, notebookOpen, modeNumbers, modes, selectMode]);

  useEffect(() => {
    // `globalThis.KeyboardEvent`, not the bare name: React's own KeyboardEvent
    // type is in scope in this file and shadows the DOM one.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') return;
      if (voiceOpen || imageOpen || feedbackOpen || notebookOpen) return;
      /*
       * Nothing at all happens while the identity wall is up.
       *
       * The wall holds focus inside its own card, which covers the textarea and
       * every button — but this listener is on the WINDOW, so a chord pressed
       * with focus on one of its team buttons would still reach here and buy a
       * translation of whatever was in the box beforehand. "No actions until you
       * have answered" has to mean no actions.
       *
       * Read off the DOM rather than through the identity context on purpose:
       * this component has no other reason to know about identity, and a hook
       * added here would re-render the whole translator on every change to it.
       */
      if (document.querySelector('[data-gt-modal="mandatory"]')) return;
      const el = e.target as HTMLElement | null;
      if (el && el !== inputRef.current && /^(INPUT|TEXTAREA)$/.test(el.tagName)) return;
      if (el && el.isContentEditable) return;
      if (!text.trim()) return;
      e.preventDefault();
      translateNow();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [text, translateNow, voiceOpen, imageOpen, feedbackOpen, notebookOpen]);

  useEffect(() => () => inflight.current?.abort(), []);

  /* ── Dictation, continued ─────────────────────────────────────────────────*/

  /**
   * The caret, moved after a committed run has been painted.
   *
   * It has to happen after the render rather than in the handler: the textarea
   * is controlled, so at the moment `setText` is called the DOM still holds the
   * previous, shorter value, and a selection set against it would be clamped to
   * the end of that one. Only finals move it — following every partial would
   * drag the caret backwards and forwards under anyone trying to type.
   */
  useEffect(() => {
    const want = caretAfterPaint.current;
    if (want === null) return;
    caretAfterPaint.current = null;
    // Not `focus()`: the mic button is what has focus while dictating, and
    // stealing it would mean the Space bar stopped the recording instead of
    // typing a space.
    inputRef.current?.setSelectionRange(want, want);
  });

  /**
   * Dictation is starting. Anchor it wherever the caret is.
   *
   * `caret` state rather than the textarea's own `selectionStart`, and the
   * difference is not cosmetic. Pressing the mic moves focus to the button, and
   * a textarea that has never been focused reports a selection of 0 — so
   * reading the DOM would anchor at the START of the box for the commonest case
   * of all: text that arrived from a paste or a voice file, followed by
   * someone pressing the mic to add to it. Every path that fills the box sets
   * `caret` with it, so this is the position the user last left.
   */
  const beginDictation = useCallback(() => {
    dictation.current = { base: text, at: Math.min(caret, text.length) };
  }, [text, caret]);

  /**
   * Words from the microphone, provisional or committed.
   *
   * The clamp at the end is not a formality. Speech has no natural stopping
   * point, so the box fills up in the one input mode where the user is not
   * watching the character count — and a `maxLength` on the textarea does not
   * apply here, because this is a state update rather than a keystroke.
   */
  const applyDictation = useCallback((spoken: string, commit: boolean) => {
    const anchor = dictation.current;
    if (!anchor) return;

    const head = anchor.base.slice(0, anchor.at);
    const tail = anchor.base.slice(anchor.at);
    /*
      "Enter" and "new line", said on their own, break the line instead of being
      written down — see `spokenCommand`, which owns the rule and the words in
      every language it knows.

      No `joinSpoken` on this branch, and that is the point of having a branch:
      a line break is not a word, so it takes no space in front of it. What
      comes after it needs none either, which `joinSpoken` already gets right on
      its own — a newline is whitespace, so the next phrase sees a space is
      already there.
    */
    const command = spokenCommand(spoken);
    const insert = command ?? joinSpoken(head, spoken);
    const room = pageSizeRef.current - anchor.base.length;
    const kept = insert.length > room ? insert.slice(0, Math.max(0, room)) : insert;

    setText(head + kept + tail);
    if (!commit) return;

    dictation.current = { base: head + kept + tail, at: anchor.at + kept.length };
    setCaret(anchor.at + kept.length);
    caretAfterPaint.current = anchor.at + kept.length;
    // Dictating is writing, and the same two things follow from it as from
    // typing: an offer made about the old text no longer describes this one,
    // and there is nothing left to take back with a backspace.
    setDismissed(null);
    undo.current = null;
    if (kept.length < insert.length) {
      setMicError(`This page holds ${pageSizeRef.current.toLocaleString()} characters — the rest was not written.`);
    }
  }, []);

  /**
   * Dictation ended, however it ended.
   *
   * Any partial still on screen is dropped. It is a guess the recogniser never
   * stood behind, and leaving half a misheard word in the box would be worse
   * than the silence it came from — the engines finalise what they have on
   * `stop()`, so in the ordinary case there is nothing here to drop.
   */
  const endDictation = useCallback(() => {
    const anchor = dictation.current;
    dictation.current = null;
    if (!anchor) return;
    setText(anchor.base);

    // Someone who has stopped talking has finished their thought, in exactly
    // the way a paste or an Enter is finished — so it does not sit out the
    // typing pause as though they were still deciding what to say.
    //
    // Asked for HERE rather than by setting `forceNext` and letting the
    // scheduler notice, because in the ordinary case there is nothing for it to
    // notice: the last partial was already replaced by its final, so `setText`
    // above is a no-op, React does not re-render, and the effect never runs.
    // The flag would then sit armed until the user's next keystroke and make
    // THAT translate instantly, which is a different feature than the one
    // intended. Through the memo first, like every other path that can spend.
    if (!anchor.base.trim()) return;
    cancelSchedule();
    // Above the first level, the end of dictation is not a request — only
    // Translate is (an answer already held is still put back).
    if (!serveFromMemo(memoKey(anchor.base, from, to, mode)) && !manual && pageReady) run(anchor.base, from, to, mode);
  }, [from, to, mode, run, cancelSchedule, memoKey, serveFromMemo, manual, pageReady]);

  /**
   * Swap the two languages *and* the two texts, so the translation becomes the
   * new input — swapping the labels alone would leave the panes describing
   * something that is no longer true.
   */
  function swap() {
    // The button is disabled in all of these cases; this is the guard for the
    // keyboard, for a click that lands in the same frame as a state change, and
    // for the next person to call this from somewhere else.
    if (swapBlocked || lockedTarget || followsInput) return;
    const newFrom = swapSource;
    if (!newFrom) return;
    // Both languages are now in active use, so both earn a visible tab.
    // `applyFrom`/`applyTo`, not the pickers: swap has already decided both
    // halves, and the pickers' flip rule would try to help by adjusting the side
    // that is about to be set a line later.
    applyFrom(to);
    applyTo(newFrom);

    // `answerText`, not `output`. The raw answer can carry things that belong to
    // the PANE rather than to the text — a trailing teaching section, the
    // overview tag when that switch is on, and the [[FORMAL]]/[[CASUAL]] tags.
    // Swapping the raw string would paste `[[OVERVIEW]] …` straight into the box
    // someone is about to type in.
    const incoming = answerText;

    // File the pair the panes are about to show, in BOTH directions, before any
    // of it becomes state. This is the whole of the swap fix — see the note on
    // `seedSwapPair`.
    seedSwapPair(newFrom, to, text, incoming, output);

    /*
     * The two summary cards change sides with the texts they describe.
     *
     * Each card is about the pane UNDER it, in that pane's own language: the one
     * over the box says what the box says, the one over the answer says what the
     * answer says. Swapping moves each of those texts to the other half of the
     * screen, so a card left where it was is now a sentence about the pane
     * opposite — a Korean summary of the input sitting over an input box that
     * has just become English.
     *
     * Exchanged HERE rather than re-derived from the next answer, because there
     * is no next answer to derive it from: the swapped pair is served out of the
     * memo (see `seedSwapPair`), a remembered answer carries no `[[OVERVIEW]]`
     * tags, and the cards therefore fall back to these held values for as long
     * as the pair stays on screen. Without the exchange they hold the pre-swap
     * arrangement indefinitely.
     *
     * `|| other` keeps a card on screen in the one case where only one of the
     * two summaries arrived — the alternative is the card blinking out and
     * taking ~100px of the pane with it, which is the same trade `shownOverview`
     * already makes by holding the previous sentence.
     */
    {
      const { target, source } = lastOverview.current;
      lastOverview.current = { target: source || target, source: target || source };
    }

    setText(incoming);
    setOutput(text);
    setDetected(null);
    // The panes are about to describe a different question; anything the old one
    // left on screen is now wrong about the new one.
    setError(null);
    setLimited(null);
    setStat(null);

    // Swapping is a request for a translation, not a draft in progress.
    //
    // The scheduler tells the two apart by whether the TEXT changed, and this
    // changes it — so without saying otherwise, pressing swap would sit through
    // the full five-second wait as though the new text were being typed. It is
    // the same reasoning as Enter and paste: someone who pressed a button asking
    // for the other direction is asking for it now.
    // Only when there IS a text: a language-only swap (both boxes empty) has
    // nothing to translate, and an armed flag would make the user's NEXT
    // keystroke translate instantly instead.
    if (incoming.trim()) forceNext.current = true;

    // The box now holds something the user never typed — none of the composition
    // state that described the old contents still applies to it.
    setCaret(incoming.length);
    setDismissed(null);
    undo.current = null;
  }

  // Split the answer: the result stays in the pane, any trailing teaching
  // section moves to its own panel below. Recomputed as the stream arrives,
  // which is cheap — one findIndex over the lines.
  /**
   * The overview rides at the END of the same answer — one request, not two.
   * Taken off here before anything else looks at the text, so every downstream
   * reader (the notes split, the NO_MISTAKES sentinel, Copy) sees the answer as
   * it would have been without the feature.
   */
  /**
   * The last summary that actually arrived, held across the next translation.
   *
   * The cards are hidden while a request is running, for a good reason: the
   * model writes the summary LAST, so mid-stream there is nothing to show, and
   * without the guard the card would pop in above the answer and push it down at
   * the moment somebody started reading it.
   *
   * But hiding is not free either, and on a page that translates while you type
   * it is the more expensive of the two. Both cards vanish and come back on
   * every pause — measured at roughly 250px of document height appearing and
   * disappearing, which moves everything below it and, at the wrong moment,
   * takes the window's scrollbar with it.
   *
   * So the card keeps showing the PREVIOUS summary until the new one lands,
   * which is exactly what the answer pane below it already does with the
   * previous translation, and for the same reason: a sentence that is one
   * revision out of date for two seconds is worth more than a hole in the layout
   * that is honest about nothing.
   */
  const lastOverview = useRef({ target: '', source: '' });
  /** The same, for the notes card under the answer. */
  const lastNotes = useRef<{ key: string; head: string; body: string; section: string }[]>([]);

  /**
   * The input-language summary comes off FIRST, because it is written LAST.
   *
   * Reversing these two would hand `splitOverview` a string whose tail is the
   * whole `[[OVERVIEW_SRC]] …` line, and it keeps everything after its own tag —
   * so the target card would show both sentences run together in two languages.
   */
  const { body: afterSourceOverview, sourceOverview } = useMemo(
    // `preferredTerms` goes here and only here, because this is the neck of the
    // funnel: the overview, the notes, the variants, the style tabs, the laid
    // out answer, Copy and Listen are all derived from this one string further
    // down. Applying it at the top means every one of them says 조선 without a
    // single one of them having to know that it does — and the raw `output`
    // state keeps what actually arrived, so nothing that reasons about the
    // answer (sentence memory, the Mistakes sentinel) is reading edited text.
    () => splitSourceOverview(preferredTerms(output)),
    [output],
  );

  const { body: afterOverview, overview: overviewText } = useMemo(
    () => splitOverview(afterSourceOverview),
    [afterSourceOverview],
  );

  /**
   * Then the learner notes, which sit between the answer and the overview.
   *
   * These are the words in the answer somebody still learning would not know,
   * explained — what the Explain mode used to produce as a whole separate
   * button. They ride under the Overview switch now: the summary goes to the
   * top of the pane, this goes to the bottom as a notes card.
   */
  const { body: outputBody, notes: taughtNotes } = useMemo(
    () => splitNotes(afterOverview),
    [afterOverview],
  );

  const { main: outputMain, notes: modeNotes } = useMemo(
    () => splitOutput(outputBody),
    [outputBody],
  );

  /**
   * The multi-card answer, or null for every ordinary one.
   *
   * Registers returns the same message at three levels of one language's
   * register, and the whole value of it is the contrast — run together in one
   * paragraph they read as a rewrite that changed its mind twice. See
   * `splitVariants`.
   */
  const variants = useMemo(() => splitVariants(outputBody), [outputBody]);

  /**
   * Which register is open, and which one this browser opens on next time.
   *
   * The three versions used to be stacked cards — all of them on screen, one
   * under the other. Tabs instead, for the reason AI Prompt has them: what the
   * reader does with this answer is TAKE ONE. Formal, Friendly and Slang are
   * alternatives, and on a pasted message each of them is a full rendering of
   * the whole thing, so three of them stacked is three screens to scroll past to
   * reach the one being sent.
   *
   * `null` means "nothing chosen on this browser yet", which resolves to Formal
   * below — the same shape `styleTab` uses, and for the same reason: this is
   * read out of localStorage in an effect, so it has to have a meaning before
   * the effect has run.
   */
  const [registerTab, setRegisterTab] = useState<Variant['key'] | null>(null);

  /**
   * …remembered across visits, not just across clicks in one page view.
   *
   * Somebody who writes to the same people every day has one of these three as
   * their register, and choosing it again on every answer is choosing it about
   * the product rather than about the message. Kept in React state alone it was
   * thrown away on every refresh.
   *
   * Read in an effect rather than in `useState`, like every other preference in
   * this file — this component is server-rendered, and a first render that reads
   * localStorage produces markup the server could not have produced, which React
   * resolves by discarding the whole tree.
   *
   * Validated on the way IN rather than on the way out, which is the one place
   * it differs from `styleTab`: that set is operator-editable and can go stale
   * at any time, while these three are fixed in the prompt, so a value that is
   * not one of them is a key from an older version and there is no later moment
   * at which it could become valid again.
   */
  const REGISTER_TAB_PREF = 'gt.register-tab.v1';
  useEffect(() => {
    let pref: string | null = null;
    try { pref = localStorage.getItem(REGISTER_TAB_PREF); } catch { /* private mode */ }
    if (pref && (VARIANT_ORDER as string[]).includes(pref)) {
      setRegisterTab(pref as Variant['key']);
    }
  }, []);

  /** Open a register, and remember it for the next visit. */
  const chooseRegisterTab = useCallback((key: Variant['key']) => {
    setRegisterTab(key);
    try { localStorage.setItem(REGISTER_TAB_PREF, key); } catch { /* private mode */ }
  }, []);

  /** Open a tab AND write it if it has never been opened for this question. */
  const openRegisterTab = useCallback((key: Variant['key']) => {
    chooseRegisterTab(key);
    loadRegister(key);
  }, [chooseRegisterTab, loadRegister]);

  /**
   * The tab that is HIGHLIGHTED — which is not the same as the tab that has
   * text.
   *
   * The row is drawn from `VARIANT_ORDER`, not from the answer, which is the
   * structural thing AI Prompt's tabs get right: tabs built out of the tags as
   * they land appear one at a time, so the row rearranges itself under the
   * reader's cursor while they are trying to click it. These three are fixed, so
   * all three are on screen from the first tag and only their CONTENTS arrive in
   * sequence.
   */
  const activeRegister: Variant['key'] = registerTab ?? 'formal';

  /**
   * Which of Translate's two tabs is open, remembered across visits.
   *
   * Somebody who reads the AI rendering reads it for the same reason every
   * time — it is how they prefer their messages delivered, not something they
   * decide per message — so making them click it again on every answer is
   * making them choose about the product rather than about the text. The same
   * argument the register tab and the AI Prompt tab are remembered on.
   *
   * Read in an effect rather than in `useState`, like every other preference in
   * this file: this component is server-rendered, and a first render that reads
   * localStorage produces markup the server could not have produced, which
   * React resolves by discarding the whole tree.
   */
  const [transTab, setTransTab] = useState<TransTab>('standard');

  // The key predates both renames and is kept so nobody's choice is lost. It
  // holds 'standard', 'ai' or 'easy' — the last one is the Easy tab again.
  const AI_TAB_PREF = 'gt.easy-tab.v1';
  useEffect(() => {
    let pref: string | null = null;
    try { pref = localStorage.getItem(AI_TAB_PREF); } catch { /* private mode */ }
    if (pref === 'ai' || pref === 'easy') setTransTab(pref);
  }, []);

  /** Open a tab, remember it, and write an extra one the first time it is asked for. */
  const openAiTab = useCallback((want: TransTab) => {
    setTransTab(want);
    try {
      localStorage.setItem(AI_TAB_PREF, want);
    } catch { /* private mode */ }
    if (want !== 'standard') loadAi(want);
  }, [loadAi]);

  /**
   * The AI tab is only offered where it means something.
   *
   * `translate` alone: the other modes in the group are already re-wordings of
   * one kind or another, Registers and AI Prompt own the tab strip for their
   * own answers, and the learning modes answer in the source language, where
   * "easier to understand" is the whole mode rather than a tab on it.
   *
   * Also not on an identity answer — `to` the same language as `from` returns
   * the input unchanged without a model call, and there is no second reading of
   * a text that was never translated.
   */
  const showAiTabs = mode === 'translate' && !identity;
  /** Which extra tab is really on screen: they can only be open where they are offered. */
  const openExtra: ExtraTab | null = showAiTabs && transTab !== 'standard' ? transTab : null;
  const onAi = openExtra !== null;
  /** The open extra tab's answer, progress and error (null / false under Standard). */
  const aiText = openExtra ? aiTexts[openExtra] : null;
  const aiBusy = openExtra ? aiBusyMap[openExtra] : false;
  const aiError = openExtra ? aiErrors[openExtra] : null;

  /*
   * …and the two facts about the AI tab where the SCHEDULER can reach them.
   * Written on every render, read only from inside its timers. See `autoStyle`.
   *
   * ⚠️ THIS REPLACED AN EFFECT, and the effect is why the tab sometimes never
   * wrote at all while Standard showed an answer.
   *
   * That effect tried to work out for itself when the question had settled, by
   * watching `busy`, `waiting` and `countdown`. Those are not that signal. The
   * translate branch of the scheduler has three paths that produce a finished
   * answer and return WITHOUT clearing `waiting` — a memo hit (`serveFromMemo`),
   * adopting a request already on the wire for this exact text
   * (`inflightKey`), and the incremental path where every sentence is already
   * translated. All three are the fast, free ones, so the commonest way to see
   * the bug was the commonest way to use the product: retype something you had
   * a moment ago, get the faithful answer instantly, and watch AI sit empty
   * because a stale `waiting` from an earlier keystroke was still up.
   *
   * There is no version of that effect that is correct, because "settled" is
   * not derivable from those flags — it is a decision the scheduler makes, in
   * six different places, for six different reasons. So the tab is now told,
   * the way Registers and AI Prompt are told, and this ref is the only thing
   * either of them needed.
   */
  autoAi.current = {
    // "The open tab has no answer and nothing is being written for it." Both
    // halves matter, exactly as in `autoRegister`: a tab already written must
    // not be paid for twice, and one being written right now must not be
    // started again by a timer still running behind the click that started it.
    pending: onAi && aiText === null && !aiBusy && !!text.trim(),
    write: () => { if (openExtra) loadAi(openExtra); },
  };

  /**
   * The tabbed answer — AI Prompt, which writes one finished prompt per style.
   *
   * Tabs rather than the stacked cards Registers uses, and the difference is
   * what the reader does with them. Registers is a comparison: the three
   * versions of one sentence only teach anything side by side. These are
   * alternatives — one of them is the prompt you are about to take away — and
   * stacking two 900-token prompts would bury the second one under a screen of
   * the first.
   */
  /**
   * The tab on screen: the reader's remembered choice if that style still
   * exists, then the default tab, then the first tab.
   *
   * Taken from the STYLE LIST rather than from the answer, which is the change
   * that lets the row be drawn before anything has been written. The fallbacks
   * are unchanged and still handle every way a remembered choice can go stale —
   * a style disabled in the console, a key left over from an older version —
   * and the last one is unconditional because a default id can go stale in
   * exactly the same way a remembered one can.
   *
   * Being the active tab no longer implies having any text: nothing is written
   * until a tab is clicked, so this names the tab that is HIGHLIGHTED, and
   * `styleTexts` says whether there is anything under it.
   */
  const activeStyleId = useMemo(() => {
    if (!promptStyles.length) return null;
    const has = (id: string | null) => !!id && promptStyles.some((s) => s.styleId === id);
    if (has(styleTab)) return styleTab;
    if (has(DEFAULT_STYLE_TAB)) return DEFAULT_STYLE_TAB;
    return promptStyles[0].styleId;
  }, [promptStyles, styleTab]);

  /** The text under the open tab, or '' when it has not been asked for yet. */
  const activeStyleText = activeStyleId ? styleTexts[activeStyleId] ?? '' : '';

  /*
   * Grammar Fix answers in two parts — the corrected text, then a short
   * `🔧 FIXES` list of the serious mistakes it put right (see lib/modes.ts).
   * Only that tab is split: the corrected prompt stays in the pane and is what
   * Copy takes, and the list drops into its own card below. Every other style
   * is one block and passes through untouched.
   */
  const { body: styleMain, fixes: styleFixes } = useMemo(
    () => (activeStyleId === 'grammar-fix' ? splitGrammarFixes(activeStyleText) : { body: activeStyleText, fixes: '' }),
    [activeStyleId, activeStyleText],
  );

  /*
   * …and the same three facts where the scheduler can reach them. Written on
   * every render, read only from inside a timer.
   */
  autoStyle.current = {
    id: activeStyleId,
    // "Nobody has asked for this one yet." Both halves matter: a tab already
    // written must not be paid for twice, and a tab being written right now
    // must not be started again by the countdown that is still running behind
    // the click that started it.
    pending:
      !!activeStyleId &&
      styleTexts[activeStyleId] === undefined &&
      styleLoading !== activeStyleId,
    write: loadStyle,
  };

  /*
   * The one moment the scheduler cannot see: the style list landing.
   *
   * The tabs come from /api/modes, which answers a beat after the first render.
   * Somebody who has a rough prompt in the box before it does — arriving in
   * this mode from another one is the ordinary way — has already made the last
   * keystroke there is going to be, so no countdown is coming to start the
   * answer, and `activeStyleId` was still null when the scheduler last looked.
   *
   * Fires on the pass the list arrives on and on no other; every later render
   * takes the `landed` guard and does nothing.
   */
  const hadStyles = useRef(false);
  useEffect(() => {
    const has = promptStyles.length > 0;
    const landed = has && !hadStyles.current;
    hadStyles.current = has;
    if (!landed || mode !== 'ai-prompt') return;
    // Above the first level only Translate writes; before `pageReady` the scheduler
    // will look again (and write it) once the level is known.
    if (manual || !pageReady) return;
    const auto = autoStyle.current;
    if (!auto.id || !auto.pending || !text.trim()) return;
    auto.write(auto.id);
    // `text` is read, not depended on: this effect is about the list arriving,
    // and listing the box would re-run it on a keystroke the scheduler owns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptStyles, mode]);

  /**
   * A tab's name. The server's label when it has one, and otherwise the id made
   * readable — `developing-fast` becomes "Developing fast".
   *
   * The fallback is not decoration: styles are added from the console, and a
   * reader with the page already open has a mode list from before that style
   * existed. A tab titled from its id is a usable tab; one titled `undefined`
   * is a bug report.
   */
  const styleName = useCallback(
    (id: string) =>
      promptStyles.find((s) => s.styleId === id)?.label ??
      id.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    [promptStyles],
  );

  /**
   * How the source is laid out — the blank lines, the indents, and where each
   * segment sits in the box.
   *
   * Recomputed on every keystroke, which is one pass over the lines of a
   * document capped at 5,000 characters. It is what the answer is laid out
   * against and what the source highlight is measured with, so it must describe
   * the text as it is RIGHT NOW rather than as it was when the answer was
   * fetched: that is exactly what makes a blank line typed after the fact appear
   * in the translation without asking anyone.
   */
  const shape = useMemo(() => shapeOf(text), [text]);

  /**
   * The answer, laid out like the source.
   *
   * The model is told to preserve line structure and mostly does, but "mostly"
   * is not a layout: paragraphs came back welded together, or split where the
   * source had no break. It was also being asked to reproduce something we
   * already know for certain — the source is right there in the box — so it is
   * no longer trusted with it. `reshape` regroups the answer under the source's
   * own blank lines, indents and spacing, and does it by matching sentences
   * rather than lines, so it comes out right whichever way the model reflowed.
   *
   * `links` comes back non-null only when the two texts really did correspond,
   * and is what lets the two panes point at each other below.
   */
  const laidOut = useMemo(() => reshape(outputMain, shape), [outputMain, shape]);

  /**
   * The same treatment for the two-card answer.
   *
   * Registers is where the missing line breaks were most obvious — it is
   * the mode people paste a whole message into, and both cards were coming back
   * as one paragraph each however the message was written. Each card is a full
   * rendering of the same source, so each is laid out against the same shape.
   */
  const laidOutVariants = useMemo(
    () => variants?.map((v) => ({ key: v.key, ...reshape(v.text, shape) })) ?? null,
    [variants, shape],
  );

  /**
   * The AI answer, laid out against the same source shape as every other one,
   * so a message written as four lines comes back as four lines — and so the
   * sentence links work under this tab exactly as they do under the faithful
   * one.
   */
  const laidOutAi = useMemo(
    () =>
      // `reline` first, then the ordinary `reshape`.
      //
      // This tab is the one that re-flows its lines: it is asked to break long
      // sentences up, and a model that has split one usually puts the halves on
      // their own lines too. That misses both of `reshape`'s counting rules, so
      // the answer kept its own layout and the reader lost the blank lines
      // between their own paragraphs — the faithful tab beside it keeps them,
      // which is exactly what made it look broken. `reline` folds the answer
      // back onto the source's lines first, so the line rule applies here the
      // same as it does anywhere else.
      aiText === null
        ? null
        // `trustLines` because `reline` has just made the correspondence true by
        // construction — and because this tab trips the veto it turns off, for
        // a reason built into what the tab is for. See ReshapeOptions.
        : reshape(reline(aiText, shape), shape, { trustLines: true }),
    [aiText, shape],
  );

  /**
   * What is under the open tab, laid out — or null when nothing has been
   * written for it yet.
   *
   * From `registerTexts`, the per-tab cache, and NOT from `laidOutVariants`:
   * one register arrives per request now, so there is no three-part answer in
   * `output` to pick a card out of. `laidOutVariants` stays for the answers
   * that still come back whole — a row restored from the result cache, a
   * transcript — and is the fallback below.
   *
   * Reshaped against the same `shape` every other answer is, so a message
   * written as four lines comes back as four lines.
   */
  const activeVariant = useMemo(() => {
    const own = registerTexts[activeRegister];
    if (own !== undefined) return { key: activeRegister, ...reshape(own, shape) };
    // An answer that arrived whole — from the result cache, or from a client
    // that did not ask for one register. Nothing writes these any more, and
    // reading them costs nothing.
    return laidOutVariants?.find((v) => v.key === activeRegister) ?? null;
  }, [registerTexts, activeRegister, shape, laidOutVariants]);

  /**
   * The register being written right now, or null when nothing is.
   *
   * Straight off `registerLoading` — the browser now knows which tab it asked
   * for, where before it had to infer it from which tag had most recently
   * appeared in a three-part answer. It drives the same pulsing dot AI Prompt
   * puts on a tab that is being written, which is what tells a reader sitting
   * on Slang that their tab is coming rather than empty.
   */
  const writingRegister: Variant['key'] | null = registerLoading;

  /**
   * The language the Registers answer is IN, and whether the output language is
   * a different one. Same language (English text, English output) means there
   * is nothing to translate, and the card is not drawn at all.
   */
  const registerSourceLang = inputLang?.code ?? (from !== 'auto' ? from : '');
  const wantsRegisterTranslation =
    mode === 'native' && !!to && !!registerSourceLang &&
    to.split('-')[0] !== registerSourceLang.split('-')[0];

  /**
   * Translate the open register once it has finished arriving.
   *
   * An effect rather than a step of `loadRegister`, because what it needs is
   * the finished text: translating a version that is still streaming would
   * translate half a message, and then again for the other half. A plain
   * Translate request, with the summary and the learner notes stripped off the
   * end of whatever comes back — this card is the message and nothing else.
   */
  useEffect(() => {
    if (!wantsRegisterTranslation) return;
    const key = activeRegister;
    const source = registerTexts[key];
    if (!source || !source.trim() || registerLoading === key) return;
    if (registerTranslations[key] !== undefined || registerTranslating === key) return;
    if (registerTranslationFailed[key]) return;
    // Above the first level: only when this register's card was asked for.
    if (manual && registerTranslationAsked !== key) return;

    registerTranslationRun.current?.abort();
    const controller = new AbortController();
    registerTranslationRun.current = controller;
    setRegisterTranslating(key);

    let raw = '';
    const fail = () => {
      if (controller.signal.aborted) return;
      setRegisterTranslating((cur) => (cur === key ? null : cur));
      setRegisterTranslationFailed((prev) => ({ ...prev, [key]: true }));
      setRegisterTranslations((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    };
    void translateStream(
      {
        text: source.trim(), from: registerSourceLang, to,
        // Plain Translate, told which register it is translating so that the
        // translation stays in it: without this the formal version came back
        // as "¿Podrías…?", which is the friendly one. `register` on a Translate
        // request means only this (backend lib/modes.ts); the Formal and Casual
        // translate modes would do it too, but an operator can switch those off.
        mode: 'translate', register: key,
        overview: false, level: sendLevel, signal: controller.signal,
      },
      {
        onDelta: (chunk) => {
          raw += chunk;
          const body = splitNotes(splitOverview(splitSourceOverview(raw).body).body).body;
          setRegisterTranslations((prev) => ({ ...prev, [key]: body.trim() }));
        },
        onDone: () => {
          if (controller.signal.aborted) return;
          setRegisterTranslating((cur) => (cur === key ? null : cur));
        },
        onError: fail,
        onLimit: fail,
      },
    );
    // Not aborted on cleanup: the effect re-runs on every streamed chunk (its
    // own state is in the list), and the guards above are what keep it to one
    // request per tab. A changed question aborts it through `registerKey`.
  }, [
    wantsRegisterTranslation, activeRegister, registerTexts, registerLoading,
    registerTranslations, registerTranslating, registerTranslationFailed, registerSourceLang, to,
    manual, registerTranslationAsked, sendLevel,
  ]);

  /** The card under the answer: the open register in the output language, or null. */
  const registerTranslationCard = useMemo(() => {
    if (!wantsRegisterTranslation || !registerTexts[activeRegister]) return null;
    const body = registerTranslations[activeRegister];
    const pending = registerTranslating === activeRegister || registerLoading === activeRegister;
    if (!body && !pending) return null;
    return { body: body ?? '', pending: pending && !body };
  }, [wantsRegisterTranslation, registerTexts, activeRegister, registerTranslations, registerTranslating, registerLoading]);

  /*
   * …and the same three facts where the scheduler can reach them. Written on
   * every render, read only from inside a timer. See `autoStyle`.
   */
  autoRegister.current = {
    id: activeRegister,
    // "Nobody has asked for this one yet." Both halves matter: a tab already
    // written must not be paid for twice, and a tab being written right now
    // must not be started again by the countdown still running behind the click
    // that started it.
    pending:
      registerTexts[activeRegister] === undefined && registerLoading !== activeRegister,
    write: loadRegister,
  };

  /**
   * The answer as plain text: what Copy puts on the clipboard and what Swap puts
   * back in the input box.
   *
   * One value rather than each of those reaching into the split themselves,
   * because they must agree — and because neither of them may ever see a
   * `[[FORMAL]]` tag, which is exactly what happened when Copy read the raw
   * output.
   */
  const answerText = useMemo(
    () =>
      // The laid-out text, not the raw one. Copy and Swap must hand over what
      // the pane is showing — an answer pasted into a chat with its paragraphs
      // stripped back out would be a different thing from the one that was read
      // before pressing the button.
      // A tabbed answer hands over the tab that is OPEN, and only that one. The
      // reader picked it; copying all of them — tags and all, which is what the
      // raw output carries — would hand over two prompts and a piece of our
      // wire format.
      // Translate hands over whichever of its two tabs is open, on the same
      // rule: the reader chose that reading, and it is the one they are about
      // to paste. Falls through to the faithful answer while the AI tab is
      // still being written, so Copy is never empty mid-stream.
      // Grammar Fix hands over the corrected text only: the FIXES list is a card
      // ABOUT the text, drawn under it, and a pasted prompt with "what was
      // wrong" stuck to its end is not the prompt anybody meant to send.
      onAi && laidOutAi
        ? laidOutAi.text
        : activeStyleText
        ? styleMain
        // Registers hands over the OPEN register and only that one, for the
        // same reason: the reader picked it, and it is the message they are
        // about to send. It used to join all three with blank lines between
        // them, which was right when all three were on screen as cards and is
        // wrong now that one is.
        : activeVariant
          ? activeVariant.text
          // A whole answer with no version under the open tab: the pane shows
          // none of them, so there is nothing to copy — never all of them
          // joined, which is a text nobody was shown.
          : laidOutVariants
            ? ''
            : laidOut.text,
    [onAi, laidOutAi, activeStyleText, styleMain, activeVariant, laidOutVariants, laidOut],
  );

  /**
   * The hold layer has something under it worth reading, so it stands aside.
   *
   * With live drafts on this is nearly always true: the pane fills up within a
   * second of somebody starting to type, and from then on the most useful thing
   * on the page is underneath the character. The full-size layer is for the one
   * case it was designed for — an empty pane, the first answer of the session,
   * nothing yet to cover.
   *
   * One constant, read twice: the layer uses it to choose its shape and the
   * answer block uses it to reserve the height that shape needs. Computing it
   * separately in the two places is how the strip ends up either floating over
   * a gap or sitting on the answer.
   */
  const holdCompact = !!answerText.trim();

  /**
   * The answer the reader is actually looking at, laid out.
   *
   * ⚠️ IT HAS TO BE THE PANE ON SCREEN, and it was not: this read the links of
   * `laidOutVariants[0]` — the FORMAL register — whatever tab was open, so
   * hovering the third sentence of the casual version marked whatever the
   * formal version's third sentence had been translated from. Wrong whenever
   * the two versions happened to split differently, which is most of the time,
   * and no mark at all when the open tab had more sentences than the first one.
   *
   * One expression, so the pane that renders the text and the pane the marks
   * are taken from cannot come apart again. Mirrors the choice made further
   * down where `LinkedAnswer` is drawn.
   */
  const shownAnswer = useMemo(
    () =>
      // Same rule as the tab-aware branch above, and for the same reason it was
      // written: the marks have to be read off the pane that is ON SCREEN, or
      // hovering a sentence of the AI answer lights up whatever the faithful
      // answer's sentence at that index had been translated from.
      onAi && laidOutAi ? laidOutAi : mode === 'native' ? activeVariant : laidOut,
    [onAi, laidOutAi, mode, activeVariant, laidOut],
  );

  /**
   * The run of source text the hovered piece of the answer was translated from.
   */
  const linkedSource = useMemo(() => {
    if (linked === null) return null;
    return shownAnswer?.links?.[linked] ?? null;
  }, [linked, shownAnswer]);

  /*
   * Forget the hovered piece when the answer underneath changes.
   *
   * `linked` is an INDEX into the links of whatever was on screen. Switch
   * register, retranslate, or let one more streamed chunk arrive, and index 3
   * is a different sentence — but no `mouseleave` fires, because the pointer
   * has not moved, so the mark just silently starts pointing somewhere else.
   * Clearing it on every change of the text costs one highlight the reader has
   * to re-hover for, and buys never marking the wrong line.
   */
  useEffect(() => { setLinked(null); }, [shownAnswer?.text]);

  /**
   * What the two cards actually draw: whatever has arrived, or failing that
   * whatever arrived last. Empty only before the first answer of the session.
   */
  const shownOverview = useMemo(() => {
    // An empty box has no summary to be out of date about. Held text would
    // otherwise sit over an empty pane describing something that is no longer
    // there — the one case where showing the previous one is not a kindness.
    if (!text.trim()) {
      lastOverview.current = { target: '', source: '' };
      return { target: '', source: '' };
    }
    if (overviewText) lastOverview.current.target = overviewText;
    if (sourceOverview) lastOverview.current.source = sourceOverview;
    // Held for as long as there is text in the box, not merely while a request
    // is running.
    //
    // "While busy" was the first rule and it was not enough, because the answers
    // that arrive WITHOUT a summary are now the common case rather than a model
    // failure: an answer assembled out of remembered sentences never went near a
    // model, so it has no summary attached and never will. Under the narrower
    // rule the cards blinked out on exactly the translations that were supposed
    // to be the smooth ones — measured at 203px of page height disappearing and
    // coming back while typing.
    //
    // What is held is a sentence about text that has since gained or lost a
    // sentence of its own, which is the same compromise the pane below already
    // makes with the previous translation, and it lasts until the next answer
    // that has a summary of its own.
    return {
      target: overviewText || lastOverview.current.target,
      source: sourceOverview || lastOverview.current.source,
    };
  }, [text, overviewText, sourceOverview]);

  const clean = isNoMistakes(outputBody);

  /**
   * The source language the swap would move INTO the target slot.
   *
   * On "Detect language" that is whatever the detector resolved to, and there is
   * nothing to swap until it has. Validated against the language list, because
   * the detector and the picker can both name a code the list no longer has, and
   * a target the server rejects comes back as a red error on a button press that
   * looked perfectly ordinary.
   */
  const swapSource = useMemo(() => {
    const code = from === 'auto' ? detected?.code ?? null : from;
    if (!code) return null;
    return languages.some((l) => l.code === code) ? code : null;
  }, [from, detected, languages]);

  /**
   * Why the swap button is not available, in the words the tooltip should use —
   * or null when it is.
   *
   * Every line of this was a way the button could be pressed and then fail. It
   * moves the ANSWER into the input box, so it needs an answer that is finished,
   * non-empty, and short enough to be one: a Mistakes report or a long
   * translation can exceed the 5,000-character input limit, and sending it back
   * produced a red "text is too long" error out of nowhere. Saying so on a
   * disabled button is the same information, before the click instead of after.
   */
  const swapBlocked = useMemo(() => {
    if (busy || countdown !== null) return 'Waiting for the translation to finish';
    if (!swapSource) {
      return text.trim()
        ? 'Waiting for language detection'
        : 'Pick the input language first — there is nothing to detect yet';
    }
    // Swap moves the ANSWER into the box, and the box holds a document rather
    // than a page. Doing it here would either throw the other pages away or
    // leave a document half in one language and half in the other; refusing,
    // with the reason on the button, is the only honest option of the three.
    if (pages.length > 1) return `This text is ${pages.length} pages — swap works on one page of text`;
    // Both sides already the same language. Pressing swap here leaves the app in
    // exactly the state it was in, with an output pane that now says "nothing to
    // translate" — a button that appears to break the translator when all it did
    // was exchange a language for itself.
    if (swapSource === to) return 'Both sides are already the same language';
    // Both boxes empty: swap the LANGUAGES alone, which is what anyone pressing
    // it before typing means (and what every other translator does). Refusing
    // here made the button look broken on phones, where the tooltip that said
    // "Nothing to swap yet" can never be seen.
    if (!text.trim() && !answerText.trim()) return null;
    if (error || limited) return 'Nothing to swap yet';
    if (!answerText.trim()) return 'Nothing to swap yet';
    if (answerText.length > pageSize) return 'This answer is too long to put back in the box';
    return null;
  }, [busy, countdown, swapSource, error, limited, answerText, pages.length, pageSize, to, text]);


  /**
   * Is what is on screen still on its way?
   *
   * `NO_MISTAKES` streams a token at a time like everything else, so `NO_MIS`
   * would be painted as the translation for a frame or two before the rest of
   * the word arrived and it became the thumbs-up. Held back until the word is
   * whole, and then it is drawn once, as itself.
   *
   * The other half of the same problem — a finished sentinel from the PREVIOUS
   * question still on screen while a new one runs — is handled in `run`, which
   * clears it before starting.
   *
   * A genuine translation that happens to be the text "NO" is delayed by one
   * chunk by this. That is the whole cost.
   */
  const pending = busy && isNoMistakesPartial(outputBody);

  /**
   * The cards under the answer.
   *
   * Two sources, and they are not the same thing:
   *
   *   - what the MODE produced. The CEFR modes write their own glossary, and that
   *     section is part of the answer whether or not any switch is on.
   *   - what the OVERVIEW switch produced. It arrives with every answer (see
   *     `run`) but is only drawn when the switch is on, which is what makes
   *     that button instant.
   *
   * Not shown for a clean Mistakes check: the answer there is "nothing to fix",
   * and hanging a vocabulary lesson off it turns a one-glance result back into
   * a page of reading.
   *
   * A mode's own section writes its heading as its first line (e.g.
   * "💎 WORDS THAT CAN CONFUSE"), lifted out so the card gets a real header row
   * to hang its copy button on. The switch's notes carry no heading — asking
   * the model for one got the first ITEM drawn as the title half the time — so
   * the card titles itself, in the app's own language like the rest of the
   * chrome.
   */
  const noteCards = useMemo(() => {
    // An empty box has no answer for notes to belong to — the same guard
    // `shownOverview` needs, for the same reason.
    if (!text.trim()) { lastNotes.current = []; return []; }
    if (clean) return [];
    const cards: { key: string; head: string; body: string; section: string }[] = [];

    // An answer with no notes section is either one still arriving or one
    // assembled from remembered sentences, and in both cases the pane below is
    // showing text the previous notes still describe. Dropping the card there
    // leaves half of one answer on screen and takes ~100px out of the page under
    // it. See `shownOverview` — same rule, same reason.
    if (!modeNotes && !taughtNotes && lastNotes.current.length) return lastNotes.current;

    if (modeNotes) {
      const [head, ...rest] = modeNotes.split('\n');
      cards.push({ key: 'mode', head: head.trim(), body: rest.join('\n').trim(), section: modeNotes });
    }
    // Both can be present and that is fine — a Mistakes report keeps its
    // REWRITE section in one card and the explained expressions in another.
    //
    // Two different sections arrive under the same tag, and only one of them
    // belongs to the switch:
    //
    //   • a mode that writes its OWN notes (the CEFR modes' glossary) is part of
    //     that answer and is drawn whether or not the switch is on. It used to
    //     be marked by an ALL-CAPS heading, which stopped working the day those
    //     modes started answering in Korean — ALL-CAPS is a Latin idea — so it
    //     moved onto this tag and can no longer be told apart by shape.
    //   • the switch's notes ride along with EVERY answer whether it is on or
    //     off (see the note in `run`: the request never changes, only the
    //     drawing does), so they must stay gated on it or the button stops
    //     doing anything visible.
    if (taughtNotes && (overview || activeMode?.writesOwnNotes)) {
      cards.push({
        // A mode that writes its own notes is explaining the ORIGINAL wording —
        // that is its glossary. The switch's notes explain the answer.
        key: 'taught',
        head: activeMode?.writesOwnNotes ? '💎 Words that can confuse' : '💡 Notes',
        body: taughtNotes,
        section: taughtNotes,
      });
    }

    // A section with nothing in it is the model saying "nothing worth
    // explaining" in the only way the format lets it. Not a card.
    const shown = cards.filter((c) => !!c.body);
    lastNotes.current = shown;
    return shown;
  }, [text, modeNotes, taughtNotes, overview, activeMode, clean]);

  const hasNotes = noteCards.length > 0;

  const over = text.length > pageSize;

  /**
   * Flipping the switch.
   *
   * The two variants are different questions to the model, so this behaves like
   * any other setting change: the scheduler sees `text` unchanged and fetches at
   * once rather than waiting. Whichever variant has been seen before comes back
   * from the memo for nothing, so toggling back and forth costs one call each
   * way and then nothing.
   */
  const toggleOverview = useCallback(() => {
    setOverview((v) => {
      const next = !v;
      saveOverview(next);
      return next;
    });
    // Pressing it — even to turn it straight off again — is the moment the
    // question the sparkle exists to answer has been answered.
    setOverviewSeen((seen) => {
      if (!seen) saveOverviewSeen();
      return true;
    });
  }, []);

  /**
   * The sparkle window.
   *
   * Bounded rather than permanent: an invitation that never stops is a
   * distraction, and this one has to share a screen with text people are trying
   * to read. Six seconds is long enough to be noticed after an answer lands and
   * short enough that it is gone before it becomes furniture.
   */
  useEffect(() => {
    if (!justTranslated) return;
    const t = setTimeout(() => setJustTranslated(false), 6000);
    return () => clearTimeout(t);
  }, [justTranslated]);

  // Off, never used, and something has just arrived worth summarising.
  const overviewAttention = justTranslated && !overview && !overviewSeen && !busy;

  /**
   * The pages whose translation is already in hand.
   *
   * Read straight out of the memo rather than tracked separately, because the
   * memo IS the answer to the question the dots are asking — "will this page
   * cost anything" — and a second list of visited pages could disagree with it
   * after a language change, a mode change or an eviction. Recomputed when the
   * memo gains an entry (`memoVersion`) or when the question changes.
   */
  const donePages = useMemo(() => {
    const found = new Set<number>();
    if (pages.length < 2) return found;
    pages.forEach((p, i) => {
      if (p.text.trim() && memo.current.has(memoKey(p.text, from, to, mode))) found.add(i);
    });
    return found;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, from, to, mode, memoKey, memoVersion]);

  /* ── THE NOTEBOOK ─────────────────────────────────────────────────────────*/

  /**
   * The panes as one saveable thing.
   *
   * Three decisions are made here and each of them is the difference between a
   * record and a souvenir:
   *
   *   • **`doc`, not `text`.** The box pages a long document at 5,000
   *     characters and `text` is the page being looked at. Saving that would
   *     file page 3 of somebody's contract under the title of page 3 and lose
   *     the rest, which is the sort of thing nobody notices until they need it.
   *
   *   • **The tabs travel.** Registers and AI Prompt are one request per tab,
   *     so the answer somebody has in front of them is spread across
   *     `registerTexts` / `styleTexts` and `output` holds at most one of them.
   *     Every tab that has been written goes into the entry, and the tab they
   *     were reading is recorded as the one to open.
   *
   *   • **`answerText`, not `output`.** The same reason Copy uses it: nobody
   *     should find `[[OVERVIEW]]` or `[[FORMAL]]` in their own notebook.
   */
  const snapshot = useCallback(() => {
    const tabs =
      mode === 'native'
        ? (registerTexts as Record<string, string>)
        : mode === 'ai-prompt'
          ? styleTexts
          // Translate keeps every reading that was written, but only once an
          // extra tab has been. An entry saved without ever opening one is
          // a plain one-answer row, exactly as it was before the tab existed —
          // a `tabs` map with one key in it would put a lone tab above every
          // translation in the notebook for nothing.
          : showAiTabs && EXTRA_TABS.some((k) => aiTexts[k]?.trim())
            ? { standard: laidOut.text, ai: aiTexts.ai ?? '', easy: aiTexts.easy ?? '' }
            : null;
    const written = tabs
      ? Object.fromEntries(Object.entries(tabs).filter(([, v]) => !!v && !!v.trim()))
      : null;
    const activeTab =
      mode === 'native'
        ? activeRegister
        : mode === 'ai-prompt'
          ? activeStyleId
          : showAiTabs && EXTRA_TABS.some((k) => aiTexts[k]?.trim())
            ? (openExtra && aiTexts[openExtra]?.trim() ? openExtra : 'standard')
            : null;
    return {
      mode,
      // What the LANGUAGE BAR says, which is not always what the picker holds:
      // `from` is 'auto' for a detected language, and an entry that recorded
      // 'auto' would open with no flag on either side of it.
      sourceLang: inputLang?.code ?? (from === 'auto' ? 'en' : from),
      sourceLangDetected: from === 'auto',
      targetLang: followsInput ? inputLang?.code ?? to : to,
      sourceText: doc,
      targetText: answerText,
      // The summary the Overview switch produced, if there is one. It names the
      // entry and draws its card — see the field's note in lib/notebook.ts.
      // `shownOverview` rather than the raw split, because that is the sentence
      // actually on screen: it holds the last real summary across answers
      // assembled from remembered sentences, which carry none of their own.
      overview: shownOverview.target || shownOverview.source || null,
      tabs: written && Object.keys(written).length ? written : null,
      activeTab,
    };
  }, [
    mode, registerTexts, styleTexts, activeRegister, activeStyleId,
    showAiTabs, aiTexts, openExtra, laidOut,
    inputLang, from, to, followsInput, doc, answerText, shownOverview,
  ]);

  /**
   * ── THE TWO WAYS IN, AND WHY THEY DIFFER ──────────────────────────────────
   *
   * **The rail button (and Ctrl+7, its number) KEEP what is on screen — in a
   * folder you pick.** A personal notebook is somewhere you file things on
   * purpose, so the gesture holds the translation and ASKS where rather than
   * dropping it somewhere by default. With an empty box there is nothing to keep,
   * so it opens the notebook to browse instead — the rail button is also the only
   * way in for anyone not on the keyboard, and must never be a dead end.
   *
   * **Ctrl+Alt+Z opens it and saves nothing.** The browsing door: going back to
   * find last week's phrasing must not file today's half-written draft on the way.
   *
   * Keeping the same thing twice is safe: `findSame` on the server refreshes the
   * row that is already there rather than filing a second copy of it.
   */
  /** Held between pressing Ctrl+7 and choosing a folder, so what is saved is
      exactly what was on screen at the gesture, not whatever it drifted to while
      the picker was open. */
  const pendingSnap = useRef<ReturnType<typeof snapshot> | null>(null);

  const registerToNotebook = useCallback(() => {
    const snap = snapshot();
    if (!snap.sourceText.trim() || !snap.targetText.trim()) {
      // Nothing worth keeping — just open the notebook to look around.
      setNotebookEntry(null);
      setNotebookOpen(true);
      return;
    }
    pendingSnap.current = snap;
    setSaveError(null);
    setSavePickerOpen(true);
  }, [snapshot]);

  /** The picker chose (or just made) a folder: file the held snapshot there. */
  const saveSnapTo = useCallback(async (folderId: string) => {
    const snap = pendingSnap.current;
    setSavePickerOpen(false);
    if (!snap) return;
    setSavingEntry(true);
    setSaveError(null);
    try {
      const { entry, folderName, duplicate } = await saveEntry({ ...snap, folderId });
      saveLastFolder(entry.folderId);
      setSaved({ id: entry.id, folder: folderName, duplicate });
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSavingEntry(false);
    }
  }, []);

  /** Ctrl+Alt+Z and the picker's "Browse": the browsing door. Opens, files nothing. */
  const openNotebook = useCallback(() => {
    setNotebookEntry(null);
    setNotebookOpen(true);
  }, []);

  // …and where the Ctrl+7 handler above can reach it. See `notebookAction`.
  notebookAction.current = registerToNotebook;

  /*
   * Ctrl+Alt+Z — open the notebook, file nothing.
   *
   * Ctrl+S is the obvious chord for a notebook and it is taken: the browser's
   * own Save Page, on every platform, and a page cannot have it. Ctrl+1…9
   * belong to the tab strip, which is the caveat the mode badges already carry.
   * Ctrl+Alt+Z is claimed by nothing on Windows, macOS or Linux and is reachable
   * with one hand from the home row.
   *
   * The guards are the ones every window-level chord on this page keeps:
   * nothing behind the identity wall, nothing while another dialog owns the
   * screen, and nothing from inside a different input — a chord pressed with
   * the caret in the feedback box is about that box.
   */
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      // `e.code`, not `e.key`: with Alt down some layouts report a dead key or
      // a different character for Z entirely, and the physical key is what the
      // chord is described as.
      if (!(e.ctrlKey || e.metaKey) || !e.altKey || e.code !== 'KeyZ') return;
      if (!notebookAllowed) return;
      if (voiceOpen || imageOpen || feedbackOpen || notebookOpen) return;
      if (document.querySelector('[data-gt-modal="mandatory"]')) return;
      const el = e.target as HTMLElement | null;
      if (el && el !== inputRef.current && /^(INPUT|TEXTAREA)$/.test(el.tagName)) return;
      if (el && el.isContentEditable) return;
      e.preventDefault();
      openNotebook();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openNotebook, notebookAllowed, voiceOpen, imageOpen, feedbackOpen, notebookOpen]);

  /** The toast lets go on its own. An error waits to be read. */
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(null), 5000);
    return () => clearTimeout(t);
  }, [saved]);

  /**
   * An entry put back into the panes.
   *
   * ── Why nothing is re-translated ────────────────────────────────────────
   * Loading an entry changes the text, the languages and the mode all at once,
   * and every one of those is a "deliberate act" as far as the scheduler is
   * concerned — so without this the dialog would close onto a paid request for
   * an answer that is sitting in the entry being loaded. The answer is put into
   * the memo FIRST, under the key the scheduler is about to compute, so its
   * `serveFromMemo` hits and the pane fills from memory.
   *
   * The tabbed modes need the same thing in their own caches, and they cannot
   * be seeded here: `registerKey` / `styleKey` empty them during the very
   * render the new text lands on. So the entry is parked in a ref and the
   * effect below plants the tabs once that has happened.
   */
  const pendingLoad = useRef<NotebookEntry | null>(null);

  const loadEntry = useCallback(
    (entry: NotebookEntry) => {
      const whole = entry.sourceText.slice(0, MAX_DOC);
      const page0 = paginate(whole, pageSizeRef.current)[0]?.text ?? whole;
      // The plain path's answer, under the FIRST level's key (no level tag): a
      // saved entry is not an answer bought at a higher level, so it must never
      // be served as one. At the first level the scheduler finds it there.
      if (entry.targetText.trim()) {
        remember(memoKey(page0, entry.sourceLangDetected ? 'auto' : entry.sourceLang, entry.targetLang, entry.mode, true),
                 entry.targetText);
        // Above the first level it is still shown — it is the saved translation,
        // and showing it costs nothing — but the pane keeps its Translate
        // button, for the version at the level that is chosen.
        if (manual) {
          inflight.current?.abort();
          inflightKey.current = null;
          setOutput(entry.targetText);
          setBusy(false);
          setStat(null);
        }
      }

      pendingLoad.current = entry;
      setDoc(whole);
      setPage(0);
      /*
       * `applyFrom`/`applyTo` with `fromMenu`, not a bare `setFrom`/`setTo`.
       *
       * The bar shows three tabs, and an entry's languages are very often not
       * among them — that is half of why somebody goes looking for an old
       * translation. A bare set left the pair correct in state and INVISIBLE on
       * the bar: an English→Korean entry came back with neither half lit, which
       * reads as the load having half-worked. `fromMenu` is the same promotion a
       * language picked out of the ▾ menu gets, which is the closest thing to
       * what has just happened.
       */
      applyFrom(entry.sourceLangDetected ? 'auto' : entry.sourceLang, true);
      applyTo(entry.targetLang, true);
      setMode(entry.mode);
      setCaret(page0.length);
      setDetected(entry.sourceLangDetected ? { code: entry.sourceLang, name: entry.sourceLang } : null);
      setError(null);
      setLimited(null);
      // Not a draft and not a request: the box is being filled from a record.
      noDraft.current = true;
      setNotebookOpen(false);
      setSaved(null);
    },
    [memoKey, remember, applyFrom, applyTo, manual],
  );

  /*
   * The tabs, planted on the render after the text has changed.
   *
   * Runs on every commit and does nothing on almost all of them — the ref is
   * the whole guard, and it is cleared the first time the new text is actually
   * in `doc`. Checking the text rather than trusting the ordering is what makes
   * this safe against a render landing in between.
   */
  useEffect(() => {
    const pend = pendingLoad.current;
    if (!pend) return;
    if (canonical(doc) !== canonical(pend.sourceText.slice(0, MAX_DOC))) return;
    pendingLoad.current = null;
    if (!pend.tabs) return;
    if (pend.mode === 'native') {
      setRegisterTexts(pend.tabs as Partial<Record<Variant['key'], string>>);
      if (pend.activeTab) setRegisterTab(pend.activeTab as Variant['key']);
    } else if (pend.mode === 'ai-prompt') {
      setStyleTexts(pend.tabs);
      if (pend.activeTab) setStyleTab(pend.activeTab);
    }
  });

  /*
   * ── Translate, above the first level ──────────────────────────────────────
   *
   * Whether the pane is waiting on the button: there is text, and what is
   * under the open tab is not the answer to it yet. Per mode, because each
   * mode keeps its answer in a different place.
   */
  const manualWaiting = (() => {
    if (!manual || !text.trim() || identity || busy) return false;
    if (mode === 'ai-prompt') {
      return !!activeStyleId && styleTexts[activeStyleId] === undefined && styleLoading !== activeStyleId;
    }
    if (mode === 'native') {
      return registerTexts[activeRegister] === undefined && registerLoading !== activeRegister;
    }
    if (shownKey !== memoKey(text, from, to, mode)) return true;
    // The faithful answer is in; the open Software-AI / Easy tab is next.
    return onAi && aiText === null && !aiBusy;
  })();
  /** "≈ N KHARMA" for one translation of this page at this level, or the guest / no-KHARMA wording. */
  const manualPrice =
    priceMode === 'none' || !levelInfo || !aiPricing
      ? ''
      : priceMode === 'free'
        ? 'free translation'
        : `≈ ${quoteLevel(aiPricing, levelInfo, text, true).toLocaleString()} KHARMA`;
  /** The register's card above the first level: offered, not written, until asked. */
  const registerTranslationOffer =
    manual && wantsRegisterTranslation && !!registerTexts[activeRegister] &&
    registerLoading !== activeRegister && registerTranslations[activeRegister] === undefined &&
    registerTranslating !== activeRegister && registerTranslationAsked !== activeRegister;
  const toLangName = languages.find((l) => l.code === to)?.name ?? to;

  const srcPinned = usePinned(srcBlock, srcPane);
  const outPinned = usePinned(outBlock, outPane);

  return (
    // The extra bottom padding is for the floating page bar: it is fixed, so it
    // is outside the flow and would otherwise sit on top of the last row of the
    // page at the end of a scroll. Only paid for when there is a bar.
    <div className={'flex flex-col gap-4 ' + (pages.length > 1 ? 'pb-28' : '')}>
      <LanguageTabs
        languages={languages}
        from={from}
        to={to}
        sourceTabs={sourceTabs}
        targetTabs={targetTabs}
        detected={detected}
        lockedTarget={lockedTarget}
        followsInput={followsInput}
        alsoTranslates={mode === 'native'}
        inputLangName={inputLang?.name ?? null}
        swapBlocked={swapBlocked}
        onFrom={pickFrom}
        onTo={pickTo}
        onSwap={swap}
        overview={overview}
        overviewAttention={overviewAttention}
        onOverview={toggleOverview}
        levelPicker={aiPricing?.live && level ? (
          <LevelPicker
            pricing={aiPricing}
            level={level}
            guest={!authUser}
            text={text}
            onPick={pickLevel}
            price={priceMode}
            freeLeft={!authUser && guestCredits && guestCredits.total > 0 ? guestCredits.remaining : null}
          />
        ) : undefined}
      />

      {limited && (
        <div
          role="status"
          className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900
                     dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
        >
          {limited}
        </div>
      )}

      {/* input | mode column | output — the same 1fr/132px/1fr template as the
          language bar above, so every control sits over the pane it affects. */}
      {/* NOT `overflow-hidden`. It was there to clip the children to the rounded
          corners, but it also clipped the mode tooltips at the card edge — the
          same trap as the language panel. The two outer panes round their own
          corners instead, so the card still looks right and nothing inside it
          gets cut off. */}
      {/*
        No fill and no shadow — a hairline box on the page, which is what
        translate.google.com draws. It used to be a white card with a shadow,
        floating over an off-white page; the two greys were close enough that the
        card read as a slightly dirty rectangle rather than as a container, and
        in the dark theme the lift was what made the whole thing look grey rather
        than dark. The only filled surface here is the output half.
      */}
      <div
        className="grid grid-cols-1 rounded-2xl border border-ink-200
                   md:grid-cols-[1fr_64px_1fr] dark:border-ink-700"
      >
        {/* Source.
            The pane no longer stretches its own contents to fill the row: the
            block inside is measured at its natural height so it can be pinned
            (see usePinned). Clicking the empty area below it therefore has to
            move focus explicitly — that used to come free from a textarea that
            filled the pane. */}
        <div
          ref={srcPane}
          /*
            A click puts the caret in the box, and a Ctrl-click does too.

            The numbered picker is opened by PRESSING CTRL — see the effect
            above — so there is nothing for this handler to toggle. It briefly
            did, and that was a misreading: holding the key and clicking is a
            different gesture from pressing the key, and only the second one is
            what was asked for. Ctrl-clicking into the box still just puts the
            caret where you clicked, the way it always has.
          */
          onClick={() => inputRef.current?.focus()}
          className="min-h-[340px] rounded-t-2xl border-b border-ink-200 p-5
                     md:rounded-l-2xl md:rounded-tr-none md:border-b-0 md:p-7 dark:border-ink-700"
        >
          <div ref={srcBlock} className={srcPinned ? 'md:sticky md:top-4' : undefined}>
            {/*
              The same summary, over the box, in the language you wrote in.

              This is the half of the screen guaranteed to be in a language the
              reader reads, which is exactly why the card belongs here as well:
              the question it answers is "did it understand what I said?", and
              that has to be answerable BEFORE trusting a translation you cannot
              check yourself. Someone sending Korean to a client in English gets
              a Korean sentence here saying what the English says.

              Only the OVERVIEW, never the notes. The notes explain vocabulary in
              the ANSWER — over the box they would be explaining words back to
              the person who wrote them.

              `sourceOverview || overviewText` is not a fallback for a missing
              card; it is the same card. When the two languages match — every
              mode that answers in the language it was given — the backend does
              not ask for the sentence twice, because the overview it already has
              IS in the input language.
            */}
            {overview && !error && !identity && (shownOverview.source || shownOverview.target) && (
              <OverviewCard text={shownOverview.source || shownOverview.target} className="mb-4" />
            )}
            {/* The floor keeps an empty box looking like a box, and keeps the
                counter under it roughly where it sat when the textarea filled
                the whole pane. */}
            {/* `relative` anchors the clear button below. The gutter it sits in
                is reserved by `.grow-wrap` in globals.css rather than here,
                because the padding has to land on the hidden mirror too. */}
            <div
              className="grow-wrap relative min-h-[15rem] text-[19px] leading-relaxed"
              data-value={text}
            >
              {/*
                Clear, where every translator puts it: top right, inside the box —
                with Copy just to its left.

                Only rendered when there is something to clear — a permanently
                visible × over an empty box is a control that does nothing, and
                the first thing anyone does with one is press it to find out.

                The pane's own click handler focuses the textarea, which is what
                should happen after clearing anyway, so this deliberately does
                not stop the event: click, and you are back in an empty box ready
                to type. `onMouseDown` preventDefault keeps the caret from
                flickering out of the textarea and back on the way.
              */}
              {text && (
                <span className="absolute right-0 top-0 z-10 flex items-center gap-0.5">
                {/* Copy, to the left of Clear: the whole text in the box, every
                    page of it — the same "whole document" Clear empties. */}
                <CopyButton iconOnly text={doc} label="Copy text" />
                <button
                  type="button"
                  aria-label="Clear text"
                  title="Clear"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    // The whole document, not the page on screen. A × that left
                    // nine pages behind the one it emptied would be the worst
                    // kind of surprise — the box looks cleared and is not.
                    clearAll();
                    setClipped(null);
                    setCaret(0);
                    setDismissed(null);
                    undo.current = null;
                    // Clearing while dictating empties the box without closing
                    // the microphone — so the anchor has to follow the text it
                    // is an offset into, or the next words would be spliced
                    // past the end of an empty string.
                    if (dictation.current) dictation.current = { base: '', at: 0 };
                    inputRef.current?.focus();
                  }}
                  className="flex h-8 w-8 items-center justify-center
                             rounded-full text-ink-400 transition hover:bg-ink-100 hover:text-ink-700
                             dark:hover:bg-ink-800 dark:hover:text-ink-100"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
                    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                  </svg>
                </button>
                </span>
              )}
              {/*
                The other half of the sentence link.

                A textarea cannot colour part of its own value, so the mark is
                painted on a copy of the text sitting in the SAME grid cell,
                underneath. Everything in that copy is transparent except the one
                highlighted run, so what shows through is a coloured rectangle
                behind the real characters — and because the copy inherits the
                wrapper's font, wrapping and right-hand gutter (see .grow-wrap in
                globals.css, which the copy is joined to), the rectangle lands
                exactly on the line it is marking however the text wraps.

                Only rendered while something is hovered: the cost is a second
                copy of a 5,000-character string in the DOM, which is not worth
                carrying for the 99% of the time nothing is highlighted.
              */}
              {linkedSource && (
                <div className="gt-link-layer" aria-hidden>
                  {text.slice(0, linkedSource.src.start)}
                  <mark>{text.slice(linkedSource.src.start, linkedSource.src.end)}</mark>
                  {text.slice(linkedSource.src.end)}
                </div>
              )}
              {/*
                No `maxLength` on this element, deliberately.

                It was the whole of the old limit and the whole of the old
                problem: the browser dropped everything past 5,000 characters on
                the floor without a word, on a paste of a document somebody had
                every reason to believe had gone in. The 5,000 is now a PAGE
                boundary rather than a wall — the only cap left is MAX_DOC, and
                that one announces itself in the counter.
              */}
              <textarea
                ref={inputRef}
                className="pane relative"
                rows={1}
                placeholder="Type or paste text…"
                value={text}
                onChange={(e) => {
                  const value = e.target.value;
                  const at = e.target.selectionStart ?? value.length;
                  // Typing while the microphone is open moves the place the
                  // next spoken words belong. Without this re-anchor, dictation
                  // would keep splicing at the offset the caret had when the
                  // button was pressed and overwrite what was typed since.
                  if (dictation.current) dictation.current = { base: value, at };
                  // The edit that lands on a whole-document selection replaces
                  // the whole document — see `selectedAll`. Typing, pasting and
                  // Delete all arrive here, and all three mean the same thing
                  // once everything is selected.
                  if (selectedAll.current && wholeAtEdit.current) {
                    dropSelectAll();
                    replaceWholeDoc(value, at);
                    return;
                  }
                  // Through `editPage`, not `setText`: this is the one edit that
                  // may push characters onto the next page, and the caret has to
                  // go with them.
                  editPage(value, at);
                }}
                // Caret moves that are not edits — clicking into the middle of a
                // word, arrowing back over one — change which segment is active,
                // so the offer has to follow them.
                /*
                  Caret tracking only.

                  ⚠️ THE SELECT-ALL FLAG IS DELIBERATELY NOT CLEARED HERE, and
                  it was to begin with. React's `onSelect` on a textarea is
                  synthesised from the document's `selectionchange`, so it does
                  not fire once, in step, with the selection it describes —
                  measured, it cleared the flag a beat after Ctrl+A had set it,
                  reading a selection that was no longer the one on screen, and
                  select-all went on emptying one page. What the selection was at
                  the instant of the edit is a question with an exact answer, and
                  `beforeinput` is where it is asked.
                */
                onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
                /*
                  A caret the READER moved abandons a half-typed syllable.

                  Every edit the Japanese and Chinese engines make is "delete
                  the pending Latin behind the caret, put this instead". Click
                  into the middle of the paragraph with `zhongg` still pending
                  and that would delete six characters of somebody's sentence.
                  `dropPending` forgets the composition instead — the Latin is
                  already real text and simply stays where it was typed.

                  On the pointer and on blur rather than on `onSelect`, which
                  also fires for the caret moves the IME makes ITSELF: routing
                  this through there cleared the buffer on every keystroke and
                  the pending text never grew past one letter.
                */
                onPointerDown={() => { dropSelectAll(); dropPending(); }}
                onBlur={() => { dropSelectAll(); dropPending(); }}
                /*
                  A paste is finished text, and is answered at once.

                  Flagged here rather than translated here: the paste event fires
                  BEFORE the textarea's value changes, so the text to send does
                  not exist yet. The scheduler picks the flag up on the change
                  that follows.
                */
                onPaste={(e) => {
                  // Every paste is a request — see the note at the top of the
                  // file. Even an unreadable clipboard (some mobile browsers,
                  // some permission states read as '') still put text in the
                  // box, so it is flagged all the same.
                  forceNext.current = true;
                  /*
                    A paste that leaves the box exactly as it was — the same
                    text pasted over itself, usually because the first try
                    showed nothing — changes no state, so the scheduler never
                    runs and the flag above would be read by some later edit
                    instead. Ask directly: `translateNow` serves a held answer
                    for free and otherwise starts the request again.
                  */
                  const before = e.currentTarget.value;
                  setTimeout(() => {
                    if (inputRef.current && inputRef.current.value === before && before.trim()) {
                      forceNext.current = false;
                      translateNow();
                    }
                  }, 0);
                }}
                /*
                  Dropped text is pasted text that came by mouse, and it fires no
                  paste event — so the same question has to be asked of the text
                  itself. The scheduler does that (`jumped`), off the difference
                  between what the box held and what it holds now. Nothing is
                  flagged here: the scheduler's size rule (`jumped`) answers a
                  dropped text at once, the same as a paste.
                */
                /*
                  Copy and cut follow the same selection the edit above does.

                  Without these, Ctrl+A then Ctrl+C put ONE PAGE on the
                  clipboard while the reader believed they had taken the
                  document — the quietest of the three ways this bug showed,
                  and the one that loses work rather than merely surprising
                  somebody.
                */
                onCopy={(e) => {
                  if (!selectedAll.current) return;
                  e.preventDefault();
                  e.clipboardData?.setData('text/plain', doc);
                }}
                /* Cutting is deleting with a clipboard on the side. */
                onCut={(e) => {
                  deleting.current = true;
                  if (!selectedAll.current) return;
                  e.preventDefault();
                  e.clipboardData?.setData('text/plain', doc);
                  dropSelectAll();
                  clearAll();
                }}
                onKeyDown={(e) => {
                  //
                  // The script keyboard, before anything else can claim the key.
                  //
                  // A letter key stops being a letter: `r` is `ㄱ` in Korean,
                  // `к` in Russian, `ρ` in Greek, and part of a syllable still
                  // being spelled in Japanese or Chinese. Which of those it is
                  // depends on the engine, and all four live in `imeKeyDown` —
                  // see it for what each one does with a key it does not want.
                  //
                  // Digits, punctuation, Enter and the space bar usually fall
                  // straight through to the handlers below and keep doing their
                  // jobs. `imeKeyDown` returning true is the signal that this
                  // one did not: the keyboard consumed it.
                  //
                  if (imeKeyDown(e)) return;
                  /*
                    Ctrl/⌘+A — note it and let the browser do its half.

                    NOT prevented: the default selects the page's text, which is
                    what makes the selection visible. All this adds is the fact
                    that the reader meant the whole document, which the next
                    edit, copy or cut reads. Flagged only when there IS more
                    than one page — on a single-page document the browser's own
                    select-all is already the whole of it, and claiming
                    otherwise would put a badge on screen saying nothing.
                  */
                  if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'a' || e.key === 'A')) {
                    if (pages.length > 1) {
                      selectedAll.current = true;
                      setShowSelectedAll(true);
                    }
                    return;
                  }
                  /*
                    Anything that moves the caret instead of editing gives the
                    selection up — the arrows, Home, End, Page keys, Escape.
                    Held Shift is the exception: that is somebody adjusting a
                    selection, not leaving one.
                  */
                  if (
                    selectedAll.current && !e.shiftKey &&
                    (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End' ||
                     e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Escape')
                  ) {
                    dropSelectAll();
                  }
                  // Ctrl/⌘+Enter is handled on the window instead — see the
                  // effect above. It has to work when the caret is NOT in this
                  // box, which is where it used to silently do nothing. It
                  // still calls preventDefault there, so no newline is added.
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') return;
                  //
                  // ENTER DOES NOT TRANSLATE. It breaks the line and nothing
                  // else.
                  //
                  // It used to set `forceNext` here, on the reading that Enter
                  // means "I have finished this thought". Inside a textarea that
                  // is simply wrong: Enter is how you start the next line of a
                  // paragraph you are still writing, so every line break bought a
                  // translation of half a thought and threw the answer on screen
                  // mid-sentence. It also contradicted the rule the wait exists
                  // to keep — the output follows eight seconds after typing
                  // STOPS, and pressing Enter is not stopping.
                  //
                  // Ctrl/⌘+Enter is the deliberate "translate now" and is
                  // untouched (see the window handler above). That chord cannot
                  // be pressed by accident mid-paragraph; a bare Enter is pressed
                  // by accident constantly, because it is not an accident at all
                  // — it is typing.
                  //
                  // Anything that finishes a word commits the conversion — the
                  // space bar, Tab, Enter, a comma, a full stop. See `endsWord`
                  // for which keys those are and why Tab is handled differently
                  // from the rest. The keystroke itself always still does what
                  // it was pressed to do; the word in front of it just arrives
                  // as Korean.
                  //
                  // Committing without being asked is a real risk, and what
                  // answers it is `revert` — one backspace puts the letters
                  // back — plus the card being visible the whole time, so
                  // nothing ever happens that was not already on screen being
                  // offered.
                  //
                  const ending = candidate ? endsWord(e) : null;
                  if (ending && candidate) {
                    if (ending.owned) e.preventDefault();
                    commit(candidate, ending.trailing, ending.owned);
                    return;
                  }
                  //
                  // BACKSPACE AND DELETE ARE TYPING, and they no longer flag the
                  // pass as a deletion.
                  //
                  // These two keys used to set `deleting`, which the scheduler
                  // reads as "not typing": it took the hold layer down and
                  // scheduled nothing. So correcting a typo mid-sentence — the
                  // commonest edit there is — dropped the reader out of the
                  // typing hold and left the pane sitting on a stale answer with
                  // no countdown and no coin, until the next character they
                  // happened to type put it back. Erasing a word is still being
                  // mid-sentence, so the wait now carries straight on through it.
                  //
                  // Cutting still flags it (see onCut above): that is a deletion
                  // made with the mouse, not somebody mid-keystroke.
                  //

                  if (e.key === 'Backspace' && revert()) {
                    e.preventDefault();
                    return;
                  }
                  // Escape turns down the current offer without touching the
                  // text — the way out for anyone who really is typing English.
                  if (e.key === 'Escape' && candidate) {
                    e.preventDefault();
                    setDismissed(candidate.source);
                  }
                }}
                spellCheck={false}
                aria-label="Text to translate"
              />
            </div>
            {/*
              The keyboard-layout correction, directly under the box where the
              text it is talking about is — the same place every translator puts
              it, and the only place someone will see it mid-sentence.

              A button, so Tab reaches it straight from the textarea. Deliberately
              NOT bound to Enter: Enter and Ctrl+Enter already mean something in
              this box, and a suggestion that silently rewrites what you typed is
              the one behaviour this feature cannot have.
            */}
            {candidate && (
              <button
                type="button"
                onClick={() => commit(candidate, '')}
                title="Convert to Korean"
                className="mt-3 flex w-full items-center gap-2.5 rounded-xl border border-ink-200
                           bg-ink-50/70 px-3 py-2 text-left transition hover:border-accent-500
                           hover:bg-accent-100 dark:border-ink-700 dark:bg-ink-800/40
                           dark:hover:border-accent-500 dark:hover:bg-accent-500/10"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-ink-400" fill="none"
                     stroke="currentColor" strokeWidth="1.6">
                  <rect x="2" y="6" width="20" height="12" rx="2.5" />
                  <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M7.5 13.5h9" strokeLinecap="round" />
                </svg>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] leading-snug text-ink-900 dark:text-ink-50">
                    {candidate.text}
                  </span>
                </span>
                {/* Naming the keys is what makes them discoverable — otherwise
                    the conversion just happens and looks like magic the first
                    time, which is unnerving rather than helpful.

                    Tab is NOT listed even though it works. It is the one commit
                    key whose real job is somewhere else — it leaves the box —
                    so advertising it here would read as an instruction to press
                    it, and anyone who followed that would lose their place in
                    the form. It stays available for the people who press it out
                    of habit and unlisted for everyone else.

                    Only Space survives on a narrow screen: a phone keyboard has
                    no Enter key in the usual place, and two chips beside the
                    Korean would crowd out the thing the card exists to show. */}
                <span className="flex shrink-0 items-center gap-1 text-xs text-ink-400">
                  {['Space', '↵'].map((k) => (
                    <kbd
                      key={k}
                      className={
                        'rounded border border-ink-200 bg-white px-1.5 py-0.5 font-sans text-[11px] ' +
                        'text-ink-500 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-400 ' +
                        (k === 'Space' ? '' : 'hidden sm:inline')
                      }
                    >
                      {k}
                    </kbd>
                  ))}
                  <span className="ml-0.5 hidden sm:inline">or click</span>
                </span>
              </button>
            )}

            {/*
              How the word is said, under the word.

              Same shelf as the keyboard-layout card above and deliberately the
              same shape — this is the second thing that can appear under the box
              about the text in it, and two different-looking cards in one place
              would read as two different features rather than one shelf.

              Never both at once, and the priority is not arbitrary: the card
              above is an OFFER, waiting on a key press to change what is
              written. This one is a fact about what is already there. An offer
              that has to be answered outranks a note that does not, and stacking
              them would push the offer up the screen while somebody is aiming
              at it with the space bar.

              `checkedWord` is the guard that keeps it honest, and it is the same
              value the dictionary entry under the ANSWER is drawn from — see its
              note. The answer arrives from the network a few hundred
              milliseconds after the word was asked about, and by then the box
              may hold something else. A pronunciation under the wrong word is
              worse than no pronunciation at all: it is unnoticeably wrong.

              Reading it from there rather than re-testing the same three things
              here is what stops this card and the entry from ever disagreeing
              about whether a word is being looked up.
            */}
            {checkedWord && phonetic && phonetic.word === checkedWord && (
              <div
                className="mt-3 flex w-full items-center gap-2.5 rounded-xl border border-ink-200
                           bg-ink-50/70 px-3 py-2 dark:border-ink-700 dark:bg-ink-800/40"
              >
                {/* Sound, as level meters. Every other glyph in this pane is
                    already taken by a neighbour — a microphone speaks, headphones
                    listen, a keyboard converts — and bars are the one that says
                    "this is what it sounds like" without competing with any of
                    them. */}
                <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-ink-400" fill="none"
                     stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 11v2" />
                </svg>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[17px] leading-snug text-ink-900 dark:text-ink-50">
                    {/* The slashes are drawn here rather than asked for from the
                        model, so every card has them and none has them twice. */}
                    /{phonetic.said.ipa}/
                    {/* Only ever present for a script the reader may not read —
                        see `isLatin` on the server. It is the half of the card a
                        beginner actually uses, so it sits beside the IPA rather
                        than under it, and it is the part allowed to truncate. */}
                    {phonetic.said.roman && (
                      <span className="ml-2 text-[13px] text-ink-500 dark:text-ink-400">
                        · {phonetic.said.roman}
                      </span>
                    )}
                  </span>
                </span>
                {/* Named, once, on the screens with room for it: a row of
                    phonetic symbols is unmistakable to anyone who has met them
                    and completely opaque to anyone who has not. */}
                <span className="hidden shrink-0 text-xs text-ink-400 sm:inline">pronunciation</span>
              </div>
            )}

            {/*
              Clear used to sit here as a text button, and a Korean-only Copy
              after it. Both moved into the box (above), top right, Copy to the
              left of Clear — so this row is the controls and the counter.
            */}
            <div className="flex items-center justify-between gap-3 pt-3 text-xs text-ink-400">
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {/*
                  The microphone, first in the row.

                  Ahead of the voice-file button because it is the one that acts
                  on this box immediately — press it and words appear — while
                  the file button opens a dialog and asks for something. Between
                  two controls with the same icon and adjacent meanings, the
                  live one goes first.

                  It is NOT told which language to expect unless something
                  actually knows — see `dictationLang`. An empty tag is the
                  signal that nobody does, and it routes the first phrase
                  through our own transcription, which hears what language it
                  is and says so; `onLanguage` below is that answer coming back,
                  and it hands the rest of the session to the browser's free
                  word-by-word engine.
                */}
                <MicButton
                  lang={dictationLang.tag}
                  disabled={text.length >= pageSize}
                  onStart={beginDictation}
                  onPartial={(spoken) => applyDictation(spoken, false)}
                  onFinal={(spoken) => applyDictation(spoken, true)}
                  onStop={endDictation}
                  onError={setMicError}
                  onLanguage={setVoiceLang}
                />
                {/*
                  Listen, next to the microphone — the same pairing every
                  translator has: one button puts your voice into the box, the
                  next reads the box back to you. Headphones against a
                  microphone, so which is which is legible without reading the
                  labels.

                  It reads THIS PAGE of the text, in the source language: the
                  box is what it sits under, and a button that read nine other
                  pages a visitor cannot see would be reading something else
                  entirely. It renders itself away when the browser has no voice
                  for the language, or when the language is not yet known — see
                  ListenButton.
                */}
                <ListenButton
                  text={text}
                  lang={sourceSpeechTag}
                  what="your text"
                  onError={setMicError}
                />
                {/*
                  The voice button, bottom-left of the box.

                  Here rather than in the header, and that is the whole argument
                  for the position: this fills the INPUT, so it belongs to the
                  input's own furniture next to the character count, beside the
                  thing it is about to change. In the header it would sit among
                  the app-wide controls — theme, font, games — and read as a
                  setting rather than as an action on this box.
                */}
                <button
                  type="button"
                  onClick={() => (authUser ? setVoiceOpen(true) : openGuestPrompt('feature'))}
                  title="Transcribe a voice file"
                  aria-label="Transcribe a voice file"
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-ink-200 px-2.5 py-1
                             text-[11px] font-medium text-ink-500 transition hover:border-ink-300
                             hover:bg-ink-100 hover:text-ink-800 dark:border-ink-700 dark:text-ink-400
                             dark:hover:bg-ink-800 dark:hover:text-ink-100"
                >
                  {/*
                    An upload arrow rather than the microphone this used to
                    carry. It gained a neighbour with a nearly identical icon,
                    and two microphones side by side say nothing about which one
                    records you and which one takes a file you already have.
                    This is also the icon its own dialog puts on the drop zone,
                    so the button and what it opens now agree.
                  */}
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
                       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15V4M8.5 7.5L12 4l3.5 3.5" />
                    <path d="M4.5 15v2.5a2 2 0 002 2h11a2 2 0 002-2V15" />
                  </svg>
                  Voice file
                </button>
                {/*
                  The image button, beside the voice one.

                  The pair is the point: these are the two ways text arrives
                  that nobody can type. A recording is words you cannot spell
                  yet; a photograph of a sign is words you cannot enter at all,
                  in a script your keyboard has no keys for. Same row, same
                  size, same dialog shape — and after the voice one, because
                  that is the older habit and moving it would move something
                  people already reach for without looking.
                */}
                <button
                  type="button"
                  onClick={() => (authUser ? setImageOpen(true) : openGuestPrompt('feature'))}
                  title="Read the text in an image"
                  aria-label="Read the text in an image"
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-ink-200 px-2.5 py-1
                             text-[11px] font-medium text-ink-500 transition hover:border-ink-300
                             hover:bg-ink-100 hover:text-ink-800 dark:border-ink-700 dark:text-ink-400
                             dark:hover:bg-ink-800 dark:hover:text-ink-100"
                >
                  {/* A picture frame with a hill and a sun in it — the one glyph
                      that is read as "image" at 14px without a label, and the
                      same one its dialog wears. */}
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
                       strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
                    <circle cx="8.5" cy="10" r="1.6" />
                    <path d="M3.5 16.5l4.7-4.2a2 2 0 012.7 0l3.4 3.1a2 2 0 002.6.05l1.6-1.3 2 1.8" />
                  </svg>
                  Image
                </button>
                {/*
                  The script keyboard switch — only there when the reader has
                  named a language whose script their keyboard cannot type.

                  Third in the row and not first: the two beside it bring text
                  in from somewhere else, while this one changes how the box
                  itself behaves. It also comes and goes with the language,
                  and a control that appears and disappears is better at the
                  end of a row than in the middle of one, where it would shove
                  its neighbours sideways every time the language changed.

                  It reads its own state rather than describing an action —
                  lit when the keys are Hangul — because that state is
                  otherwise invisible and is the single most surprising thing
                  this feature does.
                */}
                {keyboardOffered && (
                  <button
                    type="button"
                    onClick={() => setKeyboard(!imeOn)}
                    aria-pressed={imeOn}
                    title={imeOn
                      ? `Turn the ${keyboardName} keyboard off`
                      : `Type ${keyboardName} with your own keyboard`}
                    aria-label={imeOn
                      ? `Turn the ${keyboardName} keyboard off`
                      : `Turn the ${keyboardName} keyboard on`}
                    className={
                      'flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ' +
                      'font-medium transition ' +
                      (imeOn
                        ? 'border-accent-500 bg-accent-100 text-accent-700 hover:bg-accent-200 ' +
                          'dark:border-accent-500 dark:bg-accent-500/15 dark:text-accent-300 ' +
                          'dark:hover:bg-accent-500/25'
                        : 'border-ink-200 text-ink-500 hover:border-ink-300 hover:bg-ink-100 ' +
                          'hover:text-ink-800 dark:border-ink-700 dark:text-ink-400 ' +
                          'dark:hover:bg-ink-800 dark:hover:text-ink-100')
                    }
                  >
                    {/* The same keyboard glyph the conversion card uses — they
                        are two faces of one idea (your keys, as Hangul), and a
                        second unrelated icon would split it into two features. */}
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
                         strokeWidth="1.6">
                      <rect x="2" y="6" width="20" height="12" rx="2.5" />
                      <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M7.5 13.5h9" strokeLinecap="round" />
                    </svg>
                    {/* "Korean", not "조선어"; "Greek", not "Ελληνικά". The
                        label has to be readable by the person who cannot yet
                        type the script — which is exactly who this button is
                        for. Every other control in this row names itself in the
                        interface language, and this one was the odd one out. */}
                    {keyboardName}
                  </button>
                )}
                {/* `whitespace-nowrap` because this is the item that used to
                    break: squeezed by the controls beside it, "0 / 5,000" wraps
                    onto a second line and drags the whole row taller. It holds
                    its width now, and the language chip truncates instead. */}
                {/*
                  The count is of THIS PAGE, because the 5,000 beside it is the
                  page's limit — the number and the limit have to be about the
                  same thing or the pair means nothing. The document total goes
                  next to it, only once there is more than one page to total.
                */}
                <span
                  className={'shrink-0 whitespace-nowrap ' + (over || clipped ? 'font-medium text-red-500' : '')}
                  title={clipped ?? undefined}
                >
                  {text.length.toLocaleString()} / {pageSize.toLocaleString()}
                  {pages.length > 1 && (
                    /* While the whole document is selected this says so, because
                       the selection itself cannot: the browser highlights the
                       page in the box, and the other nine pages are not on
                       screen to look highlighted. Without it, Ctrl+A on page 1
                       of 10 looks exactly like Ctrl+A on a one-page document,
                       and the next keystroke does something much larger than it
                       appears to. */
                    <span
                      className={
                        showSelectedAll
                          ? 'font-medium text-accent-600 dark:text-accent-300'
                          : 'text-ink-400'
                      }
                    >
                      {' · '}
                      {showSelectedAll
                        ? `all ${pages.length} pages selected`
                        : `${doc.length.toLocaleString()} in ${pages.length} pages`}
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/*
              The layout itself, under the switch that turns it on.

              Below the controls rather than tucked against the textarea: the
              button that opens it is in that row, and a panel that appeared
              ABOVE its own switch would push the switch down the screen at the
              moment it was pressed — the reader's next click would land on
              whatever slid into its place.

              Rendered whenever the keyboard is ON, whatever `panelOpen` says —
              collapsed it becomes a one-line bar, never nothing. The bar is
              what tells the reader their keys are still Hangul; an empty space
              would say the opposite.
            */}
            {keyboard && imeOn && (
              <ScriptKeyboard
                spec={keyboard}
                language={keyboardName}
                layout={keyboardLayout}
                onChar={typePanelChar}
                onSpace={panelSpace}
                onBackspace={panelBackspace}
                onText={panelText}
                open={panelOpen}
                onOpenChange={setPanel}
                pressed={pressedKey}
                armed={armedDead}
                candidates={candidates}
                onChoose={pickCandidate}
                dictError={dictError}
              />
            )}

            {/*
              What the microphone has to say, in the flow rather than floating.

              Deliberately NOT a tooltip or a popover: the two things it has to
              report — a permission the browser is refusing, and an allowance
              that has run out — are both states the user has to go and do
              something about, and neither survives being attached to a control
              they have just stopped hovering. It is also the one placement in
              this pane that cannot be clipped by an ancestor's `overflow`,
              which has eaten an absolutely-positioned element here twice.
            */}
            {micError && (
              <p className="pt-2 text-[12px] leading-snug text-amber-600 dark:text-amber-400" role="status">
                {micError}
              </p>
            )}

            {/*
              The one thing that still does not fit, said out loud.

              This is the line that used to be missing entirely: text past the
              limit simply vanished on paste. Ten pages is a great deal of text,
              so almost nobody will ever see this — but the person who does is
              exactly the person the old silence failed.
            */}
            {clipped && (
              <p className="pt-2 text-[12px] leading-snug text-red-500" role="status">
                {clipped}
              </p>
            )}
          </div>
        </div>

        {/* The operation applied between the two panes, so it lives between them. */}
        <ModeColumn
          modes={modes}
          active={mode}
          lang={inputLang}
          onSelect={selectMode}
          numbers={modeNumbers}
          /*
           * The seventh button, and the only one on this rail that is not a
           * mode. It is here rather than in the header because this is where
           * the hand already is: the notebook is opened while looking at a
           * translation, to file it or to find the one before it, and a control
           * for that at the other end of the page is a control nobody reaches
           * for. See the `extras` note in ModeColumn.
           */
          extras={notebookAllowed ? [
            {
              id: 'notebook',
              label: 'Notebook',
              hint: 'Your private notebook. Keep this translation in a folder you choose, or open it to browse what you have kept. Ctrl+Alt+Z opens it without saving.',
              icon: 'notebook',
              active: notebookOpen,
              onClick: registerToNotebook,
            },
          ] : []}
        />

        {/* Target.

            The output pane is a DIFFERENT surface from the box you type in, and
            that difference is the whole of what tells the two halves apart at a
            glance. It used to be white-at-40%-over-white, which is white — the
            panes were one undivided sheet with a line down it.

            #f0f4f9 in the light and #282a2c in the dark: Google Translate's
            surface-container and surface-container-high, opaque rather than
            tinted, so what lands on screen is the value and not the value
            blended with whatever is behind it. */}
        <div
          ref={outPane}
          className="min-h-[340px] rounded-b-2xl bg-ink-100 p-5
                     md:rounded-r-2xl md:rounded-bl-none md:p-7 dark:bg-ink-800"
        >
          <div
            ref={outBlock}
            className={'relative ' + (outPinned ? 'md:sticky md:top-4' : '')}
          >
            {/*
              The model is working, said in the corner of the pane it is working
              on. The streaming caret already says the same thing, but only once
              the first characters have arrived — this covers the gap before
              that, which is the part of the wait that feels longest.

              Absolutely positioned so it cannot move the answer as it appears
              and disappears; the reserved right padding below is what keeps a
              long first line from running underneath it.
            */}
            {busy && (
              <span className="absolute right-0 top-0 z-10 flex items-center" aria-hidden>
                <Spinner />
              </span>
            )}
            {/*
              Somebody is mid-sentence, and the pane is holding the translation
              back until they stop — see TypingHold for the whole of why, and
              `waiting` above for exactly when.

              Inside this block rather than over the page, which is what makes it
              a layer and not a dialog: the caret never leaves the textarea, so
              the typing it is drawn to accompany is never the thing it stops.
            */}
            {waiting && (
              <TypingHold
                seconds={countdown}
                onNow={translateNowFromPane}
                compact={holdCompact}
                live={liveMs > 0}
              />
            )}
            {/*
              Above the first level nothing is sent while typing, so the pane
              says so and puts the one way to ask in front of the reader: the
              level, what one translation of this page costs, and the button.
              In the flow rather than over the answer, so the previous answer
              stays readable (dimmed below) under it.
            */}
            {manualWaiting && (
              <div
                className="mb-4 mr-7 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-accent-200
                           bg-white/80 px-3 py-2.5 dark:border-accent-500/30 dark:bg-ink-900/60"
              >
                <button
                  type="button"
                  onClick={translateNowFromPane}
                  className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition
                             hover:bg-accent-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
                >
                  Translate
                </button>
                <span className="min-w-0 text-[13px] leading-snug text-ink-600 dark:text-ink-300">
                  <b className="font-semibold text-ink-900 dark:text-ink-50">{levelInfo?.name ?? 'This level'}</b>
                  {manualPrice && <> · <span className="tabular-nums">{manualPrice}</span></>}
                  <span className="block text-xs text-ink-400 dark:text-ink-500">
                    Nothing is sent while you type at this level · Ctrl+Enter
                  </span>
                </span>
              </div>
            )}

            {/* Same floor as the input, so an empty pane and a one-line answer
                both keep the Copy row where it has always sat. Dropped once a
                notes card is there to fill the space — the gap would otherwise
                push the card away from the answer it belongs to.

                The right padding is permanent rather than applied while busy:
                reserving it only when the spinner is there would reflow the
                whole answer at the exact moment it starts arriving.

                Held back from full strength while a typing wait runs, and this
                is the one piece of the feature that is about correctness rather
                than about comfort. What is on screen at that moment is either
                the live draft of an unfinished sentence or the answer to an
                EARLIER version of the box, and at full strength either one reads
                as the finished translation of what the reader is looking at. A
                provisional answer presented as a settled one is the single thing
                this pane must never do.

                0.7 rather than the 0.5 this started at, and the number moved for
                a reason. At 0.5 the text was a placeholder you were meant to wait
                out — which was right when the only thing under this layer was a
                stale answer nobody should read. With live drafts it is the most
                useful thing on the page and people READ it, so it has to be text
                rather than a ghost. 0.7 is far enough from full strength to keep
                saying "not final" and close enough to be read for minutes.

                Opacity rather than a blur or a cover, for the same reason as
                ever: the answer stays legible and selectable underneath, which is
                what anyone editing one sentence of a paragraph is actually
                using. */}
            <div
              className={
                'pr-7 transition-all duration-200 ' +
                (hasNotes ? '' : 'min-h-[15rem] ') +
                /*
                  Room for the compact strip, rather than the strip sitting on top
                  of the answer.

                  Overlaying was the first try and it cost real content: the strip
                  is ~72px tall and the first thing under it is a Registers card's
                  own header and Copy button, which simply disappeared for the
                  length of the wait. Pushing the answer down instead means the
                  layer covers nothing at all — and the movement is honest, because
                  something genuinely did arrive above the answer.

                  Only in the compact case. The full-size layer is drawn over an
                  empty pane by definition, so there is nothing to make room for.
                */
                (waiting && holdCompact ? 'pt-[72px] ' : '') +
                (waiting || (manualWaiting && output) ? 'opacity-70' : 'opacity-100')
              }
            >
              {/*
                The overview, ABOVE the thing it summarises — which is what a
                summary is for: it is read before the answer, not after it, and
                the switch that turns it on says "above the translation".

                A tinted card rather than another paragraph: it is a different
                KIND of sentence from the translation — one is the answer, this
                is a description of it — and in the same ink at the same size the
                two would read as one run-on text, with the summary mistaken for
                the first line of the translation.

                Held back until the answer itself is on screen. The model writes
                the summary LAST, so during a stream there is nothing here to
                show; without this the card would pop in above the text and push
                it down at the moment someone started reading it.
              */}
              {overview && !error && !identity && shownOverview.target && (
                <OverviewCard text={shownOverview.target} className="mb-4" />
              )}
              {/*
                Say it in the pane, not only in the status line.

                When both halves name the same language the answer IS the input,
                and the two panes hold the same words — which looks exactly like
                a translator that has stopped working. There is already a note
                for it under the answer ("same language · nothing to translate"),
                but on a long text that line is a screen and a half below the
                text somebody is staring at, and they will have clicked half the
                interface before reaching it. This sits at the top of the answer,
                where the confusion is.

                The pickers no longer let anyone arrive here by accident (see
                `pickFrom`), so in practice this is for the person who chose it
                deliberately — and for whatever way of reaching it nobody has
                thought of yet.
              */}
              {identity && text.trim() && !error && (
                <div
                  role="status"
                  className="mb-4 flex gap-2.5 rounded-xl border border-amber-300/60 bg-amber-50 p-3
                             text-[13px] leading-relaxed text-amber-900
                             dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                >
                  <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none"
                       stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v5M12 16.2v.01" />
                  </svg>
                  <span>
                    Both sides are set to{' '}
                    <span className="font-semibold">{inputLang?.name ?? to}</span>, so there is
                    nothing to translate — this is your own text. Choose another language on the
                    right.
                  </span>
                </div>
              )}
              {error ? (
                <p className="text-base text-red-500">{error}</p>
              ) : clean ? (
                /* The sentinel is a RESULT, not text: it is drawn the moment it
                   is recognised, streaming or not. Waiting for the request to
                   finish is what used to put the raw word NO_MISTAKES on screen
                   for a second before it turned into this. */
                <NoMistakes />
              ) : pending ? (
                /* Nothing to show yet that would still be true a moment later —
                   either the answer has not started, or what is on screen
                   belongs to the previous question. Say "Translating…" rather
                   than show something that is about to be replaced. */
                <p className="text-lg text-ink-300 dark:text-ink-600">Translating…</p>
              ) : mode === 'ai-prompt' && promptStyles.length ? (
                /*
                  AI Prompt: one finished prompt per style, as tabs.

                  Each tab is the same request aimed at a different reader, so
                  they are alternatives rather than a comparison and only one is
                  on screen at a time. Which one is highlighted is the reader's
                  own last choice, remembered across visits, and Claude Session
                  until they have made one.

                  THE ROW COMES FROM THE STYLE LIST, NOT FROM THE ANSWER. It
                  used to be built from the tags in a single reply that
                  contained all five versions, so the tabs appeared one at a
                  time as each tag landed and the reader waited for four prompts
                  they had not asked to read before the fifth was complete. Now
                  the menu is there immediately and one version is written at a
                  time.

                  The HIGHLIGHTED tab is written for them, because it is already
                  open and nobody chooses to open what is open. The other four
                  are written by the click — which is what an empty tab in a row
                  of alternatives is for.
                */
                <div>
                  <FitTabs
                    // `activeStyleId` rather than the raw preference: the tab
                    // that is highlighted has to be the tab the pane below is
                    // showing, and only one of them knows about the fallbacks.
                    tabs={promptStyles.map((p) => ({
                      key: p.styleId,
                      label: styleName(p.styleId),
                      loading: styleLoading === p.styleId,
                    }))}
                    active={activeStyleId ?? ''}
                    onPick={openStyleTab}
                  />
                  {styleError ? (
                    <p className="text-base text-red-500">{styleError}</p>
                  ) : activeStyleText ? (
                    <>
                      <p className="stream-out whitespace-pre-wrap text-[17px] leading-relaxed">
                        {styleMain}
                        {styleLoading === activeStyleId && (
                          <span className="ml-0.5 inline-block h-5 w-[2px] animate-pulse bg-accent-500 align-text-bottom" />
                        )}
                      </p>
                      {/* The serious mistakes that were put right — Grammar Fix
                          only, and only when it found some. A quiet card under
                          the corrected text, not in it. */}
                      {styleFixes && (
                        <div className="gt-notes mt-4 rounded-xl border border-ink-200 bg-white/70 p-4
                                        dark:border-ink-800 dark:bg-ink-900/40">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">
                            What was wrong
                          </p>
                          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-ink-700 dark:text-ink-200">
                            {styleFixes}
                          </p>
                        </div>
                      )}
                    </>
                  ) : styleLoading === activeStyleId ? (
                    <p className="text-lg text-ink-300 dark:text-ink-600">Writing this version…</p>
                  ) : (
                    /* The gap between the last keystroke and the answer, now
                       that the open tab fills itself. It used to say "pick a
                       version above" at somebody whose version was already
                       picked out for them and underlined. */
                    <p className="text-lg text-ink-300 dark:text-ink-600">
                      {text.trim()
                        ? 'This version is written when you stop typing. The others open on a click.'
                        : 'Type a rough prompt and the open version is written for you.'}
                    </p>
                  )}
                </div>
              ) : mode === 'native' ? (
                /*
                  The three-register answer, as tabs.

                  They were stacked cards, one under the other, on the argument
                  that the versions only teach anything side by side. The
                  contrast is real, and it is not what people do with this mode:
                  they paste a message they are about to send and take ONE of the
                  three. At that length — this is the mode whole messages go into
                  — three full renderings meant two screens of scrolling past
                  versions nobody chose to read to reach the one being sent.

                  So the same treatment AI Prompt gives its alternatives, built
                  the same way and for the same reasons — and, since the first
                  pass at this, the same FETCHING as well, which is the half
                  that was missing. It was still one request writing all three,
                  so two full rewrites of the message were paid for and streamed
                  on every answer and the reader still waited through them.

                  THE ROW COMES FROM THE REGISTER LIST, NOT FROM THE ANSWER, and
                  now it is drawn before there is an answer at all: the three
                  are fixed in the prompt, so the menu is there immediately and
                  one version is written at a time. The HIGHLIGHTED tab is
                  written for them, because it is already open and nobody
                  chooses to open what is open; the other two are written by the
                  click — which is what an empty tab in a row of alternatives is
                  for — and a tab that has been written is kept until the
                  question changes, so going back to it is instant and free.
                */
                <div>
                  <FitTabs
                    tabs={VARIANT_ORDER.map((key) => ({
                      key,
                      label: VARIANT_LABEL[key],
                      loading: writingRegister === key,
                    }))}
                    active={activeRegister}
                    onPick={openRegisterTab}
                  />
                  {registerError ? (
                    <p className="text-base text-red-500">{registerError}</p>
                  ) : activeVariant ? (
                    <p className="stream-out whitespace-pre-wrap text-[17px] leading-relaxed">
                      {/* `shownAnswer` is this same object — see where it is
                          defined for why the two must not be written apart. */}
                      <LinkedAnswer
                        laid={activeVariant}
                        active={linked}
                        onEnter={setLinked}
                        onLeave={() => setLinked(null)}
                      >
                        {writingRegister === activeRegister && (
                          <span className="ml-0.5 inline-block h-5 w-[2px] animate-pulse bg-accent-500 align-text-bottom" />
                        )}
                      </LinkedAnswer>
                    </p>
                  ) : writingRegister === activeRegister ? (
                    <p className="text-lg text-ink-300 dark:text-ink-600">Writing this version…</p>
                  ) : (
                    /* The gap between the last keystroke and the answer, now
                       that the open tab fills itself. The other two say what
                       they are waiting for rather than sitting blank: a reader
                       who has just clicked Slang for the first time on this
                       message is one click from it, not stuck. */
                    <p className="text-lg text-ink-300 dark:text-ink-600">
                      {manual
                        ? (text.trim()
                          ? 'Press Translate to write this version. The others open on a click.'
                          : 'Type a message, then press Translate.')
                        : text.trim()
                          ? 'This version is written when you stop typing. The others open on a click.'
                          : 'Type a message and the open version is written for you.'}
                    </p>
                  )}
                </div>
              ) : outputBody && showAiTabs ? (
                /*
                  Translate, as two readings of one answer.

                  STANDARD is the faithful translation this pane has always
                  shown, and it is what the mode is for. AI is the same message
                  translated by someone inside the team's field — AI and
                  software engineering — so the terms the message is about come
                  out right: English terms spelled in Hangul by ear, the North's
                  technical vocabulary, and jargon whose dictionary sense is the
                  wrong one. See AI_RULES in the backend's lib/modes.ts for the
                  whole contract; it runs on the strong model (quality.ts,
                  `modelForTab`).

                  Both are here because the choice between them belongs to the
                  reader and cannot be made for them: the faithful rendering is
                  the right answer when the wording itself matters, and a
                  reading that commits to an interpretation is the right answer
                  when what matters is acting on the message. Committing to an
                  interpretation is precisely what the faithful tab must not do,
                  which is why one prompt cannot be both and why these are tabs
                  rather than a setting.

                  Unlike the Registers and AI Prompt strips above, the open tab
                  is NOT written by the scheduler — Standard is already being
                  written by the ordinary path, so filling AI unasked would
                  buy a second model call on every single translation for a
                  reading most of them will not be opened to. It is written by
                  the click, and kept until the question changes.
                */
                <div>
                  <FitTabs
                    tabs={[
                      {
                        key: 'standard',
                        label: 'Standard',
                        title: 'The standard translation: faithful to what the text says.',
                        loading: busy,
                      },
                      {
                        key: 'ai',
                        label: EXTRA_TAB_LABEL.ai,
                        title:
                          'Translated for AI and software engineers: technical terms recognised ' +
                          '(even when spelled in Hangul by ear) and written the way engineers ' +
                          'write them, meaning over word-for-word.',
                        loading: aiBusyMap.ai,
                      },
                      {
                        key: 'easy',
                        label: EXTRA_TAB_LABEL.easy,
                        title:
                          'Easy to read: the text is first simplified to beginner (A1) level, ' +
                          'then translated — short sentences and everyday words.',
                        loading: aiBusyMap.easy,
                      },
                    ]}
                    active={openExtra ?? 'standard'}
                    onPick={openAiTab}
                  />
                  {onAi ? (
                    aiError ? (
                      <p className="text-base text-red-500">{aiError}</p>
                    ) : laidOutAi ? (
                      <p className="stream-out text-[19px] leading-relaxed">
                        {/* `shownAnswer` is this same object while the tab is
                            open — see where it is defined for why the pane that
                            is drawn and the pane the marks are read from must
                            not be written apart. */}
                        <LinkedAnswer
                          laid={laidOutAi}
                          active={linked}
                          onEnter={setLinked}
                          onLeave={() => setLinked(null)}
                        >
                          {aiBusy && (
                            <span className="ml-0.5 inline-block h-5 w-[2px] animate-pulse bg-accent-500 align-text-bottom" />
                          )}
                        </LinkedAnswer>
                      </p>
                    ) : aiBusy ? (
                      <p className="text-lg text-ink-300 dark:text-ink-600">
                        Writing the {openExtra ? EXTRA_TAB_LABEL[openExtra] : ''} version…
                      </p>
                    ) : (
                      /* Open, empty, and nothing on the wire.
                         It must not claim to be writing — an idle pane that says
                         it is working is how a reader ends up waiting for
                         something that is never coming. And where there is
                         already a faithful answer beside it, the question has
                         plainly settled and this tab should have been written,
                         so it names the way to ask again rather than describing
                         a wait that is over: clicking the open tab re-runs it,
                         which is the escape hatch from an attempt that failed
                         somewhere it could not report. */
                      <p className="text-lg text-ink-300 dark:text-ink-600">
                        {outputBody
                          ? `Click ${openExtra ? EXTRA_TAB_LABEL[openExtra] : ''} to write this version.`
                          : manual
                            ? `Press Translate, then click ${openExtra ? EXTRA_TAB_LABEL[openExtra] : ''} to write this version.`
                            : `The ${openExtra ? EXTRA_TAB_LABEL[openExtra] : ''} version is written when you stop typing.`}
                      </p>
                    )
                  ) : (
                    <p className="stream-out text-[19px] leading-relaxed">
                      <LinkedAnswer
                        laid={laidOut}
                        active={linked}
                        onEnter={setLinked}
                        onLeave={() => setLinked(null)}
                      >
                        {busy && (
                          <span className="ml-0.5 inline-block h-5 w-[2px] animate-pulse bg-accent-500 align-text-bottom" />
                        )}
                      </LinkedAnswer>
                    </p>
                  )}
                </div>
              ) : outputBody ? (
                <p className="stream-out text-[19px] leading-relaxed">
                  <LinkedAnswer
                    laid={laidOut}
                    active={linked}
                    onEnter={setLinked}
                    onLeave={() => setLinked(null)}
                  >
                    {busy && (
                      <span className="ml-0.5 inline-block h-5 w-[2px] animate-pulse bg-accent-500 align-text-bottom" />
                    )}
                  </LinkedAnswer>
                </p>
              ) : (
                <p className="text-lg text-ink-300 dark:text-ink-600">
                  {busy ? (ownLevel ? 'TrueMarch Model is translating…' : 'Translating…') : 'Translation'}
                </p>
              )}
            </div>

            {/*
              Copy sits directly under the translation and ABOVE the notes card,
              and copies only the translation. Someone pasting into a chat wants
              the sentence, not the sentence plus a glossary they were reading for
              themselves — and a button below the card would look like it copied
              everything above it.

              With no card it sits under the pane's minimum text height, which is
              where it used to land when the pane stretched it to the bottom.
            */}
            <div
              className={
                'flex items-center justify-between ' + (hasNotes ? 'mt-4' : 'pt-3')
              }
            >
              <span className="text-xs text-ink-400" aria-live="polite">
                {/* The countdown takes this slot while it runs. A wait with a
                    number on it is a wait someone can sit through; the same wait
                    with nothing on screen is indistinguishable from the thing
                    being broken, and the reflex it produces is to type again —
                    which restarts the wait and costs the call anyway.

                    Saying WHY it was instant matters for the same reason: an
                    answer identical to the input, with no explanation, reads as
                    the translator having failed rather than having nothing to
                    do. */}
                {countdown !== null ? (
                  <span
                    className="gt-countdown inline-flex items-center gap-2 rounded-full border
                               border-ink-200 bg-ink-50/80 py-1 pl-1.5 pr-3 text-ink-500
                               dark:border-ink-700 dark:bg-ink-800/60 dark:text-ink-300"
                  >
                    <CountdownRing />
                    <span>
                      Translating in{' '}
                      <span key={countdown} className="gt-digit font-medium tabular-nums">
                        {countdown}
                      </span>
                      s
                    </span>
                    {/* Ctrl+Enter, not Enter — Enter is a line break and
                        translates nothing. */}
                    <span className="text-ink-400 dark:text-ink-500">· Ctrl+Enter to skip</span>
                  </span>
                ) : (
                  stat &&
                  !busy &&
                  (stat.same
                    ? 'same language · nothing to translate'
                    : (stat.cached
                      ? 'instant · from cache'
                      : `${(stat.ms / 1000).toFixed(1)}s`)
                      // Our own model wrote it (a cache hit at its level too: the caches are kept per level).
                      + ((stat.own ?? ownLevel) ? ' · by TrueMarch Model' : '')
                      // What it cost, once settled — only when something was charged.
                      + (stat.kharma ? ` · ${stat.kharma} KHARMA` : ''))
                )}
              </span>
              {/*
                The same ring again, bare, at the end of the row.

                Not a duplicate for its own sake: the eye rests on the right of
                this row, because that is where Copy lives and where the answer's
                last line ends. The wording on the left explains the wait to
                anyone who looks for an explanation; this one is simply movement
                where someone is already looking, so the pane never reads as
                having stopped. Both mount together and share one 3s animation,
                so they cannot fall out of step.
              */}
              <span className="flex items-center gap-2">
                {countdown !== null && (
                  <span
                    className="flex items-center text-ink-500 dark:text-ink-300"
                    title={`Translating in ${countdown}s`}
                  >
                    <CountdownRing />
                  </span>
                )}
                {/*
                  Listen to the ANSWER, which is the half most people came for:
                  a translation into a language you cannot pronounce is half a
                  translation, and hearing it is the difference between sending
                  the message and saying it.

                  Icon only — Copy is already carrying a word in this corner,
                  and the countdown readout shares the row on a phone.

                  Hidden while the answer is still arriving: reading a half
                  streamed sentence out loud would stop mid-word, and the queue
                  it built cannot be extended after the fact.
                */}
                {!busy && !clean && (
                  <ListenButton
                    text={answerText}
                    lang={targetSpeechTag}
                    what="the translation"
                    compact
                    onError={setMicError}
                  />
                )}
                {/* The one Copy for the output: the answer's text and nothing
                    else — not the cards under it, not a second version. The
                    tab rows and the cards have none of their own (owner,
                    2026-10-14): one button that always does the same thing. */}
                <CopyButton text={answerText} disabled={(busy && !output) || clean} />
              </span>
            </div>

            {/*
              Registers: the open version in the output language, between the
              answer and the words-that-can-confuse card. The answer stays in
              the language it was written in; this is the same message for
              the reader the output picker names. See `registerTranslations`.
            */}
            {registerTranslationOffer && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setRegisterTranslationAsked(activeRegister)}
                  className="rounded-lg border border-ink-200 bg-white/70 px-3 py-1.5 text-sm font-medium text-ink-700
                             transition hover:bg-white dark:border-ink-700/70 dark:bg-ink-900/60 dark:text-ink-200
                             dark:hover:bg-ink-900"
                >
                  🌐 Translate this version into {toLangName}
                  {priceMode === 'kharma' && levelInfo && aiPricing && registerTexts[activeRegister] && (
                    <span className="ml-1.5 text-xs tabular-nums text-ink-400">
                      ≈ {quoteLevel(aiPricing, levelInfo, registerTexts[activeRegister] ?? '', false)} KHARMA
                    </span>
                  )}
                </button>
              </div>
            )}
            {registerTranslationCard && (
              <div
                className="gt-notes mt-4 rounded-xl border border-ink-200 bg-white/70 p-4
                           dark:border-ink-700/70 dark:bg-ink-900/60"
              >
                <div className="mb-2 flex items-start justify-between gap-3">
                  <span className="text-sm font-semibold text-ink-700 dark:text-ink-200">
                    🌐 {VARIANT_LABEL[activeRegister]} Translation
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {registerTranslationCard.body && registerTranslating !== activeRegister && (
                      <ListenButton
                        text={registerTranslationCard.body}
                        lang={speakTag(to)}
                        what="the translation"
                        compact
                        onError={setMicError}
                      />
                    )}
                  </span>
                </div>
                {registerTranslationCard.pending ? (
                  <p className="animate-pulse text-[15px] leading-relaxed text-ink-400">Translating…</p>
                ) : (
                  <p className="stream-out whitespace-pre-wrap text-[15px] leading-relaxed text-ink-900 dark:text-ink-50">
                    {registerTranslationCard.body}
                  </p>
                )}
              </div>
            )}

            {/* The teaching sections, in the same column as the answer they
                belong to. Full width underneath both panes made them read as a
                separate feature rather than as part of this translation. */}
            {noteCards.map((card) => (
              <div
                key={card.key}
                className="gt-notes mt-4 rounded-xl border border-ink-200 bg-white/70 p-4
                           dark:border-ink-700/70 dark:bg-ink-900/60"
              >
                {/* The section's own heading is the card header. No copy button
                    here, or on the translation card above: the one on the answer
                    is the one people use, and three in a column was two too many
                    (owner, 2026-10-13). */}
                <div className="mb-2">
                  <span className="text-sm font-semibold text-ink-700 dark:text-ink-200">
                    {card.head}
                  </span>
                </div>
                <p className="stream-out whitespace-pre-wrap text-[15px] leading-relaxed text-ink-700 dark:text-ink-200">
                  {card.body}
                </p>
              </div>
            ))}

            {/*
              The dictionary entry, last in the column.

              Under the teaching cards rather than over them, and under the
              answer rather than in place of it, because of what it is for. The
              translation is what was ASKED for — somebody typed a word into a
              translator — and this is the thing they turn out to have wanted
              once they have it. Putting it above the answer would answer a
              question nobody asked, in front of the one they did.

              Not gated on the Overview switch, on the mode, or on the request
              having succeeded. It is a fact about the word in the box, not a
              part of the answer: it holds when the translation failed, when the
              two languages are the same, and when a rewriting mode has hidden
              the target pickers — in each of which somebody is still looking at
              a word they do not know.

              It appears with the pronunciation card under the box and goes with
              it, because they are one state and not two — see `checkedWord`.
              That pairing is the feature: phonetics under the word you typed
              and its entry under the answer say together "you are looking a word
              up, here is the whole of what we know about it", where either
              alone is a fragment.

              `shownEntry` is what keeps it honest — see its note. An entry under
              the wrong headword, or glossed into a language the reader has
              already navigated away from, is not a lesser version of being
              right; it is wrong in a way nobody can see.
            */}
            {shownEntry && checkedWord && (
              // The headword is rewritten like the rest of the card, and it is
              // the one place where that is a judgement call rather than a
              // mechanical one: this string is what the USER typed, echoed
              // back. It is rewritten anyway, because a card titled 한국 whose
              // every sense and example says 조선 does not read as a naming
              // preference — it reads as a bug. The box above still shows what
              // was actually typed, which is where that belongs.
              <DictionaryCard word={preferredTerms(checkedWord)} entry={shownEntry} className="mt-4" />
            )}

          </div>
        </div>
      </div>

      {/*
        Send feedback — its own row, under the card and hard right.

        OUTSIDE the output pane on purpose. Inside, it would be one more control
        in a corner already holding Copy, the spinner and the timing readout, and
        it would read as being about that translation — a thumbs-down on this
        answer — rather than about the product. Under the whole card, at the far
        edge, it is what it is: the last thing on the page, for the visit rather
        than for the sentence. It is also where translate.google.com puts it,
        which is worth something for a control people only look for when they are
        already annoyed.

        Quiet by default and only coloured on hover: this must be findable, not
        prominent. A button competing with Copy would be asking for feedback more
        loudly than the page asks to be used.
      */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => (authUser ? setFeedbackOpen(true) : openGuestPrompt('feature'))}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium
                     text-ink-400 transition hover:bg-ink-100 hover:text-ink-700
                     dark:hover:bg-ink-800 dark:hover:text-ink-100"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.4 8.4 0 01-9 8.4 9.4 9.4 0 01-3-.5L3 21l1.6-4.2A8.2 8.2 0 013 11.5 8.5 8.5 0 0112 3a8.5 8.5 0 019 8.5z" />
          </svg>
          Send feedback
        </button>
      </div>

      {/*
        Four rules, and the line has to carry all four because each of them
        contradicts a guess somebody would otherwise make. It says "as you type"
        again — which was untrue for as long as typing waited, and is true again
        now — and it says the number rather than a number: `holdSeconds` is the
        operator's, so a hardcoded "5 seconds" here would go quietly wrong the
        first time anybody changed it in the console.
      */}
      {manual ? (
        /* Above the first level none of the four rules below hold: nothing is
           sent until Translate is pressed (see `manual`). */
        <p className="text-xs text-ink-400">
          At {levelInfo?.name ?? 'this level'} nothing is sent while you type · press Translate or{' '}
          <kbd className="rounded border border-ink-300 px-1 dark:border-ink-700">Ctrl</kbd>
          <span className="px-0.5">+</span>
          <kbd className="rounded border border-ink-300 px-1 dark:border-ink-700">Enter</kbd>{' '}
          for one translation of the page
        </p>
      ) : (
      <p className="text-xs text-ink-400">
        {/* Both halves are read off the settings the server sent, so this line
            cannot promise behaviour an operator has switched off — which is
            exactly what "answers appear as you type" did the moment the live
            draft was turned down to 0. */}
        {liveMs > 0 ? 'Answers appear as you type · ' : ''}
        <kbd className="rounded border border-ink-300 px-1 dark:border-ink-700">Ctrl</kbd>
        <span className="px-0.5">+</span>
        <kbd className="rounded border border-ink-300 px-1 dark:border-ink-700">Enter</kbd>{' '}
        translates now · pasted text goes straight away ·
        otherwise the answer follows {holdSeconds} seconds after you stop typing
      </p>
      )}

      {voiceOpen && (
        <VoiceDialog
          onClose={() => setVoiceOpen(false)}
          onTranscript={(transcript) => {
            /*
             * A transcript is a REQUEST for a translation, not a draft.
             *
             * Somebody who uploaded a recording and watched it transcribe has
             * already waited; making them wait out the five-second typing pause
             * as well — as though they were still deciding what to write —
             * would be the same mistake `forceNext` was added for on paste and
             * on swap. So the box is filled and the translation goes now.
             */
            // The whole document, from the first page. A transcript of a lecture
            // is exactly the case pagination was added for: it used to arrive
            // cut off at 5,000 characters with the rest of the recording gone.
            setDoc(transcript.slice(0, docLimit));
            setPage(0);
            forceNext.current = true;
            setCaret(0);
            setDismissed(null);
            undo.current = null;
          }}
        />
      )}

      {imageOpen && (
        <ImageDialog
          onClose={() => setImageOpen(false)}
          onText={(read) => {
            /*
             * The same treatment a transcript gets, for the same reason: this
             * is a REQUEST for a translation, not a draft. Somebody who
             * photographed a menu and waited for it to be read has already
             * waited; making them sit out the typing pause as well — as though
             * they were still deciding what to write — is exactly the mistake
             * `forceNext` exists to prevent. The box is filled and the
             * translation goes now.
             *
             * The line breaks survive: `setDoc` takes the text as it came, so
             * a menu that was read as dish-and-price on one line is translated
             * as a menu rather than as a word list.
             */
            setDoc(read.slice(0, docLimit));
            setPage(0);
            forceNext.current = true;
            setCaret(0);
            setDismissed(null);
            undo.current = null;
          }}
        />
      )}

      {/*
        The pager, floating over the bottom of the window — but only when the
        text really did have to be split. See PageBar for why it is fixed rather
        than in the flow, and `pb-28` on the container above for the room it is
        given so it never covers the last row of the page.
      */}
      {pages.length > 1 && (
        <PageBar
          count={pages.length}
          current={pageIndex}
          onGo={goToPage}
          pageChars={text.length}
          docChars={doc.length}
          maxChars={pageSize}
          done={donePages}
          busy={busy || countdown !== null}
        />
      )}

      {feedbackOpen && (
        <FeedbackDialog
          onClose={() => setFeedbackOpen(false)}
          /*
           * The panes as they are right now, handed to the dialog to OFFER —
           * it attaches them only if the tick box says so. `answerText` rather
           * than `output`, for the same reason Copy uses it: nobody should have
           * `[[OVERVIEW]] …` quoted back at them in a bug report about their own
           * translation.
           */
          context={{
            sourceLang: inputLang?.code ?? null,
            targetLang: followsInput ? inputLang?.code ?? null : to,
            sourceLangName: inputLang?.name ?? null,
            targetLangName: followsInput
              ? inputLang?.name ?? null
              : languages.find((l) => l.code === to)?.name ?? to,
            mode,
            modeLabel: activeMode?.label ?? null,
            sourceText: text,
            targetText: answerText,
          }}
        />
      )}

      {notebookOpen && notebookAllowed && (
        <NotebookDialog
          onClose={() => setNotebookOpen(false)}
          onLoad={loadEntry}
          modes={modes}
          languages={languages}
          /*
           * The AI Prompt tab names, handed over rather than fetched again.
           * They are operator-editable and arrive with the mode registry, which
           * this component already holds — a second /api/modes per entry opened
           * would be a request to learn something already in memory.
           */
          styleLabels={Object.fromEntries(promptStyles.map((p) => [p.styleId, p.label]))}
          /*
           * The mode the reader is in, which is the folder they are most likely
           * to want. Everything a mode produces is filed in that mode's own
           * folder now, so opening the notebook from AI Prompt onto History —
           * the folder that holds the leftovers — would be opening it onto the
           * one place today's work is NOT.
           */
          mode={mode}
          initialEntryId={notebookEntry}
        />
      )}

      {/*
        Ctrl+7 / the rail button: choose where this translation is kept. A small
        window over the translator rather than the whole notebook — the one thing
        it asks is the folder, and it can make one on the spot. "Browse" is the
        way out for anyone who reached for it only to look.
      */}
      {savePickerOpen && notebookAllowed && (
        <FolderSavePicker
          onChoose={saveSnapTo}
          onClose={() => setSavePickerOpen(false)}
          onBrowse={() => { setSavePickerOpen(false); openNotebook(); }}
        />
      )}

      {/*
        What the save chord did, for five seconds.

        Bottom-centre rather than beside the button, because the chord has no
        button — it can be pressed with the pointer anywhere, and a message that
        appears where the eye happens not to be is a message that did not
        happen. It names the folder and offers the way into it, which together
        are the answer to the only question the chord leaves open.
      */}
      {/*
        The toast, but not while the notebook is open over it.

        Saving now opens the dialog ONTO the entry, and the entry on screen is a
        better confirmation than a sentence claiming one — two of them at once is
        the interface saying the same thing twice. It still appears for anybody
        who closes the notebook inside the five seconds, and an ERROR always
        appears, because a failure is the one thing the open dialog cannot show.
      */}
      {((saved && !notebookOpen) || saveError) && (
        <div
          role="status"
          /*
           * Above the notebook (z-120) and its own confirmations (z-130),
           * because the button that raises this message now OPENS that dialog in
           * the same gesture — a failure reported underneath it would be a
           * failure nobody is told about. Bottom-centre, so it clears the
           * dialog's header and actions.
           */
          className="fixed inset-x-0 bottom-6 z-[140] flex justify-center px-4"
        >
          <div
            className={
              'pointer-events-auto flex items-center gap-3 rounded-xl px-4 py-2.5 text-[13px] ' +
              'shadow-lg ring-1 ' +
              (saveError
                ? 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-950 dark:text-red-200 dark:ring-red-500/30'
                : 'bg-ink-900 text-white ring-black/10 dark:bg-ink-800')
            }
          >
            {saveError ? (
              <>
                <span>{saveError}</span>
                <button
                  type="button"
                  onClick={() => setSaveError(null)}
                  className="font-medium underline underline-offset-2"
                >
                  Dismiss
                </button>
              </>
            ) : (
              <>
                <svg
                  viewBox="0 0 24 24"
                  className={'h-4 w-4 shrink-0 ' + (saved!.duplicate ? 'text-ink-400' : 'text-emerald-400')}
                  fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                >
                  {saved!.duplicate ? <path d="M12 8v5M12 16.5v.01M12 3.5l9 16H3z" /> : <path d="M5 12.5l4.5 4.5L19 7.5" />}
                </svg>
                {/*
                  Two different things, said as two different things.

                  The chord does not make a second copy of something the folder
                  already has (see routes/notebook.ts), and claiming "Saved" for
                  a save that deliberately did not happen is the kind of small
                  lie that teaches people not to trust the message. "Already in"
                  is also the more useful sentence: it answers the question that
                  made them press the chord a second time.
                */}
                <span>
                  {saved!.duplicate ? 'Already in ' : 'Saved to '}
                  <strong className="font-semibold">{saved!.folder}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => { setNotebookEntry(saved!.id); setNotebookOpen(true); setSaved(null); }}
                  className="font-medium text-accent-300 underline underline-offset-2 hover:text-accent-200"
                >
                  Open
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {savingEntry && (
        <span className="sr-only" role="status">Saving to the notebook…</span>
      )}
    </div>
  );
}
